// src/components/DocWriterChart.jsx
// UPDATED 2026-08-13 v8: preserves the verified v7 renderer/data model and upgrades only
// the Doc Writer editing shell/interaction model to match the finalized DocWriterMath UI:
// two-pane editing, draft Undo/Redo, centered in-app delete confirmation, supplemental
// top-title/bottom-note lines, six resize handles, preview scrollbars, centered Move/Edit,
// and optional locked-aspect-ratio resizing.

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  LabelList,
} from "recharts";

/**
 * DocWriterChart
 *
 * Dedicated Doc Writer chart / graph / diagram document-object renderer.
 *
 * Design goals:
 * - Keep Doc Writer editing behavior isolated from the shared chatbot AnswerChart.
 * - Use Recharts for real analytical charts.
 * - Keep custom SVG fallback for geometry, diagrams, math/science curves, and unsupported visuals.
 * - Accept forgiving text instructions from model output.
 * - Avoid black-only chart fallback by applying palette/color mapping consistently.
 * - Support finance, accounting, legal probability, education, science, and general analytics.
 *
 * Supported model markers / persisted Doc Writer sources:
 *   [chart] type=scatter; points=A:10,80|B:30,65|C:70,35; title=Risk vs Success
 *   [graph] type=line; x=Year 1,Year 2; series=A:10,20|B:12,24
 *   [geometry] triangle; color=#2563eb
 *
 * Supported chart types / aliases:
 * - bar, horizontal_bar
 * - column, vertical_bar
 * - grouped_bar, grouped_column
 * - stacked_bar, stacked_column
 * - line, trend, multi_line
 * - area, stacked_area
 * - composed, combo, mixed
 * - pie
 * - donut, doughnut
 * - scatter, scatterplot, scatter_diagram
 * - bubble
 * - histogram, distribution
 * - bell_curve, normal_curve, gaussian, precise_bell_curve
 * - confidence_interval, error_bar
 * - confidence_band, probability_band, line_confidence_band
 * - kde_density, density_curve
 * - roc_curve
 * - precision_recall
 * - violin_plot
 * - radar, spider
 * - waterfall, bridge
 * - boxplot, box_plot, box_whisker
 * - heatmap, matrix
 * - funnel
 * - treemap_basic
 * - gauge, meter
 * - progress
 * - timeline
 * - gantt_basic
 * - candlestick_basic, ohlc
 * - pareto
 * - slope
 * - lollipop
 * - dotplot
 * - true regression trendline overlay for scatter/scatter_line
 *
 * Geometry / diagram / graph previews:
 * - circle, ellipse / oval, square, rectangle, triangle, polygon
 * - angle, right_triangle, coordinate_graph, parabola, sine_wave, cosine_wave
 * - vector, number_line, venn, flowchart_basic
 *
 * Notes:
 * - Some chart types are implemented as custom SVG because Recharts does not natively support them.
 * - This file prioritizes robust rendering, graceful fallback, and broad coverage.
 * - For truly advanced scientific plotting, a Python/Plotly backend would still be better later.
 */

const DEFAULT_PALETTE = [
  "#2563eb", // blue
  "#16a34a", // green
  "#dc2626", // red
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#f97316", // orange
  "#84cc16", // lime
  "#ec4899", // pink
  "#64748b", // slate
  "#14b8a6", // teal
  "#a855f7", // purple
];

const COLOR_NAME_MAP = {
  blue: "#2563eb",
  green: "#16a34a",
  red: "#dc2626",
  yellow: "#eab308",
  amber: "#f59e0b",
  orange: "#f97316",
  purple: "#9333ea",
  violet: "#8b5cf6",
  pink: "#ec4899",
  cyan: "#06b6d4",
  teal: "#14b8a6",
  lime: "#84cc16",
  gray: "#64748b",
  grey: "#64748b",
  black: "#111827",
  slate: "#475569",
};

const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#475569";
const GRID = "rgba(15, 23, 42, 0.12)";
const AXIS = "rgba(15, 23, 42, 0.58)";
const CARD_BG = "#ffffff";
const SOFT_BG = "#f8fafc";

const DOC_WRITER_CHART_COMPONENT_VERSION = "2026.08.13-v8";

const DOC_WRITER_DEFAULT_WIDTH = 620;
const DOC_WRITER_MIN_WIDTH = 300;
const DOC_WRITER_DEFAULT_MAX_WIDTH = 672;
const DOC_WRITER_MIN_HEIGHT = 240;
const DOC_WRITER_MAX_HEIGHT = 760;
const DOC_WRITER_EDITOR_ONLY_ATTR = "data-doc-editor-only";
const DOC_WRITER_DRAG_HANDLE_ATTR = "data-doc-visual-drag-handle";
const DOC_WRITER_RESIZE_HANDLE_ATTR = "data-doc-visual-resize-handle";
const DOC_WRITER_CHART_RESIZE_HANDLE_PX = 24;

const DOC_WRITER_CHART_LABELS = {
  bar: "Bar Chart",
  column: "Column Chart",
  grouped_bar: "Grouped Bar Chart",
  stacked_bar: "Stacked Bar Chart",
  line: "Line Chart",
  area: "Area Chart",
  stacked_area: "Stacked Area Chart",
  pie: "Pie Chart",
  donut: "Donut Chart",
  scatter: "Scatter Diagram",
  bubble: "Bubble Chart",
  histogram: "Histogram",
  bell_curve: "Bell Curve",
  confidence_interval: "Confidence Interval / Error Bar",
  confidence_band: "Confidence / Probability Band",
  kde_density: "KDE Density Curve",
  roc_curve: "ROC Curve",
  precision_recall: "Precision-Recall Curve",
  violin_plot: "Violin Plot",
  radar: "Radar Chart",
  composed: "Composed Chart",
  pareto: "Pareto Chart",
  waterfall: "Waterfall Chart",
  boxplot: "Box Plot",
  heatmap: "Heatmap",
  funnel: "Funnel Chart",
  gauge: "Gauge",
  progress: "Progress",
  timeline: "Timeline",
  gantt_basic: "Gantt Chart",
  candlestick_basic: "Candlestick Chart",
  treemap_basic: "Treemap",
  slope: "Slope Chart",
  lollipop: "Lollipop Chart",
  dotplot: "Dot Plot",
  circle: "Circle",
  ellipse: "Ellipse",
  square: "Square",
  rectangle: "Rectangle / Floor Plan",
  triangle: "Triangle",
  right_triangle: "Right Triangle",
  angle: "Angle",
  parabola: "Parabola",
  coordinate_graph: "Coordinate Graph",
  sine_wave: "Sine Wave",
  cosine_wave: "Cosine Wave",
  number_line: "Number Line",
  venn: "Venn Diagram",
  flowchart_basic: "Flowchart",
  polygon: "Polygon",
  vector: "Vector",
};

const DOC_WRITER_TYPE_OPTIONS = Object.entries(DOC_WRITER_CHART_LABELS);

const DOC_WRITER_EDITABLE_FIELDS = [
  ["labels", "Labels / categories"],
  ["values", "Values"],
  ["series", "Series"],
  ["points", "Points"],
  ["x_values", "X values"],
  ["y_values", "Y values"],
  ["rows", "Rows"],
  ["cols", "Columns"],
  ["low", "Low"],
  ["mean", "Mean"],
  ["expected", "Expected"],
  ["high", "High"],
  ["tasks", "Tasks"],
  ["events", "Events"],
  ["candles", "OHLC / candles"],
  ["data", "Data"],
  ["equation", "Equation"],
  ["partitions", "Partitions"],
  ["width", "Geometry width / total width"],
  ["height", "Geometry height / total height"],
  ["colors", "Colors"],
];

const DOC_WRITER_SOURCE_ORDER = [
  "type", "title", "subtitle", "x_label", "y_label", "z_label",
  "show_title", "title_align", "title_color", "title_font_size", "title_order",
  "show_subtitle", "subtitle_align", "subtitle_color", "subtitle_font_size", "subtitle_order",
  "show_chart_type", "chart_type_align", "chart_type_color", "chart_type_font_size", "chart_type_order",
  "top_titles", "bottom_notes",
  "legend_position", "legend_align", "legend_color", "legend_font_size", "legend_offset", "legend_content",
  "show_data_labels", "data_label_content", "data_label_color", "data_label_font_size", "label_pointer", "label_offsets",
  "data_table_mode", "data_table_show_colors", "data_table_show_headers", "data_table_show_categories",
  "data_table_show_series", "data_table_font_size", "data_table_text_color", "data_table_border_color",
  "show_category_labels", "category_label_color", "category_label_font_size", "category_label_pointer",
  "category_label_offsets", "hidden_category_indices", "category_label_angle",
  "show_x_axis_label", "x_label_position", "x_label_color", "x_label_font_size", "x_tick_color", "x_tick_font_size",
  "show_y_axis_label", "y_label_position", "y_label_color", "y_label_font_size", "y_tick_color", "y_tick_font_size",
  "show_grid", "grid_color", "axis_color", "line_width", "marker_size", "bar_gap", "value_label",
  "labels", "values", "series", "data", "points", "x_values", "y_values",
  "rows", "cols", "low", "mean", "expected", "high", "tasks", "events",
  "candles", "partitions", "colors", "color", "equation", "trendline",
  "regression", "show_regression", "line", "doc_width", "doc_height",
  "doc_alignment", "doc_top_edge_inset", "lock_aspect_ratio", "width", "height", "alignment", "background",
  "showareas", "showdimensions", "radius", "degrees", "angle", "min",
  "max", "bins", "bin_count",
];

function sanitizeDocWriterDimension(value, fallback, min, max) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, Math.round(parsed)));
}

function normalizeDocWriterAlignment(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["left", "center", "right"].includes(normalized) ? normalized : "center";
}

function normalizeLegendPosition(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["none", "hidden", "off", "false"].includes(normalized)) return "none";
  if (["top", "above"].includes(normalized)) return "top";
  return "bottom";
}

function normalizeTextAlignment(value, fallback = "center") {
  const normalized = String(value || "").trim().toLowerCase();
  return ["left", "center", "right"].includes(normalized) ? normalized : fallback;
}


function createChartSupplementalId(prefix = "line") {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch (_) {}
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function stableChartSupplementalId(kind, index, text) {
  const source = `${kind}:${index}:${String(text || "")}`;
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${kind}line-${(hash >>> 0).toString(36)}`;
}

function normalizeChartSupplementalLine(line, kind = "title", index = 0) {
  const source = line && typeof line === "object" && !Array.isArray(line)
    ? line
    : { text: String(line ?? "") };
  const normalizedKind = kind === "note" ? "note" : "title";
  const text = String(source.text ?? source.value ?? "");
  return {
    id: String(source.id || stableChartSupplementalId(normalizedKind, index, text)),
    text,
    alignment: normalizeTextAlignment(source.alignment, "center"),
    fontSizePx: clamp(safeNumber(source.fontSizePx, normalizedKind === "title" ? 14 : 12), 3, 96),
    bold: typeof source.bold === "boolean" ? source.bold : normalizedKind === "title",
    italic: source.italic === true,
    underline: source.underline === true,
  };
}

function normalizeChartSupplementalLines(value, kind = "title") {
  if (!Array.isArray(value)) return [];
  return value.map((line, index) => normalizeChartSupplementalLine(line, kind, index));
}

function parseChartSupplementalLines(value, kind = "title") {
  if (Array.isArray(value)) return normalizeChartSupplementalLines(value, kind);
  const raw = String(value || "").trim();
  if (!raw) return [];
  const candidates = [raw];
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded !== raw) candidates.unshift(decoded);
  } catch (_) {}
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (Array.isArray(parsed)) return normalizeChartSupplementalLines(parsed, kind);
    } catch (_) {}
  }
  return [normalizeChartSupplementalLine({ text: raw }, kind, 0)];
}

function serializeChartSupplementalLines(value, kind = "title") {
  const normalized = normalizeChartSupplementalLines(value, kind);
  if (!normalized.length) return "";
  try {
    return encodeURIComponent(JSON.stringify(normalized));
  } catch (_) {
    return "";
  }
}

function chartAspectRatio(width, height) {
  const w = Math.max(1, safeNumber(width, DOC_WRITER_DEFAULT_WIDTH));
  const h = Math.max(1, safeNumber(height, 390));
  return w / h;
}

function clampChartScale(startWidth, startHeight, desiredScale, maxWidth) {
  const minScale = Math.max(
    DOC_WRITER_MIN_WIDTH / Math.max(1, startWidth),
    DOC_WRITER_MIN_HEIGHT / Math.max(1, startHeight)
  );
  const maxScale = Math.min(
    Math.max(DOC_WRITER_MIN_WIDTH, maxWidth) / Math.max(1, startWidth),
    DOC_WRITER_MAX_HEIGHT / Math.max(1, startHeight)
  );
  return clamp(desiredScale, minScale, maxScale);
}

function normalizeXAxisLabelPosition(value) {
  return String(value || "").trim().toLowerCase() === "top" ? "top" : "bottom";
}

function normalizeYAxisLabelPosition(value) {
  return String(value || "").trim().toLowerCase() === "right" ? "right" : "left";
}

function boolSetting(value, fallback = true) {
  if (value === undefined || value === null || String(value).trim() === "") return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (["0", "false", "no", "off", "hidden", "none"].includes(normalized)) return false;
  if (["1", "true", "yes", "on", "show", "visible"].includes(normalized)) return true;
  return fallback;
}

function settingOrder(value, fallback) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  return Number.isFinite(parsed) ? clamp(parsed, 1, 3) : fallback;
}

function cssColor(value, fallback) {
  return normalizeColor(value, null) || fallback;
}

function safeColorPickerValue(value, fallback = "#2563eb") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw.slice(1).split("").map((c) => c + c).join("")}`;
  }
  return fallback;
}

function shouldShowDataLabels(visual) {
  return boolSetting(visual?.map?.show_data_labels, true);
}

function shouldShowGrid(visual) {
  return boolSetting(visual?.map?.show_grid, true);
}

function legendAlignment(visual) {
  return normalizeTextAlignment(visual?.map?.legend_align, "center");
}

function legendTextColor(visual) {
  return cssColor(visual?.map?.legend_color, TEXT_MUTED);
}

function getXAxisVisualProps(visual, { defaultLabel = "", defaultTickSize = 12 } = {}) {
  const map = visual?.map || {};
  const labelText = String(visual?.xLabel || defaultLabel || "").trim();
  const showLabel = Boolean(labelText) && boolSetting(map.show_x_axis_label, true);
  const position = normalizeXAxisLabelPosition(map.x_label_position);
  const axisColor = cssColor(map.axis_color, AXIS);
  const tickColor = cssColor(map.x_tick_color, TEXT_MUTED);
  const labelColor = cssColor(map.x_label_color, TEXT_MUTED);
  const props = {
    tick: { fill: tickColor, fontSize: clamp(safeNumber(map.x_tick_font_size, defaultTickSize), 9, 18) },
    axisLine: { stroke: axisColor },
    tickLine: { stroke: axisColor },
  };
  if (labelText) {
    props.label = showLabel
      ? {
          value: labelText,
          position: position === "top" ? "insideTop" : "insideBottom",
          offset: position === "top" ? 9 : -11,
          fill: labelColor,
          fontSize: clamp(safeNumber(map.x_label_font_size, 12), 9, 20),
          fontWeight: 700,
        }
      : undefined;
  }
  return props;
}

function getYAxisVisualProps(visual, { defaultLabel = "", defaultTickSize = 12 } = {}) {
  const map = visual?.map || {};
  const labelText = String(visual?.yLabel || defaultLabel || "").trim();
  const showLabel = Boolean(labelText) && boolSetting(map.show_y_axis_label, true);
  const position = normalizeYAxisLabelPosition(map.y_label_position);
  const axisColor = cssColor(map.axis_color, AXIS);
  const tickColor = cssColor(map.y_tick_color, TEXT_MUTED);
  const labelColor = cssColor(map.y_label_color, TEXT_MUTED);
  const props = {
    tick: { fill: tickColor, fontSize: clamp(safeNumber(map.y_tick_font_size, defaultTickSize), 9, 18) },
    axisLine: { stroke: axisColor },
    tickLine: { stroke: axisColor },
  };
  if (labelText) {
    props.label = showLabel
      ? {
          value: labelText,
          angle: position === "right" ? 90 : -90,
          position: position === "right" ? "insideRight" : "insideLeft",
          fill: labelColor,
          fontSize: clamp(safeNumber(map.y_label_font_size, 12), 9, 20),
          fontWeight: 700,
        }
      : undefined;
  }
  return props;
}

function getChartMargins(visual, base = {}) {
  const map = visual?.map || {};
  const legend = normalizeLegendPosition(map.legend_position || visual?.legendPosition);
  const labelsVisible = shouldShowDataLabels(visual);
  const xLabelVisible = Boolean(visual?.xLabel) && boolSetting(map.show_x_axis_label, true);
  const yLabelVisible = Boolean(visual?.yLabel) && boolSetting(map.show_y_axis_label, true);
  return {
    top: Math.max(8, safeNumber(base.top, 12)) + (legend === "top" ? 40 : 0) + (labelsVisible ? 24 : 0),
    right: Math.max(12, safeNumber(base.right, 36)) + (normalizeYAxisLabelPosition(map.y_label_position) === "right" && yLabelVisible ? 18 : 0),
    bottom: Math.max(20, safeNumber(base.bottom, 34)) + (legend === "bottom" ? 40 : 0) + (xLabelVisible ? 16 : 0),
    left: Math.max(12, safeNumber(base.left, 14)) + (normalizeYAxisLabelPosition(map.y_label_position) === "left" && yLabelVisible ? 18 : 0),
  };
}

function parseLabelOffsets(value) {
  const offsets = {};
  splitPipeList(value).forEach((part) => {
    const pieces = String(part || "").split(",").map((piece) => piece.trim());
    if (pieces.length < 4) return;
    const seriesIndex = Number.parseInt(pieces[0], 10);
    const dataIndex = Number.parseInt(pieces[1], 10);
    const dx = Number.parseFloat(pieces[2]);
    const dy = Number.parseFloat(pieces[3]);
    if (![seriesIndex, dataIndex, dx, dy].every(Number.isFinite)) return;
    offsets[`${seriesIndex}:${dataIndex}`] = { dx, dy };
  });
  return offsets;
}

function serializeLabelOffsets(offsets = {}) {
  return Object.entries(offsets)
    .map(([key, value]) => {
      const [seriesIndex, dataIndex] = key.split(":").map((part) => Number.parseInt(part, 10));
      const dx = Math.round(safeNumber(value?.dx, 0));
      const dy = Math.round(safeNumber(value?.dy, 0));
      if (!Number.isFinite(seriesIndex) || !Number.isFinite(dataIndex)) return "";
      return `${seriesIndex},${dataIndex},${dx},${dy}`;
    })
    .filter(Boolean)
    .join("|");
}

function shouldShowCategoryLabels(visual) {
  return boolSetting(visual?.map?.show_category_labels, true);
}

function categoryLabelColor(visual) {
  return cssColor(visual?.map?.category_label_color, visual?.map?.x_tick_color || TEXT_MUTED);
}

function categoryLabelFontSize(visual) {
  return clamp(safeNumber(visual?.map?.category_label_font_size, visual?.map?.x_tick_font_size || 12), 7, 28);
}

function categoryLabelAngle(visual) {
  return clamp(safeNumber(visual?.map?.category_label_angle, 0), -70, 70);
}

function parseCategoryOffsets(value) {
  const offsets = {};
  splitPipeList(value).forEach((part) => {
    const pieces = String(part || "").split(",").map((piece) => piece.trim());
    if (pieces.length < 3) return;
    const index = Number.parseInt(pieces[0], 10);
    const dx = Number.parseFloat(pieces[1]);
    const dy = Number.parseFloat(pieces[2]);
    if (![index, dx, dy].every(Number.isFinite)) return;
    offsets[index] = { dx, dy };
  });
  return offsets;
}

function serializeCategoryOffsets(offsets = {}) {
  return Object.entries(offsets)
    .map(([index, value]) => {
      const parsedIndex = Number.parseInt(index, 10);
      if (!Number.isFinite(parsedIndex)) return "";
      const dx = Math.round(safeNumber(value?.dx, 0));
      const dy = Math.round(safeNumber(value?.dy, 0));
      return `${parsedIndex},${dx},${dy}`;
    })
    .filter(Boolean)
    .join("|");
}

function parseHiddenCategoryIndices(value) {
  return new Set(
    String(value || "")
      .split(/[|,\s]+/)
      .map((part) => Number.parseInt(part, 10))
      .filter(Number.isFinite)
  );
}

function serializeHiddenCategoryIndices(indices) {
  return Array.from(indices || [])
    .map((value) => Number.parseInt(value, 10))
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .join(",");
}

function parseLegendOffset(value) {
  const parts = String(value || "")
    .split(",")
    .map((part) => Number.parseFloat(part.trim()));
  return {
    dx: Number.isFinite(parts[0]) ? parts[0] : 0,
    dy: Number.isFinite(parts[1]) ? parts[1] : 0,
  };
}

function serializeLegendOffset(offset = {}) {
  return `${Math.round(safeNumber(offset?.dx, 0))},${Math.round(safeNumber(offset?.dy, 0))}`;
}

function wrapCategoryLabel(value, maxChars = 14) {
  const text = String(value ?? "").trim();
  if (!text) return [""];
  if (text.length <= maxChars) return [text];
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    const midpoint = Math.ceil(text.length / 2);
    return [text.slice(0, midpoint), text.slice(midpoint)].filter(Boolean);
  }
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 3);
}

function categoryAxisHeight(visual, dataCount = 1) {
  if (!shouldShowCategoryLabels(visual)) return 18;
  const fontSize = categoryLabelFontSize(visual);
  const angle = Math.abs(categoryLabelAngle(visual));
  const denseExtra = dataCount > 8 ? 10 : 0;
  return clamp(Math.round(fontSize * 2.7 + angle * 0.55 + denseExtra), 34, 96);
}


function normalizeDataLabelContent(value) {
  const normalized = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const aliases = {
    value: "value",
    value_only: "value",
    category: "category",
    category_only: "category",
    series: "series",
    series_only: "series",
    category_value: "category_value",
    category_and_value: "category_value",
    series_value: "series_value",
    series_and_value: "series_value",
    category_series: "category_series",
    category_and_series: "category_series",
    category_series_value: "category_series_value",
    all: "category_series_value",
  };
  return aliases[normalized] || "value";
}

function normalizeDataTableMode(value) {
  const normalized = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (["", "none", "hidden", "off", "false"].includes(normalized)) return "none";
  if (["values", "values_only", "compact_values"].includes(normalized)) return "values";
  if (["category_value", "category_values", "categories"].includes(normalized)) return "category_values";
  if (["series_value", "series_values", "series"].includes(normalized)) return "series_values";
  if (["table", "full", "full_table", "matrix"].includes(normalized)) return "full_table";
  return "none";
}

function normalizeLegendContent(value) {
  const normalized = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ["name_value", "names_values", "value", "with_value"].includes(normalized) ? "name_value" : "name";
}

function resolveChartLabelParts(visual, index = 0, seriesIndex = 0, value, explicitCategory = "", explicitSeries = "") {
  const type = normalizeType(visual?.type || visual?.map?.type || "");
  let category = String(explicitCategory || "").trim();
  let series = String(explicitSeries || "").trim();

  if (!category || !series) {
    if (["scatter", "bubble"].includes(type)) {
      let points = parsePointList(visual?.map?.points || visual?.map?.data || "");
      if (!points.length) points = parseXYLists(visual?.map || {});
      const point = points[index];
      if (!category) category = String(point?.name || `Point ${index + 1}`);
      if (!series) series = String(visual?.map?.series_name || visual?.yLabel || "Value");
    } else if (type === "line") {
      const normalized = normalizeLineData(visual?.map || {});
      const row = normalized.data?.[index];
      const key = normalized.keys?.[seriesIndex];
      if (!category) category = String(row?.name ?? row?.x ?? `Item ${index + 1}`);
      if (!series) series = String((key && key !== "value" ? key : visual?.map?.series_name || visual?.yLabel || "Value"));
    } else {
      const normalized = normalizeMultiSeriesData(visual?.map || {});
      const row = normalized.data?.[index];
      const key = normalized.keys?.[seriesIndex];
      if (!category) category = String(row?.name || `Item ${index + 1}`);
      if (!series) series = String((key && key !== "value" ? key : visual?.map?.series_name || visual?.map?.value_label || visual?.yLabel || "Value"));
    }
  }

  return {
    category: category || `Item ${index + 1}`,
    series: series || "Value",
    value: formatValue(value),
  };
}

function formatChartDataLabel({ visual, index = 0, seriesIndex = 0, value, category = "", series = "" }) {
  const parts = resolveChartLabelParts(visual, index, seriesIndex, value, category, series);
  switch (normalizeDataLabelContent(visual?.map?.data_label_content)) {
    case "category": return parts.category;
    case "series": return parts.series;
    case "category_value": return `${parts.category} · ${parts.value}`;
    case "series_value": return `${parts.series} · ${parts.value}`;
    case "category_series": return `${parts.category} · ${parts.series}`;
    case "category_series_value": return `${parts.category} · ${parts.series} · ${parts.value}`;
    default: return parts.value;
  }
}

function buildChartDataDisplayModel(visual) {
  const map = visual?.map || {};
  const type = normalizeType(visual?.type || map.type || "");

  if (["scatter", "bubble"].includes(type)) {
    let points = parsePointList(map.points || map.data || "");
    if (!points.length) points = parseXYLists(map);
    if (!points.length) return null;
    return {
      categories: points.map((point, index) => String(point.name || `Point ${index + 1}`)),
      series: [String(visual?.xLabel || "X"), String(visual?.yLabel || "Y")],
      values: points.map((point) => [point.x, point.y]),
      colors: getSeriesColors(2, visual),
    };
  }

  if (type === "line") {
    const normalized = normalizeLineData(map);
    if (normalized.data?.length && normalized.keys?.length) {
      return {
        categories: normalized.data.map((row, index) => String(row?.name ?? row?.x ?? `Item ${index + 1}`)),
        series: normalized.keys.map((key) => String(key === "value" ? map.series_name || visual?.yLabel || "Value" : key)),
        values: normalized.data.map((row) => normalized.keys.map((key) => safeNumber(row?.[key], 0))),
        colors: getSeriesColors(normalized.keys.length, visual),
      };
    }
  }

  const multi = normalizeMultiSeriesData(map);
  if (multi.data?.length && multi.keys?.length) {
    return {
      categories: multi.data.map((row, index) => String(row?.name || `Item ${index + 1}`)),
      series: multi.keys.map((key) => String(key === "value" ? map.series_name || map.value_label || visual?.yLabel || "Value" : key)),
      values: multi.data.map((row) => multi.keys.map((key) => safeNumber(row?.[key], 0))),
      colors: getSeriesColors(multi.keys.length === 1 && multi.data.length > 1 ? multi.data.length : multi.keys.length, visual),
    };
  }

  const base = normalizeCategoryData(map);
  if (base.length) {
    return {
      categories: base.map((row, index) => String(row?.name || `Item ${index + 1}`)),
      series: [String(map.series_name || map.value_label || visual?.yLabel || "Value")],
      values: base.map((row) => [safeNumber(row?.value, 0)]),
      colors: getSeriesColors(base.length, visual),
    };
  }
  return null;
}

function ChartDataDisplay({ visual }) {
  const mode = normalizeDataTableMode(visual?.map?.data_table_mode);
  if (mode === "none") return null;
  const model = buildChartDataDisplayModel(visual);
  if (!model?.categories?.length || !model?.series?.length) return null;

  const map = visual?.map || {};
  const showColors = boolSetting(map.data_table_show_colors, true);
  const showHeaders = boolSetting(map.data_table_show_headers, true);
  const showCategories = boolSetting(map.data_table_show_categories, true);
  const showSeries = boolSetting(map.data_table_show_series, true);
  const fontSize = clamp(safeNumber(map.data_table_font_size, 11), 7, 18);
  const color = cssColor(map.data_table_text_color, TEXT_DARK);
  const borderColor = cssColor(map.data_table_border_color, "rgba(148,163,184,0.45)");
  const cellStyle = { padding: "6px 8px", borderBottom: `1px solid ${borderColor}`, borderRight: `1px solid ${borderColor}`, color, fontSize, lineHeight: 1.3, textAlign: "right", verticalAlign: "top" };
  const categoryCellStyle = { ...cellStyle, textAlign: "left", fontWeight: 750, whiteSpace: "normal", overflowWrap: "anywhere" };

  if (mode === "values") {
    const flat = model.values.flat().map((value) => formatValue(value));
    return <div className="doc-writer-chart-data-below" style={{ marginTop: 8, padding: "7px 9px", borderTop: `1px solid ${borderColor}`, color, fontSize, lineHeight: 1.45, textAlign: "center", overflowWrap: "anywhere" }}>{flat.join(" · ")}</div>;
  }

  if (mode === "category_values") {
    return (
      <div className="doc-writer-chart-data-below" style={{ marginTop: 8, display: "grid", gap: 4, padding: "7px 9px", borderTop: `1px solid ${borderColor}`, color, fontSize }}>
        {model.categories.map((category, rowIndex) => (
          <div key={`category-values-${rowIndex}`} style={{ display: "flex", flexWrap: "wrap", gap: "5px 10px", alignItems: "baseline" }}>
            {showCategories ? <strong>{category}</strong> : null}
            <span>{model.values[rowIndex].map((value, seriesIndex) => `${showSeries && model.series.length > 1 ? `${model.series[seriesIndex]}: ` : ""}${formatValue(value)}`).join(" · ")}</span>
          </div>
        ))}
      </div>
    );
  }

  if (mode === "series_values") {
    return (
      <div className="doc-writer-chart-data-below" style={{ marginTop: 8, display: "grid", gap: 4, padding: "7px 9px", borderTop: `1px solid ${borderColor}`, color, fontSize }}>
        {model.series.map((seriesName, seriesIndex) => (
          <div key={`series-values-${seriesIndex}`} style={{ display: "flex", flexWrap: "wrap", gap: "5px 10px", alignItems: "baseline" }}>
            {showSeries ? <strong style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{showColors ? <span style={{ width: 9, height: 9, borderRadius: 2, background: model.colors[seriesIndex % model.colors.length], display: "inline-block" }} /> : null}{seriesName}</strong> : null}
            <span>{model.values.map((row, rowIndex) => `${showCategories ? `${model.categories[rowIndex]}: ` : ""}${formatValue(row[seriesIndex])}`).join(" · ")}</span>
          </div>
        ))}
      </div>
    );
  }

  const columnCount = model.series.length + (showCategories ? 1 : 0);
  const gridTemplateColumns = `${showCategories ? "minmax(110px, 1.2fr) " : ""}repeat(${model.series.length}, minmax(78px, 1fr))`;
  const gridCellStyle = { ...cellStyle, borderTop: 0, borderLeft: 0, minWidth: 0, boxSizing: "border-box" };

  return (
    <div className="doc-writer-chart-data-below" style={{ marginTop: 8, width: "100%", maxWidth: "100%", overflowX: "auto", borderTop: `1px solid ${borderColor}`, paddingTop: 8 }}>
      <div role="table" aria-label="Chart data" style={{ minWidth: Math.max(280, columnCount * 92), borderTop: `1px solid ${borderColor}`, borderLeft: `1px solid ${borderColor}`, color, fontSize }}>
        {showHeaders ? (
          <div role="row" style={{ display: "grid", gridTemplateColumns }}>
            {showCategories ? <div role="columnheader" style={{ ...categoryCellStyle, background: "rgba(248,250,252,0.92)" }}>Category</div> : null}
            {model.series.map((seriesName, seriesIndex) => (
              <div key={`series-head-${seriesIndex}`} role="columnheader" style={{ ...gridCellStyle, textAlign: "center", fontWeight: 850, background: "rgba(248,250,252,0.92)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  {showColors ? <span style={{ width: 9, height: 9, borderRadius: 2, background: model.colors[seriesIndex % model.colors.length], display: "inline-block" }} /> : null}
                  {showSeries ? seriesName : `Value ${seriesIndex + 1}`}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {model.categories.map((category, rowIndex) => (
          <div key={`data-row-${rowIndex}`} role="row" style={{ display: "grid", gridTemplateColumns }}>
            {showCategories ? (
              <div role="rowheader" style={categoryCellStyle}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {showColors && model.series.length === 1 && model.colors.length > 1 ? <span style={{ width: 9, height: 9, borderRadius: 2, background: model.colors[rowIndex % model.colors.length], display: "inline-block", flex: "0 0 auto" }} /> : null}
                  {category}
                </span>
              </div>
            ) : null}
            {model.values[rowIndex].map((value, seriesIndex) => <div role="cell" key={`data-cell-${rowIndex}-${seriesIndex}`} style={gridCellStyle}>{formatValue(value)}</div>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function stripStructuredMarker(value) {
  return String(value || "")
    .replace(/^\s*\[(?:chart|graph|visual|diagram|geometry|flowchart)\]\s*/i, "")
    .trim();
}

function mapObjectChartSpec(spec) {
  if (!spec || typeof spec !== "object" || Array.isArray(spec)) return {};
  const map = {};
  const meta = spec.meta && typeof spec.meta === "object" ? spec.meta : {};
  const type = spec.type || spec.chartType || spec.chart_type || spec.kind || spec.visualType;
  if (type != null) map.type = normalizeType(type);

  const scalarFields = {
    title: spec.title ?? meta.title,
    subtitle: spec.subtitle ?? spec.description ?? meta.description,
    x_label: spec.x_label ?? spec.xAxisLabel ?? spec.xlabel,
    y_label: spec.y_label ?? spec.yAxisLabel ?? spec.ylabel,
    z_label: spec.z_label ?? spec.zAxisLabel ?? spec.zlabel,
    legend_position: spec.legend_position ?? spec.legendPosition,
    legend_align: spec.legend_align ?? spec.legendAlign,
    legend_color: spec.legend_color ?? spec.legendColor,
    legend_font_size: spec.legend_font_size ?? spec.legendFontSize,
    legend_offset: spec.legend_offset ?? spec.legendOffset,
    legend_content: spec.legend_content ?? spec.legendContent,
    show_title: spec.show_title ?? spec.showTitle,
    title_align: spec.title_align ?? spec.titleAlign,
    title_color: spec.title_color ?? spec.titleColor,
    title_font_size: spec.title_font_size ?? spec.titleFontSize,
    title_order: spec.title_order ?? spec.titleOrder,
    show_subtitle: spec.show_subtitle ?? spec.showSubtitle,
    subtitle_align: spec.subtitle_align ?? spec.subtitleAlign,
    subtitle_color: spec.subtitle_color ?? spec.subtitleColor,
    subtitle_font_size: spec.subtitle_font_size ?? spec.subtitleFontSize,
    subtitle_order: spec.subtitle_order ?? spec.subtitleOrder,
    show_chart_type: spec.show_chart_type ?? spec.showChartType,
    chart_type_align: spec.chart_type_align ?? spec.chartTypeAlign,
    chart_type_color: spec.chart_type_color ?? spec.chartTypeColor,
    chart_type_font_size: spec.chart_type_font_size ?? spec.chartTypeFontSize,
    chart_type_order: spec.chart_type_order ?? spec.chartTypeOrder,
    show_data_labels: spec.show_data_labels ?? spec.showDataLabels,
    data_label_content: spec.data_label_content ?? spec.dataLabelContent,
    data_label_color: spec.data_label_color ?? spec.dataLabelColor,
    data_label_font_size: spec.data_label_font_size ?? spec.dataLabelFontSize,
    label_pointer: spec.label_pointer ?? spec.labelPointer,
    label_offsets: spec.label_offsets ?? spec.labelOffsets,
    data_table_mode: spec.data_table_mode ?? spec.dataTableMode ?? spec.bottom_data_mode ?? spec.bottomDataMode,
    data_table_show_colors: spec.data_table_show_colors ?? spec.dataTableShowColors,
    data_table_show_headers: spec.data_table_show_headers ?? spec.dataTableShowHeaders,
    data_table_show_categories: spec.data_table_show_categories ?? spec.dataTableShowCategories,
    data_table_show_series: spec.data_table_show_series ?? spec.dataTableShowSeries,
    data_table_font_size: spec.data_table_font_size ?? spec.dataTableFontSize,
    data_table_text_color: spec.data_table_text_color ?? spec.dataTableTextColor,
    data_table_border_color: spec.data_table_border_color ?? spec.dataTableBorderColor,
    show_category_labels: spec.show_category_labels ?? spec.showCategoryLabels,
    category_label_color: spec.category_label_color ?? spec.categoryLabelColor,
    category_label_font_size: spec.category_label_font_size ?? spec.categoryLabelFontSize,
    category_label_pointer: spec.category_label_pointer ?? spec.categoryLabelPointer,
    category_label_offsets: spec.category_label_offsets ?? spec.categoryLabelOffsets,
    hidden_category_indices: spec.hidden_category_indices ?? spec.hiddenCategoryIndices,
    category_label_angle: spec.category_label_angle ?? spec.categoryLabelAngle,
    show_x_axis_label: spec.show_x_axis_label ?? spec.showXAxisLabel,
    x_label_position: spec.x_label_position ?? spec.xLabelPosition,
    x_label_color: spec.x_label_color ?? spec.xLabelColor,
    x_label_font_size: spec.x_label_font_size ?? spec.xLabelFontSize,
    x_tick_color: spec.x_tick_color ?? spec.xTickColor,
    x_tick_font_size: spec.x_tick_font_size ?? spec.xTickFontSize,
    show_y_axis_label: spec.show_y_axis_label ?? spec.showYAxisLabel,
    y_label_position: spec.y_label_position ?? spec.yLabelPosition,
    y_label_color: spec.y_label_color ?? spec.yLabelColor,
    y_label_font_size: spec.y_label_font_size ?? spec.yLabelFontSize,
    y_tick_color: spec.y_tick_color ?? spec.yTickColor,
    y_tick_font_size: spec.y_tick_font_size ?? spec.yTickFontSize,
    show_grid: spec.show_grid ?? spec.showGrid,
    grid_color: spec.grid_color ?? spec.gridColor,
    axis_color: spec.axis_color ?? spec.axisColor,
    line_width: spec.line_width ?? spec.lineWidth,
    marker_size: spec.marker_size ?? spec.markerSize,
    bar_gap: spec.bar_gap ?? spec.barGap,
    value_label: spec.value_label ?? spec.valueLabel,
    equation: spec.equation ?? spec.formula,
    trendline: spec.trendline,
    regression: spec.regression,
    show_regression: spec.show_regression ?? spec.showRegression,
    line: spec.line ?? spec.connect,
    doc_width: spec.doc_width ?? spec.docWidth ?? spec.canvas_width ?? spec.canvasWidth,
    doc_height: spec.doc_height ?? spec.docHeight ?? spec.canvas_height ?? spec.canvasHeight,
    doc_alignment: spec.doc_alignment ?? spec.docAlignment,
    doc_top_edge_inset: spec.doc_top_edge_inset ?? spec.docTopEdgeInset ?? meta.docTopEdgeInset,
    lock_aspect_ratio: spec.lock_aspect_ratio ?? spec.lockAspectRatio ?? meta.lockAspectRatio,
    width: spec.width,
    height: spec.height,
    alignment: spec.alignment ?? spec.align,
    background: spec.background,
    partitions: spec.partitions,
    showareas: spec.showareas ?? spec.showAreas,
    showdimensions: spec.showdimensions ?? spec.showDimensions,
    radius: spec.radius,
    degrees: spec.degrees,
    angle: spec.angle,
    min: spec.min,
    max: spec.max,
    bins: spec.bins,
  };
  const topTitlesSource = spec.topTitles ?? spec.top_titles ?? meta.topTitles ?? meta.top_titles;
  const bottomNotesSource = spec.bottomNotes ?? spec.bottom_notes ?? meta.bottomNotes ?? meta.bottom_notes;
  if (Array.isArray(topTitlesSource)) map.top_titles = serializeChartSupplementalLines(topTitlesSource, "title");
  else if (topTitlesSource !== undefined && topTitlesSource !== null && String(topTitlesSource).trim() !== "") map.top_titles = String(topTitlesSource).trim();
  if (Array.isArray(bottomNotesSource)) map.bottom_notes = serializeChartSupplementalLines(bottomNotesSource, "note");
  else if (bottomNotesSource !== undefined && bottomNotesSource !== null && String(bottomNotesSource).trim() !== "") map.bottom_notes = String(bottomNotesSource).trim();

  Object.entries(scalarFields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      map[key] = String(value).trim();
    }
  });

  if (Array.isArray(spec.partitions)) {
    const serializedPartitions = spec.partitions.map((item, index) => {
      if (!item || typeof item !== "object") return "";
      const name = item.name || item.label || `Section ${index + 1}`;
      const width = item.width ?? item.w;
      const height = item.height ?? item.h;
      if (!Number.isFinite(Number(width)) || !Number.isFinite(Number(height))) return "";
      return `${name}:${Number(width)}x${Number(height)}`;
    }).filter(Boolean);
    if (serializedPartitions.length) map.partitions = serializedPartitions.join("|");
  }

  [
    ["labels", "labels"], ["categories", "labels"], ["values", "values"],
    ["colors", "colors"], ["palette", "colors"], ["points", "points"],
    ["x_values", "x_values"], ["y_values", "y_values"], ["rows", "rows"],
    ["cols", "cols"], ["columns", "cols"], ["low", "low"], ["mean", "mean"],
    ["expected", "expected"], ["high", "high"], ["tasks", "tasks"],
    ["events", "events"], ["candles", "candles"], ["data", "data"],
  ].forEach(([sourceKey, targetKey]) => {
    const value = spec[sourceKey];
    if (Array.isArray(value)) {
      if (value.length && typeof value[0] === "object") return;
      map[targetKey] = value.join(",");
    } else if (value !== undefined && value !== null && typeof value !== "object") {
      map[targetKey] = String(value).trim();
    }
  });

  const rows = Array.isArray(spec.data)
    ? spec.data.filter((row) => row && typeof row === "object" && !Array.isArray(row))
    : [];
  const xKey = spec.xKey || spec.x_key || spec.nameKey || spec.name_key;
  const valueKey = spec.valueKey || spec.value_key;
  const seriesDefs = Array.isArray(spec.series) ? spec.series : [];

  if (rows.length) {
    const normalizedSpecType = normalizeType(type);
    const scatterSeriesKey = seriesDefs.find((series) => series && typeof series === "object" && (series.dataKey || series.data_key));
    const scatterYKey = scatterSeriesKey?.dataKey || scatterSeriesKey?.data_key || valueKey || "y";
    if (["scatter", "bubble"].includes(normalizedSpecType) && xKey && scatterYKey) {
      map.points = rows.map((row, index) => {
        const x = row[String(xKey)];
        const y = row[String(scatterYKey)];
        const zKey = spec.zKey || spec.z_key;
        const z = zKey ? row[String(zKey)] : undefined;
        const label = row.name || row.label || row.month || row.period || `Point ${index + 1}`;
        return `${label}:${x},${y}${z !== undefined ? `,${z}` : ""}`;
      }).join("|");
    }

    if (xKey) map.labels = rows.map((row, index) => row[String(xKey)] ?? `Item ${index + 1}`).join(",");
    if (valueKey) map.values = rows.map((row) => row[String(valueKey)] ?? 0).join(",");

    if (seriesDefs.length) {
      const parts = seriesDefs.map((series, index) => {
        if (!series || typeof series !== "object") return "";
        const dataKey = series.dataKey || series.data_key;
        if (!dataKey) return "";
        const label = series.label || series.name || dataKey || `Series ${index + 1}`;
        return `${label}:${rows.map((row) => row[String(dataKey)] ?? 0).join(",")}`;
      }).filter(Boolean);
      if (parts.length) map.series = parts.join("|");

      const seriesColors = seriesDefs.map((series) => series?.color || series?.stroke || series?.fill).filter(Boolean);
      if (seriesColors.length && !map.colors) map.colors = seriesColors.join(",");
    }

    if (!xKey && !valueKey && !map.series) {
      const first = rows[0] || {};
      const labelKey = ["name", "category", "label", "period", "month", "year"].find((key) => key in first);
      const inferredValueKey = ["value", "amount", "revenue", "count", "percentage"].find((key) => key in first);
      const xNumericKey = ["x", "adSpend", "marketing", "spend"].find((key) => key in first);
      const yNumericKey = ["y", "revenue", "value", "amount"].find((key) => key in first && key !== xNumericKey);
      if (labelKey && inferredValueKey) {
        map.labels = rows.map((row) => row[labelKey]).join(",");
        map.values = rows.map((row) => row[inferredValueKey] ?? 0).join(",");
      } else if (xNumericKey && yNumericKey) {
        map.points = rows.map((row, index) => `${row.name || row.label || `Point ${index + 1}`}:${row[xNumericKey]},${row[yNumericKey]}`).join("|");
      }
    }
  } else if (seriesDefs.length) {
    const parts = seriesDefs.map((series, index) => {
      if (!series || typeof series !== "object") return "";
      const values = series.values || series.data;
      if (!Array.isArray(values)) return "";
      const label = series.label || series.name || series.dataKey || `Series ${index + 1}`;
      return `${label}:${values.join(",")}`;
    }).filter(Boolean);
    if (parts.length) map.series = parts.join("|");
  }

  Object.entries(spec).forEach(([key, value]) => {
    const normalized = String(key).trim().toLowerCase().replace(/[\s-]+/g, "_");
    if (!normalized || normalized in map || value == null || typeof value === "object") return;
    map[normalized] = String(value).trim();
  });

  return map;
}

function serializeInstructionMap(map = {}) {
  const keys = [];
  const seen = new Set();
  DOC_WRITER_SOURCE_ORDER.forEach((key) => {
    if (map[key] !== undefined && map[key] !== null && String(map[key]).trim() !== "") {
      keys.push(key);
      seen.add(key);
    }
  });
  Object.keys(map).sort().forEach((key) => {
    if (!seen.has(key) && map[key] !== undefined && map[key] !== null && String(map[key]).trim() !== "") {
      keys.push(key);
    }
  });
  const body = keys.map((key) => `${key}=${String(map[key]).replace(/[\r\n]+/g, " ").trim()}`).join("; ");
  return `[chart]\n${body}`;
}

export function normalizeDocWriterChartSource(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return serializeInstructionMap(mapObjectChartSpec(value));
  }
  const raw = String(value || "").trim();
  if (!raw) return "";
  const payload = stripStructuredMarker(raw);
  if (/^\{[\s\S]*\}$/.test(payload)) {
    try {
      return serializeInstructionMap(mapObjectChartSpec(JSON.parse(payload)));
    } catch (_) {}
  }
  return raw;
}

function docWriterChartMapFromValue(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) return mapObjectChartSpec(value);
  const raw = String(value || "").trim();
  const payload = stripStructuredMarker(raw);
  if (/^\{[\s\S]*\}$/.test(payload)) {
    try {
      return mapObjectChartSpec(JSON.parse(payload));
    } catch (_) {}
  }
  return parseInstructionMap(payload);
}

function getRecommendedDocWriterWidth(type, maxWidth = DOC_WRITER_DEFAULT_MAX_WIDTH) {
  const t = normalizeType(type);
  const desired = ["pie", "donut", "gauge", "progress", "circle", "square", "triangle", "right_triangle", "angle", "venn"].includes(t)
    ? 500
    : 620;
  return Math.min(maxWidth, desired);
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function safeNumber(value, fallback = 0) {
  if (value === null || value === undefined) return fallback;
  const cleaned = String(value).replace(/[$,%]/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : fallback;
}

function toFiniteNumber(value) {
  const n = safeNumber(value, NaN);
  return Number.isFinite(n) ? n : null;
}

function isTruthyFlag(value) {
  const s = String(value ?? "").trim().toLowerCase();
  return ["1", "true", "yes", "y", "on", "line", "connect", "connected"].includes(s);
}

function formatValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? "");
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return n.toLocaleString();
  if (Math.abs(n) > 0 && Math.abs(n) < 1) return n.toFixed(3);
  return String(Number.isInteger(n) ? n : Number(n.toFixed(3)));
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function splitLooseList(value) {
  return String(value || "")
    .split(/[,|]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function splitPipeList(value) {
  return String(value || "")
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseNumberList(value) {
  return splitLooseList(value)
    .map((v) => toFiniteNumber(v))
    .filter((n) => n !== null);
}

function normalizeColor(color, fallback = null) {
  const raw = String(color || "").trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (COLOR_NAME_MAP[lower]) return COLOR_NAME_MAP[lower];
  if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
  if (/^rgba?\(/i.test(raw)) return raw;
  if (/^hsla?\(/i.test(raw)) return raw;
  if (lower === "transparent") return "transparent";
  if (/^[a-z]+$/i.test(raw)) return raw;
  return fallback || raw;
}

function parseColorList(value) {
  return splitLooseList(value)
    .map((c) => normalizeColor(c, null))
    .filter(Boolean);
}

function getSeriesColors(count, visual) {
  const requested = Array.isArray(visual.colors) ? visual.colors : [];
  return Array.from({ length: count }, (_, i) => requested[i] || DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]);
}

function normalizeInstructionKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
}

const DOC_WRITER_KNOWN_SOURCE_KEYS = new Set([
  ...DOC_WRITER_SOURCE_ORDER,
  ...DOC_WRITER_EDITABLE_FIELDS.map(([key]) => key),
  "chart", "graph", "visual", "diagram", "name", "note",
  "xlabel", "ylabel", "zlabel", "x_axis", "y_axis", "z_axis",
  "categories", "category", "dataset", "datasets", "palette",
  "amount", "amounts", "value", "label", "periods", "xs", "ys",
]);

const DOC_WRITER_CONTINUATION_KEYS = new Set([
  "data", "points", "series", "tasks", "events", "candles",
  "partitions", "rows", "cols",
]);

function splitStructuredSegments(value) {
  const raw = String(value || "").trim();
  if (!raw) return [];
  if (raw.includes("|")) return raw.split("|").map((part) => part.trim()).filter(Boolean);
  if (raw.includes(";")) return raw.split(";").map((part) => part.trim()).filter(Boolean);
  return [raw];
}

function parseNarrativeChartInstruction(raw) {
  const text = String(raw || "").trim();
  const map = {};
  if (!text) return map;

  const heading = text.match(/(?:^|\n)\s*(?:chart|graph|visual|diagram)\s*:\s*["“]?([^"”\n(]+?)["”]?\s*\(([^)]+)\)/i);
  if (heading) {
    map.title = heading[1].trim().replace(/[.\s]+$/, "");
    map.type = normalizeType(heading[2]);
  }

  const titleMatch = text.match(/(?:^|[;\n])\s*(?:title|chart_title|chart title)\s*[:=]\s*["“]?([^"”\n;]+)["”]?/i);
  if (!map.title && titleMatch) map.title = titleMatch[1].trim().replace(/[.\s]+$/, "");

  const typeMatch = text.match(/(?:^|[;\n])\s*(?:type|chart_type|chart type)\s*[:=]\s*([a-zA-Z][a-zA-Z0-9 _-]*)/i);
  if (!map.type && typeMatch) map.type = normalizeType(typeMatch[1]);

  const fieldPatterns = [
    ["labels", /\b(?:labels|categories)\s*[:=]\s*([^\n.]+?)(?=(?:\s+[A-Z][A-Za-z _-]{1,30}\s*[:=])|$)/i],
    ["values", /\bvalues\s*[:=]\s*([^\n.]+?)(?=(?:\s+[A-Z][A-Za-z _-]{1,30}\s*[:=])|$)/i],
    ["series", /\bseries\s*[:=]\s*([^\n]+?)(?=(?:\s+(?:points|colors|legend|x[- ]?axis|y[- ]?axis)\s*[:=])|$)/i],
    ["points", /\bpoints\s*[:=]\s*([^\n]+?)(?=(?:\s+(?:series|colors|legend|x[- ]?axis|y[- ]?axis)\s*[:=])|$)/i],
  ];
  for (const [key, pattern] of fieldPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) map[key] = match[1].trim().replace(/[.\s]+$/, "");
  }

  const dataMatch = text.match(/\bdata\s*:\s*([\s\S]+?)(?=(?:\n\s*(?:note|caption|description|x[- ]?axis|y[- ]?axis|legend|colors?)\s*[:=])|$)/i);
  if (dataMatch && dataMatch[1]) {
    const dataText = dataMatch[1].trim().replace(/[.\s]+$/, "");
    const type = normalizeType(map.type || "");
    const segments = splitStructuredSegments(dataText);
    const numericPairs = segments.filter((segment) => /[:=]\s*-?\$?\d/.test(segment));
    if (["scatter", "bubble"].includes(type) || numericPairs.some((segment) => /[:=][^;|]*,\s*-?\$?\d/.test(segment))) {
      map.points = segments.join("|");
    } else if (numericPairs.length) {
      const rhsHaveLists = numericPairs.filter((segment) => {
        const rhs = String(segment.split(/[:=]/).slice(1).join(":") || "");
        return (rhs.match(/-?\$?\d+(?:\.\d+)?/g) || []).length > 1;
      });
      if (rhsHaveLists.length === numericPairs.length && numericPairs.length > 1) {
        map.series = segments.join("|");
      } else {
        map.data = segments.join("|");
      }
    } else {
      map.data = dataText;
    }
  }

  const colorMatch = text.match(/\bcolors?\s*[:=]\s*([^\n.]+)/i);
  if (colorMatch) map.colors = colorMatch[1].trim();

  return map;
}

function parseInstructionMap(raw) {
  const map = {};
  const text = String(raw || "").trim();
  if (!text) return map;

  let currentKey = "";
  const parts = text
    .split(/[;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  for (const part of parts) {
    const match = part.match(/^([a-zA-Z_][a-zA-Z0-9_\-\s]*)\s*[:=]\s*(.+)$/);
    if (!match) {
      if (currentKey && DOC_WRITER_CONTINUATION_KEYS.has(currentKey)) {
        map[currentKey] = `${map[currentKey] || ""}; ${part}`.trim();
      }
      continue;
    }

    const key = normalizeInstructionKey(match[1]);
    const value = match[2].trim();
    if (DOC_WRITER_KNOWN_SOURCE_KEYS.has(key)) {
      map[key] = value;
      currentKey = key;
      continue;
    }

    if (currentKey && DOC_WRITER_CONTINUATION_KEYS.has(currentKey)) {
      map[currentKey] = `${map[currentKey] || ""}; ${part}`.trim();
    }
  }

  const narrative = parseNarrativeChartInstruction(text);
  return { ...narrative, ...map };
}

function normalizeType(type) {
  const t = String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const aliases = {
    vertical_bar: "column",
    vertical_bars: "column",
    column_chart: "column",
    bar_chart: "bar",
    horizontal_bar: "bar",
    horizontal_bars: "bar",
    grouped_column: "grouped_bar",
    grouped_columns: "grouped_bar",
    grouped_bar_chart: "grouped_bar",
    stacked_column: "stacked_bar",
    stacked_columns: "stacked_bar",
    stacked_bar_chart: "stacked_bar",
    trend: "line",
    multi_line: "line",
    multiline: "line",
    line_chart: "line",
    area_chart: "area",
    stacked_area_chart: "stacked_area",
    doughnut: "donut",
    donut_chart: "donut",
    scatterplot: "scatter",
    scatter_plot: "scatter",
    scatter_diagram: "scatter",
    connected_scatter: "scatter",
    scatter_line: "scatter",
    bubble_chart: "bubble",
    distribution: "histogram",
    frequency: "histogram",
    frequency_distribution: "histogram",
    bell: "bell_curve",
    normal: "bell_curve",
    normal_curve: "bell_curve",
    gaussian: "bell_curve",
    gaussian_curve: "bell_curve",
    precise_bell: "bell_curve",
    precise_bell_curve: "bell_curve",
    normal_distribution: "bell_curve",
    confidence_interval: "confidence_interval",
    confidence_intervals: "confidence_interval",
    ci: "confidence_interval",
    ci_chart: "confidence_interval",
    error_bar: "confidence_interval",
    error_bars: "confidence_interval",
    errorbar: "confidence_interval",
    confidence_band: "confidence_band",
    confidence_bands: "confidence_band",
    probability_band: "confidence_band",
    probability_bands: "confidence_band",
    line_confidence_band: "confidence_band",
    forecast_band: "confidence_band",
    uncertainty_band: "confidence_band",
    kde: "kde_density",
    kde_density: "kde_density",
    density: "kde_density",
    density_curve: "kde_density",
    kernel_density: "kde_density",
    roc: "roc_curve",
    roc_curve: "roc_curve",
    receiver_operating_characteristic: "roc_curve",
    pr_curve: "precision_recall",
    precision_recall_curve: "precision_recall",
    precision_recall: "precision_recall",
    violin: "violin_plot",
    violin_plot: "violin_plot",
    regression: "scatter",
    regression_trendline: "scatter",
    trendline_scatter: "scatter",
    spider: "radar",
    spider_chart: "radar",
    radar_chart: "radar",
    combo: "composed",
    mixed: "composed",
    mixed_chart: "composed",
    bridge: "waterfall",
    bridge_chart: "waterfall",
    waterfall_chart: "waterfall",
    box_plot: "boxplot",
    box_whisker: "boxplot",
    box_and_whisker: "boxplot",
    heat_map: "heatmap",
    matrix: "heatmap",
    funnel_chart: "funnel",
    tree_map: "treemap_basic",
    treemap: "treemap_basic",
    meter: "gauge",
    gauge_chart: "gauge",
    progress_bar: "progress",
    time_line: "timeline",
    gantt: "gantt_basic",
    gantt_chart: "gantt_basic",
    candlestick: "candlestick_basic",
    candle: "candlestick_basic",
    ohlc: "candlestick_basic",
    pareto_chart: "pareto",
    slope_chart: "slope",
    lollipop_chart: "lollipop",
    dot_plot: "dotplot",
    dot_chart: "dotplot",
    coordinate: "coordinate_graph",
    coordinate_graph: "coordinate_graph",
    coordinate_plane: "coordinate_graph",
    x_y_graph: "coordinate_graph",
    graph: "coordinate_graph",
    oval: "ellipse",
    right_triangle: "right_triangle",
    sine: "sine_wave",
    cosine: "cosine_wave",
  };

  return aliases[t] || t || "column";
}

function inferType(raw, map) {
  const directType = map.type || map.chart_type || map.graph_type || map.visual_type || "";
  if (directType) return normalizeType(directType);

  // Legacy prose sometimes uses `Chart: "Title" (donut). Data: ...`. In that
  // format `chart` is a title sentence, not a type token, so only accept these
  // alias fields when they contain a simple type-like value.
  for (const candidate of [map.chart, map.graph, map.visual, map.diagram]) {
    const token = String(candidate || "").trim();
    if (token && /^[a-zA-Z][a-zA-Z0-9 _-]{0,36}$/.test(token) && !/["“”():.]/.test(token)) {
      return normalizeType(token);
    }
  }

  const lower = String(raw || "").toLowerCase();

  const checks = [
    ["confidence_interval", /confidence interval|error bar|error-bar|uncertainty range/],
    ["confidence_band", /confidence band|probability band|forecast band|uncertainty band|line confidence/],
    ["roc_curve", /roc curve|receiver operating characteristic/],
    ["precision_recall", /precision[-\s]?recall|pr curve/],
    ["violin_plot", /violin plot|violin/],
    ["kde_density", /kde|kernel density|density curve/],
    ["bell_curve", /bell|normal curve|normal distribution|gaussian/],
    ["histogram", /histogram|distribution|frequency/],
    ["scatter", /scatter|regression trendline/],
    ["bubble", /bubble/],
    ["radar", /radar|spider/],
    ["boxplot", /box\s*plot|box-and-whisker|quartile/],
    ["heatmap", /heat\s*map|matrix/],
    ["waterfall", /waterfall|bridge/],
    ["funnel", /funnel/],
    ["gauge", /gauge|meter/],
    ["candlestick_basic", /candlestick|ohlc/],
    ["pareto", /pareto/],
    ["slope", /slope chart/],
    ["lollipop", /lollipop/],
    ["dotplot", /dot\s*plot/],
    ["donut", /donut|doughnut/],
    ["pie", /pie/],
    ["stacked_area", /stacked area/],
    ["area", /area/],
    ["line", /line|trend/],
    ["stacked_bar", /stacked bar|stacked column/],
    ["grouped_bar", /grouped bar|grouped column/],
    ["bar", /horizontal bar|bar/],
    ["column", /column/],
    ["circle", /circle/],
    ["ellipse", /ellipse|oval/],
    ["square", /square/],
    ["rectangle", /rectangle/],
    ["right_triangle", /right triangle/],
    ["triangle", /triangle/],
    ["angle", /angle/],
    ["parabola", /parabola|x\^2|x²/],
    ["sine_wave", /sine|sin\(/],
    ["cosine_wave", /cosine|cos\(/],
    ["number_line", /number line/],
    ["venn", /venn/],
    ["flowchart_basic", /flowchart|flow chart/],
    ["polygon", /polygon/],
    ["vector", /vector/],
    ["coordinate_graph", /coordinate|y\s*=/],
  ];

  for (const [type, re] of checks) {
    if (re.test(lower)) return type;
  }

  return "column";
}

function detectEquation(raw, map) {
  const eq = map.equation || map.eq || map.formula || "";
  if (eq) return eq.trim();
  const m = String(raw || "").match(/\by\s*=\s*([^\n;]+)/i);
  return m ? `y=${m[1].trim()}` : "";
}

function parseLabeledPairs(value) {
  const parts = splitStructuredSegments(value);
  const pairs = [];

  for (const part of parts) {
    const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(-?\$?\d+(?:\.\d+)?%?)\s*$/);
    if (m) {
      pairs.push({
        label: m[1].trim(),
        value: safeNumber(m[2], 0),
      });
    }
  }

  return pairs;
}

function parsePointList(value) {
  const parts = splitStructuredSegments(value);
  const points = [];

  for (const part of parts) {
    const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(.+)\s*$/);
    if (!m) continue;

    const label = m[1].trim();
    const nums = splitLooseList(m[2]).map((x) => safeNumber(x, NaN)).filter(Number.isFinite);

    if (nums.length >= 2) {
      points.push({
        name: label,
        x: nums[0],
        y: nums[1],
        z: nums[2] ?? 1,
        value: nums[2] ?? nums[1],
      });
    }
  }

  return points;
}

function parseXYLists(map) {
  const xs = parseNumberList(map.x_values || map.xs || map.x_numeric || "");
  const ys = parseNumberList(map.y_values || map.ys || map.y_numeric || "");

  if (xs.length && ys.length) {
    const len = Math.min(xs.length, ys.length);
    return Array.from({ length: len }, (_, i) => ({
      name: `Point ${i + 1}`,
      x: xs[i],
      y: ys[i],
      z: 1,
      value: ys[i],
    }));
  }

  return [];
}

function parseSeries(value) {
  const seriesParts = splitStructuredSegments(value);
  const series = [];

  for (const part of seriesParts) {
    const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(.+)\s*$/);
    if (!m) continue;
    series.push({
      name: m[1].trim(),
      values: parseNumberList(m[2]),
    });
  }

  return series;
}

function normalizeCategoryData(map) {
  const pairSource = map.data || map.dataset || map.points || "";
  const pairs = parseLabeledPairs(pairSource);

  if (pairs.length >= 1) {
    return pairs.map((p) => ({
      name: p.label,
      value: p.value,
    }));
  }

  const labels = splitLooseList(map.labels || map.label || map.categories || map.category || map.x || "");
  const values = parseNumberList(map.values || map.value || map.y || map.amounts || map.amount || "");

  if (labels.length && values.length) {
    const maxLen = Math.max(labels.length, values.length);
    return Array.from({ length: maxLen }, (_, i) => ({
      name: labels[i] || `Item ${i + 1}`,
      value: values[i] ?? 0,
    }));
  }

  if (values.length) {
    return values.map((v, i) => ({
      name: `Item ${i + 1}`,
      value: v,
    }));
  }

  return [];
}

function normalizeMultiSeriesData(map) {
  const labels = splitLooseList(map.labels || map.categories || map.x || map.periods || "");
  const parsedSeries = parseSeries(map.series || map.datasets || "");

  if (!parsedSeries.length) {
    const base = normalizeCategoryData(map);
    return {
      data: base,
      keys: base.length ? ["value"] : [],
      labels: base.map((d) => d.name),
    };
  }

  const maxLen = Math.max(
    labels.length,
    ...parsedSeries.map((s) => s.values.length),
    0
  );

  const data = Array.from({ length: maxLen }, (_, i) => {
    const row = {
      name: labels[i] || `Item ${i + 1}`,
    };

    for (const s of parsedSeries) {
      row[s.name] = s.values[i] ?? 0;
    }

    return row;
  });

  return {
    data,
    keys: parsedSeries.map((s) => s.name),
    labels: data.map((d) => d.name),
  };
}

function buildHistogramData(values, binCount = 10) {
  const nums = values.map(Number).filter(Number.isFinite);
  if (!nums.length) return [];

  const min = Math.min(...nums);
  const max = Math.max(...nums);

  if (min === max) {
    return [{ name: String(min), range: String(min), count: nums.length, value: nums.length }];
  }

  const bins = clamp(Math.round(binCount || 10), 4, 30);
  const size = (max - min) / bins;

  const result = Array.from({ length: bins }, (_, i) => {
    const start = min + i * size;
    const end = min + (i + 1) * size;
    return {
      name: `${formatValue(start)}-${formatValue(end)}`,
      range: `${formatValue(start)}-${formatValue(end)}`,
      count: 0,
      value: 0,
      start,
      end,
    };
  });

  nums.forEach((n) => {
    const idx = Math.min(Math.floor((n - min) / size), bins - 1);
    result[idx].count += 1;
    result[idx].value = result[idx].count;
  });

  return result;
}

function normalPdf(x, mean, sd) {
  if (!sd || sd <= 0) return 0;
  return (1 / (sd * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mean) / sd) ** 2);
}

function buildBellCurveData(map) {
  const mean = safeNumber(map.mean || map.mu || map.average || 0, 0);
  const sd = Math.max(0.0001, safeNumber(map.sd || map.std || map.standard_deviation || map.sigma || 1, 1));
  const points = clamp(safeNumber(map.points_count || map.count || map.samples || 121, 121), 41, 301);
  const min = safeNumber(map.min || mean - 4 * sd, mean - 4 * sd);
  const max = safeNumber(map.max || mean + 4 * sd, mean + 4 * sd);
  const step = (max - min) / (points - 1 || 1);

  const raw = Array.from({ length: points }, (_, i) => {
    const x = min + i * step;
    const density = normalPdf(x, mean, sd);
    return { name: formatValue(x), x, density };
  });

  const maxDensity = Math.max(...raw.map((d) => d.density)) || 1;

  return raw.map((d) => ({
    ...d,
    y: (d.density / maxDensity) * 100,
    value: d.density,
  }));
}

function percentile(sorted, p) {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * p;
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower];
  const weight = idx - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

function buildBoxStats(values) {
  const sorted = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) {
    return { min: 0, q1: 0, median: 0, q3: 0, max: 0, iqr: 0 };
  }

  const q1 = percentile(sorted, 0.25);
  const median = percentile(sorted, 0.5);
  const q3 = percentile(sorted, 0.75);
  const iqr = q3 - q1;

  return {
    min: sorted[0],
    q1,
    median,
    q3,
    max: sorted[sorted.length - 1],
    iqr,
  };
}

function buildWaterfallData(baseData) {
  let running = 0;
  return baseData.map((d, i) => {
    const value = safeNumber(d.value, 0);
    const name = String(d.name || "");
    const isLast = i === baseData.length - 1;
    const isTotal = /total|ending|final|net\s+(?:income|profit|operating)|ending\s+balance/i.test(name) ||
      (isLast && i > 0 && value >= 0 && value <= Math.max(...baseData.slice(0, -1).map((item) => Math.abs(safeNumber(item.value, 0))), 0));

    if (isTotal) {
      const start = 0;
      const end = value;
      running = value;
      return {
        ...d,
        start,
        end,
        delta: value,
        value: Math.abs(value),
        fill: "#16a34a",
        isTotal: true,
      };
    }

    const start = running;
    running += value;
    return {
      ...d,
      start,
      end: running,
      delta: value,
      value: Math.abs(value),
      fill: value >= 0 ? "#16a34a" : "#dc2626",
      isTotal: false,
    };
  });
}

function buildParetoData(baseData) {
  const sorted = [...baseData].sort((a, b) => safeNumber(b.value, 0) - safeNumber(a.value, 0));
  const total = sorted.reduce((sum, d) => sum + Math.max(0, safeNumber(d.value, 0)), 0) || 1;
  let cumulative = 0;

  return sorted.map((d) => {
    cumulative += Math.max(0, safeNumber(d.value, 0));
    return {
      ...d,
      cumulative_percent: (cumulative / total) * 100,
    };
  });
}


function linearRegression(points) {
  const clean = (points || [])
    .map((p) => ({ x: Number(p.x), y: Number(p.y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

  if (clean.length < 2) return null;

  const n = clean.length;
  const sumX = clean.reduce((s, p) => s + p.x, 0);
  const sumY = clean.reduce((s, p) => s + p.y, 0);
  const sumXY = clean.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = clean.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-12) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const meanY = sumY / n;
  const ssTot = clean.reduce((s, p) => s + (p.y - meanY) ** 2, 0);
  const ssRes = clean.reduce((s, p) => s + (p.y - (slope * p.x + intercept)) ** 2, 0);
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 1;
  const minX = Math.min(...clean.map((p) => p.x));
  const maxX = Math.max(...clean.map((p) => p.x));

  return {
    slope,
    intercept,
    r2,
    line: [
      { name: "Regression start", x: minX, y: slope * minX + intercept, value: slope * minX + intercept },
      { name: "Regression end", x: maxX, y: slope * maxX + intercept, value: slope * maxX + intercept },
    ],
  };
}

function parseRangeData(map) {
  const labels = splitLooseList(map.labels || map.categories || map.x || map.periods || "");
  const meanVals = parseNumberList(map.mean || map.expected || map.base || map.mid || map.values || map.value || map.y || "");
  const lowVals = parseNumberList(map.low || map.lower || map.min || map.lower_bound || map.ci_low || map.p10 || "");
  const highVals = parseNumberList(map.high || map.upper || map.max || map.upper_bound || map.ci_high || map.p90 || "");

  const source = map.data || map.points || "";
  const pipeRows = splitPipeList(source);
  if (pipeRows.length && (!meanVals.length || !lowVals.length || !highVals.length)) {
    const parsed = pipeRows.map((part, i) => {
      const m = part.match(/^\s*([^:=]+?)\s*[:=]\s*(.+)\s*$/);
      if (!m) return null;
      const nums = parseNumberList(m[2]);
      if (nums.length < 3) return null;
      return {
        name: m[1].trim() || `Item ${i + 1}`,
        low: nums[0],
        mean: nums[1],
        high: nums[2],
        range: Math.max(0, nums[2] - nums[0]),
      };
    }).filter(Boolean);
    if (parsed.length) return parsed;
  }

  const len = Math.max(labels.length, meanVals.length, lowVals.length, highVals.length);
  return Array.from({ length: len }, (_, i) => {
    const low = lowVals[i] ?? meanVals[i] ?? 0;
    const high = highVals[i] ?? meanVals[i] ?? low;
    const mean = meanVals[i] ?? (low + high) / 2;
    return {
      name: labels[i] || `Item ${i + 1}`,
      low,
      mean,
      high,
      range: Math.max(0, high - low),
    };
  }).filter((d) => Number.isFinite(d.low) && Number.isFinite(d.mean) && Number.isFinite(d.high));
}

function parseBandData(map) {
  const data = parseRangeData(map);
  return data.map((d) => ({
    ...d,
    baseline: d.low,
    band: Math.max(0, d.high - d.low),
    expected: d.mean,
  }));
}

function standardDeviation(values) {
  const nums = values.map(Number).filter(Number.isFinite);
  if (nums.length < 2) return 0;
  const mean = nums.reduce((s, v) => s + v, 0) / nums.length;
  const variance = nums.reduce((s, v) => s + (v - mean) ** 2, 0) / (nums.length - 1);
  return Math.sqrt(variance);
}

function buildKdeData(values, requestedBandwidth, pointCount = 121) {
  const nums = values.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!nums.length) return [];
  if (nums.length === 1) {
    return [
      { x: nums[0] - 1, y: 0, density: 0, name: formatValue(nums[0] - 1) },
      { x: nums[0], y: 100, density: 1, name: formatValue(nums[0]) },
      { x: nums[0] + 1, y: 0, density: 0, name: formatValue(nums[0] + 1) },
    ];
  }

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const sd = standardDeviation(nums) || (max - min) / 6 || 1;
  const n = nums.length;
  const silverman = 1.06 * sd * Math.pow(n, -1 / 5);
  const bandwidth = Math.max(0.0001, safeNumber(requestedBandwidth, silverman || 1));
  const pad = bandwidth * 3;
  const start = min - pad;
  const end = max + pad;
  const count = clamp(pointCount, 41, 241);
  const step = (end - start) / (count - 1 || 1);

  const raw = Array.from({ length: count }, (_, i) => {
    const x = start + i * step;
    const density = nums.reduce((sum, v) => sum + normalPdf(x, v, bandwidth), 0) / n;
    return { x, density, name: formatValue(x) };
  });
  const maxDensity = Math.max(...raw.map((d) => d.density)) || 1;
  return raw.map((d) => ({ ...d, y: (d.density / maxDensity) * 100, value: d.density }));
}

function parseCurvePoints(map) {
  let data = parsePointList(map.points || map.data || "");
  if (!data.length) data = parseXYLists(map);
  return data
    .map((p, i) => ({ name: p.name || `Point ${i + 1}`, x: Number(p.x), y: Number(p.y), value: Number(p.y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);
}

function buildViolinShape(values, width, height, left, right, top, bottom, maxHalfWidth) {
  const kde = buildKdeData(values, null, 121);
  const nums = values.map(Number).filter(Number.isFinite);
  if (!kde.length || !nums.length) return { path: "", stats: buildBoxStats(nums) };

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const stats = buildBoxStats(nums);
  const plotH = height - top - bottom;
  const centerX = (width + left - right) / 2;

  /*
    Do NOT crop the KDE tails. Cropping makes the violin look visibly cut off.
    Instead, include the full KDE domain and scale it into a padded viewBox.
    Actual min/max/median markers still use the real sample values.
  */
  const kdeMin = Math.min(...kde.map((d) => d.x), min);
  const kdeMax = Math.max(...kde.map((d) => d.x), max);
  const domainPad = Math.max((kdeMax - kdeMin || 1) * 0.035, 0.001);
  const domainMin = kdeMin - domainPad;
  const domainMax = kdeMax + domainPad;
  const maxY = Math.max(...kde.map((d) => d.y), 1);
  const safeHalfWidth = Math.min(maxHalfWidth, Math.max(48, (width - left - right) * 0.34));

  const scaleY = (v) => top + plotH - ((v - domainMin) / (domainMax - domainMin || 1)) * plotH;
  const half = (densityScore) => (densityScore / maxY) * safeHalfWidth;

  const rightSide = kde.map((d) => `${centerX + half(d.y)},${scaleY(d.x)}`);
  const leftSide = [...kde].reverse().map((d) => `${centerX - half(d.y)},${scaleY(d.x)}`);
  return {
    path: `M ${rightSide.join(" L ")} L ${leftSide.join(" L ")} Z`,
    stats,
    scaleY,
    centerX,
    domainMin,
    domainMax,
  };
}

function parseHeatmapData(map) {
  // rows=A,B; cols=X,Y; values=10,20|30,40
  const rows = splitLooseList(map.rows || map.y_labels || map.y || "");
  const cols = splitLooseList(map.cols || map.columns || map.x_labels || map.x || "");
  const rowValues = splitPipeList(map.values || map.data || "");

  const matrix = [];

  for (let r = 0; r < rowValues.length; r += 1) {
    const nums = parseNumberList(rowValues[r]);
    matrix.push(nums);
  }

  if (!rows.length && matrix.length) {
    for (let i = 0; i < matrix.length; i += 1) rows.push(`Row ${i + 1}`);
  }

  if (!cols.length && matrix[0]?.length) {
    for (let i = 0; i < matrix[0].length; i += 1) cols.push(`Col ${i + 1}`);
  }

  return { rows, cols, matrix };
}

function parseTimelineData(map) {
  // events=Filed Complaint:Jan 1|Discovery:Feb 15
  const source = map.events || map.timeline || map.data || "";
  return splitPipeList(source).map((part, i) => {
    const [label, date] = part.split(":");
    return {
      name: (label || `Event ${i + 1}`).trim(),
      date: (date || "").trim(),
    };
  });
}

function parseGanttData(map) {
  // tasks=Task A:0,5|Task B:4,10
  const source = map.tasks || map.data || "";
  return splitPipeList(source).map((part, i) => {
    const [label, numsRaw] = part.split(":");
    const nums = parseNumberList(numsRaw);
    const start = nums[0] ?? i * 2;
    const end = nums[1] ?? start + 1;
    return {
      name: (label || `Task ${i + 1}`).trim(),
      start,
      end,
      duration: Math.max(0, end - start),
    };
  });
}

function parseCandlestickData(map) {
  // candles=Day1:100,110,90,105|Day2:105,120,101,115  open,high,low,close
  const source = map.candles || map.ohlc || map.data || "";
  return splitPipeList(source).map((part, i) => {
    const [label, numsRaw] = part.split(":");
    const nums = parseNumberList(numsRaw);
    return {
      name: (label || `T${i + 1}`).trim(),
      open: nums[0] ?? 0,
      high: nums[1] ?? 0,
      low: nums[2] ?? 0,
      close: nums[3] ?? 0,
    };
  });
}


function estimateCountFromMap(map, keys = []) {
  for (const key of keys) {
    const value = map?.[key];
    if (!value) continue;
    if (String(value).includes("|")) return splitPipeList(value).length;
    const list = splitLooseList(value);
    if (list.length) return list.length;
  }
  return 0;
}

function getRecommendedVisualHeight(type, map = {}) {
  const t = normalizeType(type);
  const explicit = toFiniteNumber(map.height);
  if (explicit !== null) return explicit;

  const labelsCount = estimateCountFromMap(map, ["labels", "categories", "x", "periods"]);
  const rowCount = estimateCountFromMap(map, ["rows", "y_labels", "tasks", "events", "timeline", "data", "points"]);

  switch (t) {
    case "bell_curve":
    case "kde_density":
      return 430;
    case "confidence_band":
    case "scatter":
    case "bubble":
    case "roc_curve":
    case "precision_recall":
      return 420;
    case "violin_plot":
      return 560;
    case "radar":
      return 430;
    case "confidence_interval":
      return clamp(130 + Math.max(labelsCount, rowCount, 3) * 48, 320, 620);
    case "heatmap": {
      const rows = estimateCountFromMap(map, ["rows", "y_labels", "y"]);
      const cols = estimateCountFromMap(map, ["cols", "columns", "x_labels", "x"]);
      return clamp(150 + Math.max(rows || 3, 3) * 46 + Math.max(cols || 3, 3) * 4, 330, 680);
    }
    case "timeline":
      return clamp(130 + Math.max(rowCount, 3) * 52, 300, 720);
    case "gantt_basic":
      return clamp(130 + Math.max(rowCount, 3) * 48, 320, 720);
    case "histogram":
    case "boxplot":
    case "waterfall":
    case "pareto":
      return 410;
    case "pie":
    case "donut":
    case "gauge":
      return 390;
    default:
      return 390;
  }
}

function parseVisualInstruction(value) {
  const raw = normalizeDocWriterChartSource(value);
  const map = docWriterChartMapFromValue(value);
  const type = inferType(raw, map);
  const title = String(map.title || map.name || "").trim();
  const subtitle = String(map.subtitle || map.note || "").trim();
  const colors = parseColorList(map.colors || map.color_palette || map.palette || map.color || "");
  const equation = detectEquation(raw, map);
  const xLabel = map.x_label || map.xlabel || map.x_axis || "";
  const yLabel = map.y_label || map.ylabel || map.y_axis || "";
  const zLabel = map.z_label || map.zlabel || map.size_label || "";
  const height = sanitizeDocWriterDimension(
    map.doc_height ?? map.canvas_height,
    clamp(getRecommendedVisualHeight(type, map), DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT),
    DOC_WRITER_MIN_HEIGHT,
    DOC_WRITER_MAX_HEIGHT
  );
  const width = sanitizeDocWriterDimension(
    map.doc_width ?? map.canvas_width,
    getRecommendedDocWriterWidth(type),
    DOC_WRITER_MIN_WIDTH,
    1600
  );

  return {
    raw,
    map,
    type,
    title,
    subtitle,
    colors,
    equation,
    xLabel,
    yLabel,
    zLabel,
    color: colors[0] || normalizeColor(map.color, DEFAULT_PALETTE[0]),
    height,
    width,
    alignment: normalizeDocWriterAlignment(map.doc_alignment || map.alignment),
    legendPosition: normalizeLegendPosition(map.legend_position || map.legend),
    legendAlign: normalizeTextAlignment(map.legend_align, "center"),
    legendColor: cssColor(map.legend_color, TEXT_MUTED),
    background: normalizeColor(map.background, CARD_BG) || CARD_BG,
  };
}

/* =========================
   Layout / UI helpers
   ========================= */

function ChartShell({
  visual,
  children,
  height,
  background = CARD_BG,
  supplementalEditable = false,
  onSupplementalFocus = null,
  onSupplementalCommit = null,
  canvasTextAlignmentHandoff = false,
}) {
  const chartHeight = clamp(safeNumber(height, 390), DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT);
  const map = visual?.map || {};
  const title = String(visual?.title || "").trim();
  const subtitle = String(visual?.subtitle || "").trim();
  const typeLabel = DOC_WRITER_CHART_LABELS[visual?.type] || titleCase(visual?.type || "Chart");
  const topTitles = parseChartSupplementalLines(map.top_titles, "title");
  const bottomNotes = parseChartSupplementalLines(map.bottom_notes, "note");

  const handoffCanvasTextAlignment = (node, requestedAlignment) => {
    if (!node || !canvasTextAlignmentHandoff) return;
    const nextAlignment = normalizeTextAlignment(requestedAlignment, "center");
    const marker = "data-docwriter-chart-canvas-alignment-handoff";
    if (node.getAttribute(marker) === nextAlignment) return;
    node.style.setProperty("text-align", nextAlignment, "important");
    node.setAttribute(marker, nextAlignment);
  };

  const supplementalLine = (line, supplementalKind) => {
    const isTitle = supplementalKind === "title";
    return (
      <div
        key={`${supplementalKind}-${line.id}`}
        ref={(node) => handoffCanvasTextAlignment(node, line.alignment)}
        data-docwriter-chart-supplemental-kind={supplementalKind}
        data-docwriter-chart-supplemental-id={line.id}
        contentEditable={supplementalEditable || undefined}
        suppressContentEditableWarning
        spellCheck={supplementalEditable}
        onFocus={() => {
          if (supplementalEditable) {
            try { onSupplementalFocus?.(supplementalKind, line.id); } catch (_) {}
          }
        }}
        onPointerDown={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
        }}
        onClick={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
          if (event.key === "Enter") event.preventDefault();
        }}
        onBlur={(event) => {
          if (!supplementalEditable) return;
          try {
            onSupplementalCommit?.(
              supplementalKind,
              line.id,
              String(event.currentTarget.textContent || "")
            );
          } catch (_) {}
        }}
        style={{
          minHeight: 18,
          width: "100%",
          boxSizing: "border-box",
          textAlign: line.alignment,
          fontSize: `${line.fontSizePx}px`,
          fontWeight: line.bold ? 700 : 400,
          fontStyle: line.italic ? "italic" : "normal",
          textDecoration: line.underline ? "underline" : "none",
          lineHeight: 1.35,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          color: TEXT_DARK,
          margin: isTitle ? "0 0 4px" : "4px 0 0",
          padding: "1px 4px",
          outline: "none",
          cursor: supplementalEditable ? "text" : "default",
          userSelect: supplementalEditable ? "text" : "auto",
        }}
      >
        {line.text}
      </div>
    );
  };

  const rows = [
    {
      key: "title",
      text: title,
      visible: Boolean(title) && boolSetting(map.show_title, true),
      order: settingOrder(map.title_order, 1),
      align: normalizeTextAlignment(map.title_align, "center"),
      color: cssColor(map.title_color, TEXT_DARK),
      fontSize: clamp(safeNumber(map.title_font_size, 14), 10, 24),
      fontWeight: 850,
    },
    {
      key: "subtitle",
      text: subtitle,
      visible: Boolean(subtitle) && boolSetting(map.show_subtitle, true),
      order: settingOrder(map.subtitle_order, 2),
      align: normalizeTextAlignment(map.subtitle_align, "center"),
      color: cssColor(map.subtitle_color, TEXT_MUTED),
      fontSize: clamp(safeNumber(map.subtitle_font_size, 12), 9, 20),
      fontWeight: 600,
    },
    {
      key: "chart-type",
      text: typeLabel,
      visible: boolSetting(map.show_chart_type, false),
      order: settingOrder(map.chart_type_order, 3),
      align: normalizeTextAlignment(map.chart_type_align, "center"),
      color: cssColor(map.chart_type_color, TEXT_MUTED),
      fontSize: clamp(safeNumber(map.chart_type_font_size, 11), 9, 18),
      fontWeight: 750,
    },
  ]
    .filter((row) => row.visible && row.text)
    .sort((a, b) => a.order - b.order || ["title", "subtitle", "chart-type"].indexOf(a.key) - ["title", "subtitle", "chart-type"].indexOf(b.key));

  return (
    <div
      className="doc-writer-chart-surface"
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        background,
        breakInside: "avoid",
        pageBreakInside: "avoid",
      }}
    >
      {topTitles.map((line) => supplementalLine(line, "title"))}
      {rows.length ? (
        <div style={{ display: "grid", gap: 4, padding: "0 4px 8px" }}>
          {rows.map((row) => (
            <div
              key={row.key}
              ref={(node) => {
                if (row.key === "title" || row.key === "subtitle") {
                  handoffCanvasTextAlignment(node, row.align);
                }
              }}
              className={`doc-writer-chart-${row.key}`}
              style={{
                color: row.color,
                fontSize: row.fontSize,
                fontWeight: row.fontWeight,
                lineHeight: 1.3,
                textAlign: row.align,
                overflowWrap: "anywhere",
              }}
            >
              {row.text}
            </div>
          ))}
        </div>
      ) : null}

      <div
        className="doc-writer-chart-preview"
        role="region"
        aria-label={`${String(title || typeLabel || "Chart")} visual`}
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          height: chartHeight,
          minHeight: chartHeight,
          overflow: "hidden",
          position: "relative",
          background,
        }}
      >
        <div
          className="doc-writer-chart-canvas"
          style={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
            background,
          }}
        >
          {children}
        </div>
      </div>
      <ChartDataDisplay visual={visual} />
      {bottomNotes.map((line) => supplementalLine(line, "note"))}
    </div>
  );
}

function printableChartSnapshotHtml(surface) {
  if (!surface?.cloneNode) return "";
  try {
    const clone = surface.cloneNode(true);
    clone
      .querySelectorAll?.(`[${DOC_WRITER_EDITOR_ONLY_ATTR}="true"]`)
      .forEach((node) => node.remove());
    clone.querySelectorAll?.("[contenteditable]").forEach((node) => {
      node.removeAttribute("contenteditable");
      node.removeAttribute("spellcheck");
    });
    return String(clone.innerHTML || "");
  } catch (_) {
    return String(surface.innerHTML || "");
  }
}

function EmptyChart({ message = "Chart data was not provided in a renderable format." }) {
  return (
    <div
      style={{
        minHeight: 180,
        borderRadius: 14,
        border: `1px dashed ${GRID}`,
        background: SOFT_BG,
        display: "grid",
        placeItems: "center",
        color: TEXT_MUTED,
        fontSize: 14,
        padding: 16,
        textAlign: "center",
      }}
    >
      {message}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const cleanPayload = payload
    .filter((item) => item && item.value !== undefined && item.value !== null)
    .filter((item) => item.type !== "none")
    .slice(0, 12);

  if (!cleanPayload.length) return null;

  const firstRaw = cleanPayload[0]?.payload || {};
  const coordinateParts = [];
  if (Number.isFinite(Number(firstRaw.x))) coordinateParts.push(`x: ${formatValue(firstRaw.x)}`);
  if (Number.isFinite(Number(firstRaw.y))) coordinateParts.push(`y: ${formatValue(firstRaw.y)}`);

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(15,23,42,0.14)",
        borderRadius: 12,
        padding: "9px 11px",
        boxShadow: "0 14px 30px rgba(15,23,42,0.14)",
        color: TEXT_DARK,
        fontSize: 12,
        maxWidth: 285,
        minWidth: 150,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        pointerEvents: "none",
      }}
    >
      {label !== undefined && label !== null ? (
        <div style={{ fontWeight: 850, marginBottom: 4 }}>{String(label)}</div>
      ) : null}
      {!label && coordinateParts.length ? (
        <div style={{ fontWeight: 850, marginBottom: 4 }}>{coordinateParts.join(" · ")}</div>
      ) : null}
      {cleanPayload.map((item, i) => {
        const raw = item.payload || {};
        const name = item.name || item.dataKey || raw.name || `Series ${i + 1}`;
        const value = item.value;
        const color =
          raw.__tooltipColor ||
          raw.fill ||
          item.color ||
          item.stroke ||
          item.fill ||
          DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
        const extra = [];
        if (Number.isFinite(Number(raw.low)) && Number.isFinite(Number(raw.high))) {
          extra.push(`range ${formatValue(raw.low)}–${formatValue(raw.high)}`);
        }
        if (Number.isFinite(Number(raw.density))) {
          extra.push(`density ${formatValue(raw.density)}`);
        }
        if (Number.isFinite(Number(raw.x)) && Number.isFinite(Number(raw.y)) && !coordinateParts.length) {
          extra.push(`x ${formatValue(raw.x)}, y ${formatValue(raw.y)}`);
        }
        return (
          <div key={`${name}-${i}`} style={{ display: "grid", gridTemplateColumns: "10px 1fr auto", gap: 7, alignItems: "start", marginTop: i ? 4 : 0 }}>
            <span
              style={{
                width: 9,
                height: 9,
                marginTop: 4,
                borderRadius: 999,
                background: color,
                display: "inline-block",
              }}
            />
            <span style={{ color: TEXT_MUTED, minWidth: 0 }}>{name}</span>
            <strong style={{ color: TEXT_DARK }}>{formatValue(value)}</strong>
            {extra.length ? <span /> : null}
            {extra.length ? <span style={{ gridColumn: "2 / 4", color: TEXT_MUTED, fontSize: 11 }}>{extra.join(" · ")}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

const SAFE_TOOLTIP_PROPS = {
  allowEscapeViewBox: { x: true, y: true },
  wrapperStyle: { zIndex: 1000, pointerEvents: "none" },
  offset: 12,
};


function RechartsFrame({ children, height = 390, background = CARD_BG }) {
  const chartHeight = clamp(safeNumber(height, 390), DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT);
  const frameRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  useEffect(() => {
    const node = frameRef.current;
    if (!node || typeof window === "undefined") return undefined;

    let raf = 0;
    let retryCount = 0;

    const measure = () => {
      if (!frameRef.current) return;
      const rect = frameRef.current.getBoundingClientRect?.();
      const nextWidth = Math.max(
        0,
        Math.round(rect?.width || frameRef.current.clientWidth || 0)
      );
      if (nextWidth > 0) {
        setMeasuredWidth((current) => (current === nextWidth ? current : nextWidth));
        retryCount = 0;
        return;
      }

      // A chart can be mounted while the Editor is rebuilding/paginating a page.
      // Retry a few frames instead of letting Recharts permanently initialize at 0px.
      if (retryCount < 12) {
        retryCount += 1;
        raf = window.requestAnimationFrame(measure);
      }
    };

    measure();

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        if (raf) window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(measure);
      });
      observer.observe(node);
    }

    window.addEventListener("resize", measure);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      observer?.disconnect?.();
      window.removeEventListener("resize", measure);
    };
  }, []);

  if (!React.isValidElement(children)) {
    return <EmptyChart message="Chart could not be rendered." />;
  }

  const safeWidth = Math.max(1, measuredWidth || 1);
  const renderedChart = React.cloneElement(children, {
    width: safeWidth,
    height: chartHeight,
  });

  return (
    <div
      ref={frameRef}
      className="doc-writer-chart-recharts-frame"
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: chartHeight,
        minHeight: chartHeight,
        overflow: "hidden",
        position: "relative",
        background,
      }}
    >
      {measuredWidth > 0 ? renderedChart : (
        <div
          aria-hidden="true"
          style={{ width: "100%", height: chartHeight, background }}
        />
      )}
    </div>
  );
}

function getDataKeys(data, fallback = ["value"]) {
  if (!data?.length) return fallback;
  const keys = Object.keys(data[0]).filter((k) => !["name", "label", "range", "fill", "start", "end", "x", "y", "z"].includes(k));
  return keys.length ? keys : fallback;
}


function useDraggableLegendOffset(visual) {
  const persisted = parseLegendOffset(visual?.map?.legend_offset || "");
  const [localOffset, setLocalOffset] = useState(persisted);
  const dragRef = useRef(null);
  const latestRef = useRef(persisted);

  useEffect(() => {
    const next = parseLegendOffset(visual?.map?.legend_offset || "");
    latestRef.current = next;
    setLocalOffset(next);
  }, [visual?.map?.legend_offset]);

  const canDrag = Boolean(visual?.canEditLegend && typeof visual?.onCommitLegendOffset === "function");
  const beginDrag = (event) => {
    if (!canDrag) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      start: { ...localOffset },
    };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}

    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const next = {
        dx: Math.round(state.start.dx + moveEvent.clientX - state.startX),
        dy: Math.round(state.start.dy + moveEvent.clientY - state.startY),
      };
      latestRef.current = next;
      setLocalOffset(next);
    };
    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = dragRef.current;
      dragRef.current = null;
      if (!state) return;
      visual.onCommitLegendOffset?.(latestRef.current);
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  return { localOffset, canDrag, beginDrag };
}

function CategoryValueLegend({ data, colors, valueKey = "value", title = "", visual }) {
  const rows = (data || [])
    .map((row, index) => ({
      name: row?.name || row?.label || `Item ${index + 1}`,
      value: safeNumber(row?.[valueKey], 0),
      color: colors?.[index % Math.max(colors.length, 1)] || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length],
    }))
    .filter((row) => row.name !== undefined && row.name !== null);

  if (!rows.length) return null;
  const align = legendAlignment(visual);
  const color = legendTextColor(visual);
  const fontSize = clamp(safeNumber(visual?.map?.legend_font_size, 12), 7, 24);
  const { localOffset, canDrag, beginDrag } = useDraggableLegendOffset(visual);

  return (
    <div
      className="doc-writer-chart-category-legend"
      onPointerDown={beginDrag}
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
        alignItems: "center",
        gap: "8px 14px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        padding: "6px 10px 0",
        marginTop: 2,
        color,
        fontSize,
        lineHeight: 1.25,
        transform: `translate(${localOffset.dx}px, ${localOffset.dy}px)`,
        cursor: canDrag ? "move" : "default",
        userSelect: "none",
        pointerEvents: "auto",
        touchAction: "none",
      }}
      title={canDrag ? "Drag legend to reposition" : undefined}
      aria-label={title || "Chart legend"}
    >
      {rows.map((row, index) => (
        <div
          key={`category-legend-${index}-${row.name}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
            whiteSpace: "nowrap",
          }}
          title={`${row.name}: ${formatValue(row.value)}`}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: row.color,
              flex: "0 0 auto",
              boxShadow: "0 0 0 1px rgba(15,23,42,0.08)",
            }}
          />
          <span style={{ color, fontWeight: 650 }}>{row.name}</span>
          {normalizeLegendContent(visual?.map?.legend_content) === "name_value" ? (
            <strong style={{ color, fontWeight: 850 }}>{formatValue(row.value)}</strong>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function renderCategoryLegendContent(data, colors, valueKey, title, visual) {
  return function CategoryLegendContent() {
    return <CategoryValueLegend data={data} colors={colors} valueKey={valueKey} title={title} visual={visual} />;
  };
}

function shouldShowLegend(visual) {
  return normalizeLegendPosition(visual?.map?.legend_position || visual?.legendPosition) !== "none";
}

function DraggableStandardLegendContent({ payload = [], visual }) {
  const align = legendAlignment(visual);
  const color = legendTextColor(visual);
  const fontSize = clamp(safeNumber(visual?.map?.legend_font_size, 12), 7, 24);
  const { localOffset, canDrag, beginDrag } = useDraggableLegendOffset(visual);

  return (
    <div
      onPointerDown={beginDrag}
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
        alignItems: "center",
        gap: "7px 14px",
        width: "100%",
        color,
        fontSize,
        transform: `translate(${localOffset.dx}px, ${localOffset.dy}px)`,
        cursor: canDrag ? "move" : "default",
        userSelect: "none",
        pointerEvents: "auto",
        touchAction: "none",
      }}
      title={canDrag ? "Drag legend to reposition" : undefined}
    >
      {(payload || []).map((entry, index) => (
        <span key={`${entry?.dataKey || entry?.value || "legend"}-${index}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: entry?.color || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length], flex: "0 0 auto" }} />
          <span style={{ color, fontWeight: 650 }}>{String(entry?.value || entry?.dataKey || `Series ${index + 1}`)}</span>
          {normalizeLegendContent(visual?.map?.legend_content) === "name_value" ? (
            <strong style={{ color, fontWeight: 850 }}>
              {(() => {
                const key = String(entry?.dataKey || entry?.value || "");
                const multi = normalizeMultiSeriesData(visual?.map || {});
                const total = multi.data?.reduce((sum, row) => sum + safeNumber(row?.[key], 0), 0);
                return Number.isFinite(total) ? formatValue(total) : "";
              })()}
            </strong>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function StandardLegend({ visual, plotLeft = 46, plotRight = 36 }) {
  if (!shouldShowLegend(visual)) return null;
  const position = normalizeLegendPosition(visual?.map?.legend_position || visual?.legendPosition);
  return (
    <Legend
      verticalAlign={position === "top" ? "top" : "bottom"}
      align="center"
      layout="horizontal"
      content={(props) => <DraggableStandardLegendContent {...props} visual={visual} />}
      wrapperStyle={{
        width: `calc(100% - ${Math.max(0, plotLeft + plotRight)}px)`,
        left: Math.max(0, plotLeft),
        pointerEvents: "auto",
        paddingTop: position === "bottom" ? 8 : 0,
        paddingBottom: position === "top" ? 8 : 0,
      }}
    />
  );
}

function DraggableCategoryTick({
  x = 0,
  y = 0,
  payload,
  index = 0,
  visual,
  orientation = "x",
  dataCount = 1,
}) {
  if (!shouldShowCategoryLabels(visual)) return null;
  const hidden = parseHiddenCategoryIndices(visual?.map?.hidden_category_indices || "");
  if (hidden.has(Number(index))) return null;

  const persisted = parseCategoryOffsets(visual?.map?.category_label_offsets || "")[index] || { dx: 0, dy: 0 };
  const [localOffset, setLocalOffset] = useState(persisted);
  const latestOffsetRef = useRef(persisted);
  const dragRef = useRef(null);

  useEffect(() => {
    const next = parseCategoryOffsets(visual?.map?.category_label_offsets || "")[index] || { dx: 0, dy: 0 };
    latestOffsetRef.current = next;
    setLocalOffset(next);
  }, [visual?.map?.category_label_offsets, index]);

  const canDrag = Boolean(visual?.canEditCategoryLabels && typeof visual?.onCommitCategoryOffset === "function");
  const fontSize = categoryLabelFontSize(visual);
  const color = categoryLabelColor(visual);
  const angle = orientation === "x" ? categoryLabelAngle(visual) : 0;
  const baseX = x;
  const baseY = y + (orientation === "x" ? Math.max(13, fontSize * 0.9) : 4);
  const labelX = baseX + safeNumber(localOffset.dx, 0);
  const labelY = baseY + safeNumber(localOffset.dy, 0);
  const moved = Math.abs(labelX - baseX) > 2 || Math.abs(labelY - baseY) > 2;
  const pointerVisible = boolSetting(visual?.map?.category_label_pointer, false);
  const markerScope = String(visual?.chartId || "chart").replace(/[^a-zA-Z0-9_-]+/g, "-");
  const markerId = `doc-writer-category-arrow-${markerScope}-${orientation}-${index}`;
  const rawLabel = String(payload?.value ?? "");
  const maxChars = dataCount > 12 ? 8 : dataCount > 8 ? 10 : 14;
  const lines = wrapCategoryLabel(rawLabel, maxChars);

  const beginDrag = (event) => {
    if (!canDrag) return;
    event.preventDefault();
    event.stopPropagation();
    const start = { ...localOffset };
    dragRef.current = { startX: event.clientX, startY: event.clientY, start };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}
    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const next = {
        dx: Math.round(state.start.dx + moveEvent.clientX - state.startX),
        dy: Math.round(state.start.dy + moveEvent.clientY - state.startY),
      };
      latestOffsetRef.current = next;
      setLocalOffset(next);
    };
    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = dragRef.current;
      dragRef.current = null;
      if (!state) return;
      visual.onCommitCategoryOffset?.(index, latestOffsetRef.current);
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  const anchor = orientation === "y" ? "end" : "middle";
  const rotate = orientation === "x" && angle ? `rotate(${angle} ${labelX} ${labelY})` : undefined;
  return (
    <g onPointerDown={beginDrag} style={{ cursor: canDrag ? "move" : "default", userSelect: "none", touchAction: "none" }}>
      {pointerVisible && moved ? (
        <>
          <defs>
            <marker id={markerId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L6,3 L0,6 z" fill={color} />
            </marker>
          </defs>
          <line x1={labelX} y1={labelY} x2={baseX} y2={baseY - 3} stroke={color} strokeWidth="1" opacity="0.7" markerEnd={`url(#${markerId})`} />
        </>
      ) : null}
      <text
        x={labelX}
        y={labelY}
        textAnchor={anchor}
        dominantBaseline="hanging"
        fill={color}
        fontSize={fontSize}
        fontWeight="650"
        transform={rotate}
      >
        {lines.map((line, lineIndex) => (
          <tspan key={`${index}-${lineIndex}`} x={labelX} dy={lineIndex === 0 ? 0 : Math.round(fontSize * 1.15)}>{line}</tspan>
        ))}
      </text>
    </g>
  );
}

function categoryXAxisProps(visual, dataCount = 1) {
  return {
    interval: 0,
    height: categoryAxisHeight(visual, dataCount),
    tick: (props) => <DraggableCategoryTick {...props} visual={visual} orientation="x" dataCount={dataCount} />,
  };
}

function categoryYAxisProps(visual, dataCount = 1) {
  const fontSize = categoryLabelFontSize(visual);
  const labels = splitLooseList(visual?.map?.labels || visual?.map?.categories || "");
  const longest = labels.reduce((max, label) => Math.max(max, String(label).length), 0);
  return {
    interval: 0,
    width: clamp(Math.round(longest * fontSize * 0.55 + 18), 42, 180),
    tick: (props) => <DraggableCategoryTick {...props} visual={visual} orientation="y" dataCount={dataCount} />,
  };
}

function DraggableBarValueLabel({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  value,
  index = 0,
  seriesIndex = 0,
  layout = "horizontal",
  dataCount = 1,
  visual,
}) {
  const key = `${seriesIndex}:${index}`;
  const persisted = parseLabelOffsets(visual?.map?.label_offsets || "")[key] || null;
  const dense = dataCount > 6;
  const autoLevel = dense && layout !== "vertical" ? ((index % 2) + seriesIndex * 2) : seriesIndex;
  const defaultOffset = layout === "vertical"
    ? { dx: 0, dy: 0 }
    : { dx: 0, dy: -(dense ? autoLevel * 14 : seriesIndex * 12) };
  const [localOffset, setLocalOffset] = useState(persisted || defaultOffset);
  const dragRef = useRef(null);
  const latestOffsetRef = useRef(persisted || defaultOffset);

  useEffect(() => {
    const next = persisted || defaultOffset;
    latestOffsetRef.current = next;
    setLocalOffset(next);
  }, [persisted?.dx, persisted?.dy, defaultOffset.dx, defaultOffset.dy]);

  if (!shouldShowDataLabels(visual)) return null;

  const baseX = layout === "vertical" ? x + width + 9 : x + width / 2;
  const baseY = layout === "vertical" ? y + height / 2 + 4 : y - 8;
  const labelX = baseX + safeNumber(localOffset.dx, 0);
  const labelY = baseY + safeNumber(localOffset.dy, 0);
  const labelColor = cssColor(visual?.map?.data_label_color, TEXT_DARK);
  const fontSize = clamp(safeNumber(visual?.map?.data_label_font_size, 12), 9, 20);
  const pointerVisible = boolSetting(visual?.map?.label_pointer, false);
  const canDrag = Boolean(visual?.canEditLabels && typeof visual?.onCommitLabelOffset === "function");
  const moved = Boolean(persisted) && (Math.abs(labelX - baseX) > 2 || Math.abs(labelY - baseY) > 2);
  const markerScope = String(visual?.chartId || "chart").replace(/[^a-zA-Z0-9_-]+/g, "-");
  const markerId = `doc-writer-label-arrow-${markerScope}-${seriesIndex}-${index}`;

  const beginDrag = (event) => {
    if (!canDrag) return;
    event.preventDefault();
    event.stopPropagation();
    const start = { ...localOffset };
    dragRef.current = { startX: event.clientX, startY: event.clientY, start };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}

    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const next = {
        dx: Math.round(state.start.dx + moveEvent.clientX - state.startX),
        dy: Math.round(state.start.dy + moveEvent.clientY - state.startY),
      };
      latestOffsetRef.current = next;
      setLocalOffset(next);
    };
    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = dragRef.current;
      dragRef.current = null;
      if (!state) return;
      visual.onCommitLabelOffset?.(key, latestOffsetRef.current);
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  return (
    <g
      onPointerDown={beginDrag}
      style={{ cursor: canDrag ? "move" : "default", userSelect: "none", touchAction: "none" }}
      aria-label={`Value ${formatValue(value)}`}
    >
      {pointerVisible && moved ? (
        <>
          <defs>
            <marker id={markerId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L6,3 L0,6 z" fill={labelColor} />
            </marker>
          </defs>
          <line
            x1={labelX}
            y1={labelY + 2}
            x2={baseX}
            y2={baseY}
            stroke={labelColor}
            strokeWidth="1"
            opacity="0.72"
            markerEnd={`url(#${markerId})`}
          />
        </>
      ) : null}
      <text
        x={labelX}
        y={labelY}
        textAnchor={layout === "vertical" ? "start" : "middle"}
        dominantBaseline="central"
        fill={labelColor}
        fontSize={fontSize}
        fontWeight="800"
        stroke={visual?.background === "transparent" ? "#ffffff" : (visual?.background || "#ffffff")}
        strokeWidth="3"
        paintOrder="stroke"
      >
        {formatChartDataLabel({ visual, index, seriesIndex, value })}
      </text>
    </g>
  );
}


function DraggablePointValueLabel({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  value,
  index = 0,
  seriesIndex = 0,
  visual,
}) {
  if (!shouldShowDataLabels(visual)) return null;
  const key = `${seriesIndex}:${index}`;
  const persisted = parseLabelOffsets(visual?.map?.label_offsets || "")[key] || { dx: 0, dy: 0 };
  const [localOffset, setLocalOffset] = useState(persisted);
  const latestOffsetRef = useRef(persisted);
  const dragRef = useRef(null);

  useEffect(() => {
    const next = parseLabelOffsets(visual?.map?.label_offsets || "")[key] || { dx: 0, dy: 0 };
    latestOffsetRef.current = next;
    setLocalOffset(next);
  }, [visual?.map?.label_offsets, key]);

  const baseX = x + width / 2;
  const baseY = y - 8;
  const labelX = baseX + safeNumber(localOffset.dx, 0);
  const labelY = baseY + safeNumber(localOffset.dy, 0);
  const labelColor = cssColor(visual?.map?.data_label_color, TEXT_DARK);
  const fontSize = clamp(safeNumber(visual?.map?.data_label_font_size, 11), 7, 24);
  const pointerVisible = boolSetting(visual?.map?.label_pointer, false);
  const canDrag = Boolean(visual?.canEditLabels && typeof visual?.onCommitLabelOffset === "function");
  const moved = Math.abs(labelX - baseX) > 2 || Math.abs(labelY - baseY) > 2;
  const markerScope = String(visual?.chartId || "chart").replace(/[^a-zA-Z0-9_-]+/g, "-");
  const markerId = `doc-writer-point-label-arrow-${markerScope}-${seriesIndex}-${index}`;

  const beginDrag = (event) => {
    if (!canDrag) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { startX: event.clientX, startY: event.clientY, start: { ...localOffset } };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}
    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const next = {
        dx: Math.round(state.start.dx + moveEvent.clientX - state.startX),
        dy: Math.round(state.start.dy + moveEvent.clientY - state.startY),
      };
      latestOffsetRef.current = next;
      setLocalOffset(next);
    };
    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = dragRef.current;
      dragRef.current = null;
      if (!state) return;
      visual.onCommitLabelOffset?.(key, latestOffsetRef.current);
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  return (
    <g onPointerDown={beginDrag} style={{ cursor: canDrag ? "move" : "default", userSelect: "none", touchAction: "none" }}>
      {pointerVisible && moved ? (
        <>
          <defs>
            <marker id={markerId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L6,3 L0,6 z" fill={labelColor} />
            </marker>
          </defs>
          <line x1={labelX} y1={labelY + 2} x2={baseX} y2={baseY + 5} stroke={labelColor} strokeWidth="1" opacity="0.7" markerEnd={`url(#${markerId})`} />
        </>
      ) : null}
      <text
        x={labelX}
        y={labelY}
        textAnchor="middle"
        dominantBaseline="central"
        fill={labelColor}
        fontSize={fontSize}
        fontWeight="800"
        stroke={visual?.background === "transparent" ? "#ffffff" : (visual?.background || "#ffffff")}
        strokeWidth="3"
        paintOrder="stroke"
      >
        {formatChartDataLabel({ visual, index, seriesIndex, value })}
      </text>
    </g>
  );
}

function DraggablePieValueLabel({
  cx = 0,
  cy = 0,
  midAngle = 0,
  outerRadius = 0,
  name,
  value,
  index = 0,
  visual,
}) {
  if (!shouldShowDataLabels(visual)) return null;
  const RAD = Math.PI / 180;
  const radius = safeNumber(outerRadius, 0) + 18;
  const baseX = cx + radius * Math.cos(-midAngle * RAD);
  const baseY = cy + radius * Math.sin(-midAngle * RAD);
  const key = `0:${index}`;
  const persisted = parseLabelOffsets(visual?.map?.label_offsets || "")[key] || { dx: 0, dy: 0 };
  const [localOffset, setLocalOffset] = useState(persisted);
  const latestOffsetRef = useRef(persisted);
  const dragRef = useRef(null);

  useEffect(() => {
    const next = parseLabelOffsets(visual?.map?.label_offsets || "")[key] || { dx: 0, dy: 0 };
    latestOffsetRef.current = next;
    setLocalOffset(next);
  }, [visual?.map?.label_offsets, key]);

  const labelX = baseX + safeNumber(localOffset.dx, 0);
  const labelY = baseY + safeNumber(localOffset.dy, 0);
  const labelColor = cssColor(visual?.map?.data_label_color, TEXT_DARK);
  const fontSize = clamp(safeNumber(visual?.map?.data_label_font_size, 11), 7, 24);
  const pointerVisible = boolSetting(visual?.map?.label_pointer, false);
  const canDrag = Boolean(visual?.canEditLabels && typeof visual?.onCommitLabelOffset === "function");
  const moved = Math.abs(localOffset.dx) > 2 || Math.abs(localOffset.dy) > 2;
  const markerScope = String(visual?.chartId || "chart").replace(/[^a-zA-Z0-9_-]+/g, "-");
  const markerId = `doc-writer-pie-label-arrow-${markerScope}-${index}`;

  const beginDrag = (event) => {
    if (!canDrag) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { startX: event.clientX, startY: event.clientY, start: { ...localOffset } };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}
    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const next = {
        dx: Math.round(state.start.dx + moveEvent.clientX - state.startX),
        dy: Math.round(state.start.dy + moveEvent.clientY - state.startY),
      };
      latestOffsetRef.current = next;
      setLocalOffset(next);
    };
    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = dragRef.current;
      dragRef.current = null;
      if (!state) return;
      visual.onCommitLabelOffset?.(key, latestOffsetRef.current);
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  return (
    <g onPointerDown={beginDrag} style={{ cursor: canDrag ? "move" : "default", userSelect: "none", touchAction: "none" }}>
      {pointerVisible && moved ? (
        <>
          <defs>
            <marker id={markerId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L6,3 L0,6 z" fill={labelColor} />
            </marker>
          </defs>
          <line x1={labelX} y1={labelY} x2={baseX} y2={baseY} stroke={labelColor} strokeWidth="1" opacity="0.7" markerEnd={`url(#${markerId})`} />
        </>
      ) : null}
      <text x={labelX} y={labelY} textAnchor={labelX >= cx ? "start" : "end"} dominantBaseline="central" fill={labelColor} fontSize={fontSize} fontWeight="700">
        {formatChartDataLabel({ visual, index, seriesIndex: 0, value, category: name })}
      </text>
    </g>
  );
}

/* =========================
   Recharts renderers
   ========================= */

function RenderBarLike({ visual, mode = "column", stacked = false, grouped = false }) {
  const { data, keys } = normalizeMultiSeriesData(visual.map);
  if (!data.length) return <EmptyChart />;

  const layout = mode === "bar" ? "vertical" : "horizontal";
  const isSingleSeries = keys.length === 1 && keys[0] === "value" && !stacked && !grouped;
  const seriesColors = getSeriesColors(keys.length, visual);
  const barColors = getSeriesColors(data.length, visual);
  const chartData = isSingleSeries
    ? data.map((row, index) => ({
        ...row,
        __tooltipColor: barColors[index % barColors.length],
      }))
    : data;
  const maxValue = Math.max(
    1,
    ...data.flatMap((row) => keys.map((key) => Math.abs(safeNumber(row[key], 0))))
  );
  const numericDomain = [0, Math.ceil(maxValue * (shouldShowDataLabels(visual) ? 1.32 : 1.12))];
  const legendName = visual.map.legend || visual.map.series_name || visual.map.value_label || visual.yLabel || "Value";
  const showCategoryLegend = isSingleSeries && data.length > 1;
  const categoryLegendContent = renderCategoryLegendContent(
    data,
    barColors,
    keys[0] || "value",
    visual.title || legendName,
    visual
  );

  const commonMargin = getChartMargins(
    visual,
    layout === "vertical"
      ? { top: 22, right: showCategoryLegend ? 82 : 54, bottom: 30, left: 18 }
      : { top: 28, right: 44, bottom: 34, left: 18 }
  );

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <BarChart
        data={chartData}
        layout={layout}
        margin={commonMargin}
        barCategoryGap={`${clamp(safeNumber(visual.map.bar_gap, layout === "vertical" ? 18 : 24), 0, 70)}%`}
      >
        {shouldShowGrid(visual) ? (
          <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} />
        ) : null}
        {layout === "vertical" ? (
          <>
            <XAxis type="number" domain={numericDomain} {...getXAxisVisualProps(visual)} />
            <YAxis type="category" dataKey="name" {...getYAxisVisualProps(visual)} {...categoryYAxisProps(visual, data.length)} />
          </>
        ) : (
          <>
            <XAxis dataKey="name" {...getXAxisVisualProps(visual)} {...categoryXAxisProps(visual, data.length)} />
            <YAxis domain={numericDomain} {...getYAxisVisualProps(visual)} />
          </>
        )}
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        {showCategoryLegend && shouldShowLegend(visual) ? (
          <Legend
            verticalAlign={visual.legendPosition === "top" ? "top" : "bottom"}
            align={legendAlignment(visual)}
            layout="horizontal"
            content={categoryLegendContent}
            wrapperStyle={{
              width: `calc(100% - ${Math.max(0, commonMargin.left + commonMargin.right)}px)`,
              left: Math.max(0, commonMargin.left),
              textAlign: legendAlignment(visual),
              color: legendTextColor(visual),
            }}
          />
        ) : (
          <StandardLegend visual={visual} plotLeft={commonMargin.left} plotRight={commonMargin.right} />
        )}
        {keys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            name={isSingleSeries ? legendName : key}
            stackId={stacked ? "stack" : undefined}
            fill={isSingleSeries ? barColors[0] : seriesColors[i]}
            radius={layout === "vertical" ? [0, 8, 8, 0] : [8, 8, 0, 0]}
            isAnimationActive={false}
          >
            {isSingleSeries
              ? chartData.map((_, cellIndex) => (
                  <Cell key={`bar-cell-${cellIndex}`} fill={barColors[cellIndex % barColors.length]} />
                ))
              : null}
            {shouldShowDataLabels(visual) ? (
              <LabelList
                dataKey={key}
                content={(
                  <DraggableBarValueLabel
                    seriesIndex={i}
                    layout={layout}
                    dataCount={data.length}
                    visual={visual}
                  />
                )}
              />
            ) : null}
          </Bar>
        ))}
      </BarChart>
    </RechartsFrame>
  );
}

function normalizeLineData(map) {
  /*
    Robust line chart support.

    Accepts:
      type=line; labels=Jan,Feb,Mar; values=10,20,30
      type=line; x=Jan,Feb,Mar; y=10,20,30
      type=line; points=A:10,92|B:25,80|C:40,65
      type=line; data=Jan:10|Feb:20|Mar:30
      type=line; series=Revenue:10,20,30|Expense:5,8,12
  */

  const pointData = parsePointList(map.points || "");
  if (pointData.length >= 2) {
    return {
      data: pointData.map((p) => ({
        name: p.name,
        x: p.x,
        value: p.y,
        y: p.y,
      })),
      keys: ["value"],
      xMode: "number",
    };
  }

  const xyData = parseXYLists(map);
  if (xyData.length >= 2) {
    return {
      data: xyData.map((p) => ({
        name: p.name,
        x: p.x,
        value: p.y,
        y: p.y,
      })),
      keys: ["value"],
      xMode: "number",
    };
  }

  const { data, keys } = normalizeMultiSeriesData(map);
  return {
    data,
    keys,
    xMode: "category",
  };
}

function RenderLine({ visual }) {
  const { data, keys, xMode } = normalizeLineData(visual.map);
  if (!data.length) return <EmptyChart message="Line charts require labels/values, series, or points data." />;

  const colors = getSeriesColors(keys.length, visual);
  const lineKind = visual.map.line_type || visual.map.curve || visual.map.interpolation || "monotone";

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <LineChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        {xMode === "number" ? (
          <XAxis
            type="number"
            dataKey="x"
            tick={{ fill: TEXT_MUTED, fontSize: 12 }}
            label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} {...getXAxisVisualProps(visual)} />
        ) : (
          <XAxis
            dataKey="name"
            label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined}
            {...getXAxisVisualProps(visual)}
            {...categoryXAxisProps(visual, data.length)}
          />
        )}
        <YAxis
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} plotLeft={46} plotRight={46} />
        {keys.map((key, i) => (
          <Line
            key={key}
            type={lineKind}
            dataKey={key}
            name={keys.length === 1 && key === "value" ? visual.title || "Line" : key}
            stroke={colors[i]}
            strokeWidth={clamp(safeNumber(visual.map.line_width, 3), 1, 10)}
            connectNulls
            dot={{ r: clamp(safeNumber(visual.map.marker_size, 5), 1, 14), fill: colors[i], stroke: "#fff", strokeWidth: 2 }}
            activeDot={{ r: clamp(safeNumber(visual.map.marker_size, 5), 1, 14) + 2 }}
            isAnimationActive={false}
          >
            {shouldShowDataLabels(visual) ? (
              <LabelList dataKey={key} content={<DraggablePointValueLabel seriesIndex={i} visual={visual} />} />
            ) : null}
          </Line>
        ))}
      </LineChart>
    </RechartsFrame>
  );
}

function RenderArea({ visual, stacked = false }) {
  const { data, keys } = normalizeMultiSeriesData(visual.map);
  if (!data.length) return <EmptyChart />;

  const colors = getSeriesColors(keys.length, visual);

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <AreaChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 28, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis dataKey="name" {...getXAxisVisualProps(visual)} {...categoryXAxisProps(visual, data.length)} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} plotLeft={46} plotRight={46} />
        {keys.map((key, i) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stackId={stacked ? "stack" : undefined}
            stroke={colors[i]}
            fill={colors[i]}
            fillOpacity={0.28}
          >
            {shouldShowDataLabels(visual) ? (
              <LabelList dataKey={key} content={<DraggablePointValueLabel seriesIndex={i} visual={visual} />} />
            ) : null}
          </Area>
        ))}
      </AreaChart>
    </RechartsFrame>
  );
}

function RenderPieDonut({ visual, donut = false }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const colors = getSeriesColors(data.length, visual);

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={donut ? "48%" : 0}
          outerRadius="78%"
          paddingAngle={donut ? 2 : 1}
          label={shouldShowDataLabels(visual) ? (props) => <DraggablePieValueLabel {...props} visual={visual} /> : false}
          labelLine={false}
        >
          {data.map((_, i) => (
            <Cell key={`slice-${i}`} fill={colors[i]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} plotLeft={32} plotRight={32} />
      </PieChart>
    </RechartsFrame>
  );
}

function RenderScatterBubble({ visual, bubble = false }) {
  let data = parsePointList(visual.map.points || visual.map.data || "");
  if (!data.length) data = parseXYLists(visual.map);
  if (!data.length) return <EmptyChart message="Scatter/bubble charts require points like A:10,80|B:30,60." />;

  const color = visual.color || DEFAULT_PALETTE[0];
  const requestedTrend = String(visual.map.trendline || visual.map.regression || visual.map.show_regression || "").trim().toLowerCase();
  const connectLine =
    isTruthyFlag(visual.map.line) ||
    isTruthyFlag(visual.map.connect) ||
    isTruthyFlag(visual.map.connected) ||
    isTruthyFlag(visual.map.show_line) ||
    requestedTrend === "connect" ||
    requestedTrend === "connected" ||
    normalizeType(visual.map.type) === "connected_scatter" ||
    normalizeType(visual.map.type) === "scatter_line";

  const showRegression =
    requestedTrend === "regression" ||
    requestedTrend === "linear" ||
    requestedTrend === "true" ||
    isTruthyFlag(visual.map.regression) ||
    isTruthyFlag(visual.map.show_regression) ||
    normalizeType(visual.map.chart) === "regression_trendline" ||
    normalizeType(visual.map.type) === "regression_trendline";

  const sortedData = [...data].sort((a, b) => safeNumber(a.x, 0) - safeNumber(b.x, 0));
  const regression = showRegression ? linearRegression(sortedData) : null;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <ScatterChart margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis
          type="number"
          dataKey="x"
          name={visual.xLabel || "X"}
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} {...getXAxisVisualProps(visual)} />
        <YAxis
          type="number"
          dataKey="y"
          name={visual.yLabel || "Y"}
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} {...getYAxisVisualProps(visual)} />
        {bubble ? <ZAxis type="number" dataKey="z" range={[70, 650]} name={visual.zLabel || "Size"} /> : null}
        <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} plotLeft={46} plotRight={46} />
        <Scatter
          name={visual.title || (bubble ? "Bubble" : connectLine ? "Connected Scatter" : "Scatter")}
          data={sortedData}
          fill={color}
          stroke={color}
          line={connectLine ? { stroke: color, strokeWidth: 3 } : false}
          lineType="joint"
          isAnimationActive={false}
          shape={(props) => {
            const { cx, cy, fill } = props;
            return (
              <circle
                cx={cx}
                cy={cy}
                r={bubble ? Math.max(7, clamp(safeNumber(visual.map.marker_size, 9), 2, 20)) : clamp(safeNumber(visual.map.marker_size, 7), 2, 20)}
                fill={fill || color}
                stroke="#ffffff"
                strokeWidth={2}
              />
            );
          }}
        >
          {shouldShowDataLabels(visual) ? (
            <LabelList dataKey="y" content={<DraggablePointValueLabel seriesIndex={0} visual={visual} />} />
          ) : null}
        </Scatter>
        {regression ? (
          <Scatter
            name={`Regression trendline${Number.isFinite(regression.r2) ? ` (R² ${regression.r2.toFixed(2)})` : ""}`}
            data={regression.line}
            fill="transparent"
            stroke={DEFAULT_PALETTE[2]}
            line={{ stroke: DEFAULT_PALETTE[2], strokeWidth: 3, strokeDasharray: "6 4" }}
            lineType="joint"
            legendType="line"
            tooltipType="none"
            isAnimationActive={false}
            shape={() => null}
          />
        ) : null}
      </ScatterChart>
    </RechartsFrame>
  );
}

function RenderHistogram({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  const binCount = safeNumber(visual.map.bins || visual.map.bin_count || 10, 10);
  const data = buildHistogramData(values, binCount);

  if (!data.length) return <EmptyChart message="Histogram requires values=10,12,14,15,20..." />;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <BarChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis dataKey="range" tick={{ fill: TEXT_MUTED, fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={55} {...getXAxisVisualProps(visual)} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Bar dataKey="count" name="Frequency" fill={visual.color || DEFAULT_PALETTE[0]} radius={[6, 6, 0, 0]} />
      </BarChart>
    </RechartsFrame>
  );
}

function RenderBellCurve({ visual }) {
  const data = buildBellCurveData(visual.map);
  const mean = safeNumber(visual.map.mean || visual.map.mu || visual.map.average || 0, 0);
  const sd = Math.max(0.0001, safeNumber(visual.map.sd || visual.map.std || visual.map.standard_deviation || visual.map.sigma || 1, 1));
  const shadeLow = toFiniteNumber(visual.map.low || visual.map.shade_low || visual.map.ci_low || visual.map.confidence_low);
  const shadeHigh = toFiniteNumber(visual.map.high || visual.map.shade_high || visual.map.ci_high || visual.map.confidence_high);
  const showStd = !String(visual.map.show_std || visual.map.standard_deviation_lines || "").trim() || isTruthyFlag(visual.map.show_std || visual.map.standard_deviation_lines || "true");

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <AreaChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis
          dataKey="x"
          type="number"
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          domain={["dataMin", "dataMax"]}
          label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} {...getXAxisVisualProps(visual)} />
        <YAxis
          tick={{ fill: TEXT_MUTED, fontSize: 12 }}
          domain={[0, 105]}
          label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        {shadeLow !== null && shadeHigh !== null ? (
          <ReferenceArea x1={shadeLow} x2={shadeHigh} fill={DEFAULT_PALETTE[1]} fillOpacity={0.16} />
        ) : null}
        <Area
          type="monotone"
          dataKey="y"
          name="Relative density"
          stroke={visual.color || DEFAULT_PALETTE[0]}
          fill={visual.color || DEFAULT_PALETTE[0]}
          fillOpacity={0.25}
          isAnimationActive={false}
        />
        <ReferenceLine x={mean} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "Mean", fill: TEXT_MUTED, fontSize: 11 }} />
        {showStd ? [-2, -1, 1, 2].map((k) => (
          <ReferenceLine key={k} x={mean + k * sd} stroke={AXIS} strokeDasharray="2 5" />
        )) : null}
      </AreaChart>
    </RechartsFrame>
  );
}

function RenderConfidenceInterval({ visual }) {
  const data = parseRangeData(visual.map);
  if (!data.length) return <EmptyChart message="Confidence interval charts require labels plus low/mean/high values." />;

  const width = 520;
  const height = Math.max(240, 80 + data.length * 44);
  const left = 112;
  const right = 42;
  const top = 34;
  const bottom = 44;
  const rowH = (height - top - bottom) / data.length;
  const allVals = data.flatMap((d) => [d.low, d.mean, d.high]).filter(Number.isFinite);
  const min = Math.min(...allVals, 0);
  const max = Math.max(...allVals, 1);
  const pad = (max - min || 1) * 0.08;
  const scaleX = (v) => left + ((v - (min - pad)) / ((max + pad) - (min - pad) || 1)) * (width - left - right);
  const color = visual.color || DEFAULT_PALETTE[0];

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Confidence interval chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const x = left + r * (width - left - right);
        return <line key={i} x1={x} y1={top - 8} x2={x} y2={height - bottom + 8} stroke={GRID} />;
      })}
      {data.map((d, i) => {
        const y = top + i * rowH + rowH / 2;
        const xLow = scaleX(d.low);
        const xMean = scaleX(d.mean);
        const xHigh = scaleX(d.high);
        return (
          <g key={`${d.name}-${i}`}>
            <text x={left - 12} y={y + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 16)}
            </text>
            <line x1={xLow} y1={y} x2={xHigh} y2={y} stroke={color} strokeWidth="4" strokeLinecap="round" />
            <line x1={xLow} y1={y - 9} x2={xLow} y2={y + 9} stroke={color} strokeWidth="3" />
            <line x1={xHigh} y1={y - 9} x2={xHigh} y2={y + 9} stroke={color} strokeWidth="3" />
            <circle cx={xMean} cy={y} r="7" fill={DEFAULT_PALETTE[2]} stroke="#fff" strokeWidth="2" />
            <text x={xHigh + 8} y={y + 4} fontSize="10" fill={TEXT_DARK} fontWeight="700">
              {formatValue(d.low)}–{formatValue(d.high)}
            </text>
          </g>
        );
      })}
      <line x1={left} y1={height - bottom + 8} x2={width - right} y2={height - bottom + 8} stroke={AXIS} />
      <text x={left} y={height - 16} fontSize="10" fill={TEXT_MUTED}>{formatValue(min)}</text>
      <text x={width - right} y={height - 16} textAnchor="end" fontSize="10" fill={TEXT_MUTED}>{formatValue(max)}</text>
    </SvgFrame>
  );
}

function RenderConfidenceBand({ visual }) {
  const data = parseBandData(visual.map);
  if (!data.length) return <EmptyChart message="Confidence/probability bands require low, expected/mean, and high values." />;

  const color = visual.color || DEFAULT_PALETTE[0];
  const bandColor = visual.colors?.[1] || DEFAULT_PALETTE[0];

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <ComposedChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis dataKey="name" label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} {...getXAxisVisualProps(visual)} {...categoryXAxisProps(visual, data.length)} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} />
        <Area type="monotone" dataKey="baseline" stackId="band" stroke="transparent" fill="transparent" name="Low" isAnimationActive={false} />
        <Area type="monotone" dataKey="band" stackId="band" stroke="transparent" fill={bandColor} fillOpacity={0.22} name="Confidence / Probability Band" isAnimationActive={false} />
        <Line type="monotone" dataKey="expected" name="Expected" stroke={color} strokeWidth={clamp(safeNumber(visual.map.line_width, 3), 1, 10)} dot={{ r: 5, fill: color, stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="low" name="Low" stroke={AXIS} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
        <Line type="monotone" dataKey="high" name="High" stroke={AXIS} strokeDasharray="5 5" dot={false} isAnimationActive={false} />
      </ComposedChart>
    </RechartsFrame>
  );
}

function RenderKdeDensity({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  const data = buildKdeData(values, visual.map.bandwidth, safeNumber(visual.map.points_count || visual.map.count || 121, 121));
  if (!data.length) return <EmptyChart message="KDE density requires values=10,12,14,20..." />;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <AreaChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis dataKey="x" type="number" domain={["dataMin", "dataMax"]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.xLabel ? { value: visual.xLabel, position: "insideBottom", offset: -10 } : undefined} {...getXAxisVisualProps(visual)} />
        <YAxis domain={[0, 105]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={visual.yLabel ? { value: visual.yLabel, angle: -90, position: "insideLeft" } : undefined} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <Area type="monotone" dataKey="y" name="Relative density" stroke={visual.color || DEFAULT_PALETTE[0]} fill={visual.color || DEFAULT_PALETTE[0]} fillOpacity={0.24} isAnimationActive={false} />
      </AreaChart>
    </RechartsFrame>
  );
}

function RenderRocCurve({ visual }) {
  const data = parseCurvePoints(visual.map);
  if (!data.length) return <EmptyChart message="ROC curve requires points=false_positive_rate,true_positive_rate." />;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <LineChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis type="number" dataKey="x" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.xLabel || "False Positive Rate", position: "insideBottom", offset: -10 }} {...getXAxisVisualProps(visual, { defaultLabel: "False Positive Rate" })} />
        <YAxis type="number" dataKey="y" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.yLabel || "True Positive Rate", angle: -90, position: "insideLeft" }} {...getYAxisVisualProps(visual, { defaultLabel: "True Positive Rate" })} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} />
        <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} stroke={AXIS} strokeDasharray="5 5" />
        <Line type="monotone" dataKey="y" name={visual.title || "ROC Curve"} stroke={visual.color || DEFAULT_PALETTE[0]} strokeWidth={clamp(safeNumber(visual.map.line_width, 3), 1, 10)} dot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </RechartsFrame>
  );
}

function RenderPrecisionRecall({ visual }) {
  const data = parseCurvePoints(visual.map);
  if (!data.length) return <EmptyChart message="Precision-recall requires points=threshold:recall,precision or x/y numeric values." />;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <LineChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 34, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis type="number" dataKey="x" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.xLabel || "Recall", position: "insideBottom", offset: -10 }} {...getXAxisVisualProps(visual, { defaultLabel: "Recall" })} />
        <YAxis type="number" dataKey="y" domain={[0, 1]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} label={{ value: visual.yLabel || "Precision", angle: -90, position: "insideLeft" }} {...getYAxisVisualProps(visual, { defaultLabel: "Precision" })} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} />
        <Line type="monotone" dataKey="y" name={visual.title || "Precision-Recall Curve"} stroke={visual.color || DEFAULT_PALETTE[0]} strokeWidth={clamp(safeNumber(visual.map.line_width, 3), 1, 10)} dot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </RechartsFrame>
  );
}

function RenderViolinPlot({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  if (!values.length) return <EmptyChart message="Violin plot requires values=..." />;

  const width = 560;
  const height = Math.max(500, clamp(safeNumber(visual.height, 560) - 36, 460, 680));
  const left = 78;
  const right = 58;
  const top = 64;
  const bottom = 86;
  const maxHalfWidth = 118;
  const shape = buildViolinShape(values, width, height, left, right, top, bottom, maxHalfWidth);
  const color = visual.color || DEFAULT_PALETTE[0];
  const stats = shape.stats;
  const scaleY = shape.scaleY || ((v) => v);
  const centerX = shape.centerX || width / 2;

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Violin plot">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const y = top + r * (height - top - bottom);
        return <line key={i} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      <path d={shape.path} fill={color} opacity="0.26" stroke={color} strokeWidth="3" />
      <line x1={centerX} y1={scaleY(stats.min)} x2={centerX} y2={scaleY(stats.max)} stroke={TEXT_DARK} strokeWidth="2" />
      <rect x={centerX - 24} y={scaleY(stats.q3)} width="48" height={Math.max(4, scaleY(stats.q1) - scaleY(stats.q3))} rx="8" fill="#ffffff" stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={centerX - 34} y1={scaleY(stats.median)} x2={centerX + 34} y2={scaleY(stats.median)} stroke={DEFAULT_PALETTE[2]} strokeWidth="3" />
      <text x={centerX} y={height - 22} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="800">
        {visual.map.label || visual.title || "Distribution"}
      </text>
      <text x={left} y={scaleY(stats.max) + 4} textAnchor="start" fontSize="10" fill={TEXT_MUTED}>Max {formatValue(stats.max)}</text>
      <text x={left} y={scaleY(stats.median) + 4} textAnchor="start" fontSize="10" fill={TEXT_MUTED}>Median {formatValue(stats.median)}</text>
      <text x={left} y={scaleY(stats.min) + 4} textAnchor="start" fontSize="10" fill={TEXT_MUTED}>Min {formatValue(stats.min)}</text>
    </SvgFrame>
  );
}

function RenderRadar({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <RadarChart data={data} outerRadius="74%">
        <PolarGrid />
        <PolarAngleAxis dataKey="name" tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <PolarRadiusAxis tick={{ fill: TEXT_MUTED, fontSize: 11 }} />
        <Radar
          name={visual.title || "Score"}
          dataKey="value"
          stroke={visual.color || DEFAULT_PALETTE[0]}
          fill={visual.color || DEFAULT_PALETTE[0]}
          fillOpacity={0.45}
        />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} />
      </RadarChart>
    </RechartsFrame>
  );
}

function RenderComposed({ visual }) {
  const { data, keys } = normalizeMultiSeriesData(visual.map);
  if (!data.length) return <EmptyChart />;

  const colors = getSeriesColors(keys.length, visual);
  const lineKeys = splitLooseList(visual.map.line_keys || visual.map.lines || "");
  const barKeys = splitLooseList(visual.map.bar_keys || visual.map.bars || "");

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <ComposedChart data={data} margin={getChartMargins(visual, { top: 12, right: 46, bottom: 28, left: 14  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis dataKey="name" {...getXAxisVisualProps(visual)} {...categoryXAxisProps(visual, data.length)} />
        <YAxis tick={{ fill: TEXT_MUTED, fontSize: 12 }} {...getYAxisVisualProps(visual)} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} />
        {keys.map((key, i) => {
          const shouldLine = lineKeys.includes(key) || (!barKeys.includes(key) && i === keys.length - 1);
          if (shouldLine) {
            return <Line key={key} dataKey={key} stroke={colors[i]} strokeWidth={clamp(safeNumber(visual.map.line_width, 3), 1, 10)} type="monotone" />;
          }
          return <Bar key={key} dataKey={key} fill={colors[i]} radius={[6, 6, 0, 0]} />;
        })}
      </ComposedChart>
    </RechartsFrame>
  );
}

function RenderPareto({ visual }) {
  const base = normalizeCategoryData(visual.map);
  const data = buildParetoData(base);
  if (!data.length) return <EmptyChart />;

  return (
    <RechartsFrame height={visual.height} background={visual.background}>
      <ComposedChart data={data} margin={getChartMargins(visual, { top: 12, right: 28, bottom: 12, left: 8  })}>
        {shouldShowGrid(visual) ? <CartesianGrid strokeDasharray="3 3" stroke={cssColor(visual.map.grid_color, GRID)} /> : null}
        <XAxis dataKey="name" {...getXAxisVisualProps(visual)} {...categoryXAxisProps(visual, data.length)} />
        <YAxis yAxisId="left" tick={{ fill: TEXT_MUTED, fontSize: 12 }} {...getYAxisVisualProps(visual)} />
        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: TEXT_MUTED, fontSize: 12 }} />
        <Tooltip content={<CustomTooltip />} {...SAFE_TOOLTIP_PROPS} />
        <StandardLegend visual={visual} />
        <Bar yAxisId="left" dataKey="value" fill={DEFAULT_PALETTE[0]} radius={[6, 6, 0, 0]} />
        <Line yAxisId="right" type="monotone" dataKey="cumulative_percent" stroke={DEFAULT_PALETTE[2]} strokeWidth={clamp(safeNumber(visual.map.line_width, 3), 1, 10)} />
      </ComposedChart>
    </RechartsFrame>
  );
}

/* =========================
   Custom SVG renderers
   ========================= */

function SvgFrame({ children, viewBox = "0 0 520 300", label = "Chart", background = CARD_BG }) {
  return (
    <svg
      viewBox={viewBox}
      className="doc-writer-chart-svg"
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: "100%",
        maxHeight: "100%",
        display: "block",
        overflow: "hidden",
        background,
      }}
    >
      {children}
    </svg>
  );
}

function RenderWaterfall({ visual }) {
  const base = normalizeCategoryData(visual.map);
  const data = buildWaterfallData(base);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const left = 48;
  const right = 20;
  const top = 24;
  const bottom = 52;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const allVals = data.flatMap((d) => [d.start, d.end]);
  const min = Math.min(0, ...allVals);
  const max = Math.max(1, ...allVals);
  const scaleY = (v) => top + plotH - ((v - min) / (max - min || 1)) * plotH;
  const barW = Math.max(18, plotW / data.length - 14);

  return (
    <SvgFrame background={visual.background} label="Waterfall chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const y = top + plotH * r;
        return <line key={i} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      <line x1={left} y1={scaleY(0)} x2={width - right} y2={scaleY(0)} stroke={AXIS} strokeWidth="2" />
      {data.map((d, i) => {
        const x = left + i * (plotW / data.length) + 8;
        const y1 = scaleY(d.start);
        const y2 = scaleY(d.end);
        const y = Math.min(y1, y2);
        const h = Math.max(3, Math.abs(y2 - y1));
        return (
          <g key={d.name}>
            <rect x={x} y={y} width={barW} height={h} rx="6" fill={d.isTotal ? "#16a34a" : d.delta >= 0 ? "#16a34a" : "#dc2626"} />
            <text x={x + barW / 2} y={height - 24} textAnchor="middle" fontSize="10" fill={TEXT_MUTED}>
              {String(d.name).slice(0, 10)}
            </text>
            <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize="10" fill={TEXT_DARK} fontWeight="700">
              {formatValue(d.end)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderBoxPlot({ visual }) {
  const values = parseNumberList(visual.map.values || visual.map.data || "");
  const stats = buildBoxStats(values);
  if (!values.length) return <EmptyChart message="Box plot requires values=..." />;

  const width = 520;
  const height = 300;
  const left = 60;
  const right = 40;
  const top = 30;
  const bottom = 46;
  const plotW = width - left - right;
  const centerY = top + (height - top - bottom) / 2;
  const min = stats.min;
  const max = stats.max || 1;
  const scaleX = (v) => left + ((v - min) / (max - min || 1)) * plotW;

  return (
    <SvgFrame background={visual.background} label="Box plot">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <line x1={left} y1={centerY} x2={width - right} y2={centerY} stroke={AXIS} strokeWidth="2" />
      <line x1={scaleX(stats.min)} y1={centerY - 34} x2={scaleX(stats.min)} y2={centerY + 34} stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={scaleX(stats.max)} y1={centerY - 34} x2={scaleX(stats.max)} y2={centerY + 34} stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={scaleX(stats.min)} y1={centerY} x2={scaleX(stats.q1)} y2={centerY} stroke={TEXT_DARK} strokeWidth="2" />
      <line x1={scaleX(stats.q3)} y1={centerY} x2={scaleX(stats.max)} y2={centerY} stroke={TEXT_DARK} strokeWidth="2" />
      <rect
        x={scaleX(stats.q1)}
        y={centerY - 42}
        width={Math.max(3, scaleX(stats.q3) - scaleX(stats.q1))}
        height="84"
        rx="10"
        fill="rgba(37,99,235,0.18)"
        stroke={visual.color || DEFAULT_PALETTE[0]}
        strokeWidth="3"
      />
      <line x1={scaleX(stats.median)} y1={centerY - 44} x2={scaleX(stats.median)} y2={centerY + 44} stroke="#dc2626" strokeWidth="3" />
      {[
        ["Min", stats.min],
        ["Q1", stats.q1],
        ["Median", stats.median],
        ["Q3", stats.q3],
        ["Max", stats.max],
      ].map(([label, value], i) => (
        <text key={label} x={scaleX(value)} y={height - 18 - (i % 2) * 14} textAnchor="middle" fontSize="10" fill={TEXT_MUTED}>
          {label}: {formatValue(value)}
        </text>
      ))}
    </SvgFrame>
  );
}

function RenderHeatmap({ visual }) {
  const { rows, cols, matrix } = parseHeatmapData(visual.map);
  if (!matrix.length) return <EmptyChart message="Heatmap requires rows, cols, and values like values=10,20|30,40." />;

  const flat = matrix.flat().filter(Number.isFinite);
  const min = Math.min(...flat, 0);
  const max = Math.max(...flat, 1);
  const cell = 42;
  const left = 92;
  const top = 48;
  const width = left + cols.length * cell + 24;
  const height = top + rows.length * cell + 34;

  const color = visual.color || DEFAULT_PALETTE[0];

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Heatmap">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {cols.map((c, i) => (
        <text key={c} x={left + i * cell + cell / 2} y={28} textAnchor="middle" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
          {String(c).slice(0, 8)}
        </text>
      ))}
      {rows.map((r, row) => (
        <g key={r}>
          <text x={left - 10} y={top + row * cell + cell / 2 + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
            {String(r).slice(0, 12)}
          </text>
          {cols.map((c, col) => {
            const v = matrix[row]?.[col] ?? 0;
            const opacity = clamp((v - min) / (max - min || 1), 0.08, 1);
            return (
              <g key={`${r}-${c}`}>
                <rect
                  x={left + col * cell}
                  y={top + row * cell}
                  width={cell - 4}
                  height={cell - 4}
                  rx="8"
                  fill={color}
                  opacity={opacity}
                  stroke="rgba(15,23,42,0.08)"
                />
                <text x={left + col * cell + cell / 2 - 2} y={top + row * cell + cell / 2 + 4} textAnchor="middle" fontSize="10" fill="#ffffff" fontWeight="800">
                  {formatValue(v)}
                </text>
              </g>
            );
          })}
        </g>
      ))}
    </SvgFrame>
  );
}

function RenderFunnel({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 320;
  const top = 26;
  const stepH = 44;
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Funnel chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {data.map((d, i) => {
        const w = 380 * (Math.abs(d.value) / max);
        const x = (width - w) / 2;
        const y = top + i * (stepH + 8);
        return (
          <g key={d.name}>
            <rect x={x} y={y} width={w} height={stepH} rx="12" fill={colors[i]} opacity="0.9" />
            <text x={width / 2} y={y + 27} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="800">
              {d.name}: {formatValue(d.value)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderGauge({ visual }) {
  const value = clamp(safeNumber(visual.map.value || visual.map.score || visual.map.percent || 0, 0), 0, 100);
  const width = 520;
  const height = 280;
  const cx = 260;
  const cy = 220;
  const r = 145;
  const angle = Math.PI * (1 - value / 100);
  const needleX = cx + (r - 28) * Math.cos(angle);
  const needleY = cy - (r - 28) * Math.sin(angle);

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Gauge chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#e5e7eb" strokeWidth="30" strokeLinecap="round" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r}`} fill="none" stroke="#dc2626" strokeWidth="30" strokeLinecap="round" />
      <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#16a34a" strokeWidth="30" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={TEXT_DARK} strokeWidth="5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="9" fill={TEXT_DARK} />
      <text x={cx} y={cy - 42} textAnchor="middle" fontSize="34" fill={TEXT_DARK} fontWeight="900">
        {formatValue(value)}%
      </text>
      <text x={cx} y={cy - 16} textAnchor="middle" fontSize="13" fill={TEXT_MUTED} fontWeight="700">
        {visual.map.label || "Score"}
      </text>
    </SvgFrame>
  );
}

function RenderProgress({ visual }) {
  const value = clamp(safeNumber(visual.map.value || visual.map.percent || visual.map.progress || 0, 0), 0, 100);
  return (
    <div style={{ padding: 24, height: "100%", display: "grid", alignContent: "center" }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: TEXT_DARK, marginBottom: 8 }}>
        {visual.map.label || visual.title || "Progress"} — {formatValue(value)}%
      </div>
      <div style={{ height: 22, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", borderRadius: 999, background: visual.color || DEFAULT_PALETTE[0] }} />
      </div>
    </div>
  );
}

function RenderTimeline({ visual }) {
  const data = parseTimelineData(visual.map);
  if (!data.length) return <EmptyChart message="Timeline requires events=Event:Date|Event:Date." />;

  const width = 520;
  const height = Math.max(220, 80 + data.length * 48);
  const x = 120;

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Timeline">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <line x1={x} y1="40" x2={x} y2={height - 30} stroke={AXIS} strokeWidth="3" />
      {data.map((d, i) => {
        const y = 52 + i * 48;
        return (
          <g key={`${d.name}-${i}`}>
            <circle cx={x} cy={y} r="9" fill={DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]} stroke="#fff" strokeWidth="3" />
            <text x={x + 24} y={y - 4} fontSize="13" fill={TEXT_DARK} fontWeight="800">
              {d.name}
            </text>
            <text x={x + 24} y={y + 14} fontSize="11" fill={TEXT_MUTED}>
              {d.date}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderGantt({ visual }) {
  const data = parseGanttData(visual.map);
  if (!data.length) return <EmptyChart message="Gantt requires tasks=Task A:0,5|Task B:4,10." />;

  const width = 520;
  const height = Math.max(220, 70 + data.length * 42);
  const left = 120;
  const right = 24;
  const top = 38;
  const rowH = 34;
  const min = Math.min(...data.map((d) => d.start), 0);
  const max = Math.max(...data.map((d) => d.end), 1);
  const scaleX = (v) => left + ((v - min) / (max - min || 1)) * (width - left - right);
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Gantt chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <line x1={left} y1={height - 28} x2={width - right} y2={height - 28} stroke={AXIS} />
      {data.map((d, i) => {
        const y = top + i * rowH;
        const x = scaleX(d.start);
        const w = Math.max(8, scaleX(d.end) - scaleX(d.start));
        return (
          <g key={d.name}>
            <text x={left - 10} y={y + 16} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 16)}
            </text>
            <rect x={x} y={y} width={w} height="22" rx="8" fill={colors[i]} />
            <text x={x + w + 6} y={y + 15} fontSize="10" fill={TEXT_DARK} fontWeight="700">
              {formatValue(d.duration)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderCandlestick({ visual }) {
  const data = parseCandlestickData(visual.map);
  if (!data.length) return <EmptyChart message="Candlestick requires candles=Day1:open,high,low,close|Day2:..." />;

  const width = 520;
  const height = 300;
  const left = 48;
  const right = 24;
  const top = 24;
  const bottom = 44;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  const min = Math.min(...data.map((d) => d.low), 0);
  const max = Math.max(...data.map((d) => d.high), 1);
  const scaleY = (v) => top + plotH - ((v - min) / (max - min || 1)) * plotH;
  const step = plotW / data.length;
  const bodyW = Math.max(10, step * 0.5);

  return (
    <SvgFrame background={visual.background} label="Candlestick chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
        const y = top + plotH * r;
        return <line key={i} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      {data.map((d, i) => {
        const x = left + i * step + step / 2;
        const up = d.close >= d.open;
        const color = up ? "#16a34a" : "#dc2626";
        const yOpen = scaleY(d.open);
        const yClose = scaleY(d.close);
        return (
          <g key={d.name}>
            <line x1={x} y1={scaleY(d.high)} x2={x} y2={scaleY(d.low)} stroke={color} strokeWidth="2" />
            <rect x={x - bodyW / 2} y={Math.min(yOpen, yClose)} width={bodyW} height={Math.max(3, Math.abs(yClose - yOpen))} rx="3" fill={color} opacity="0.85" />
            <text x={x} y={height - 18} textAnchor="middle" fontSize="10" fill={TEXT_MUTED}>
              {String(d.name).slice(0, 6)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderTreemapBasic({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const total = data.reduce((s, d) => s + Math.max(0, safeNumber(d.value, 0)), 0) || 1;
  let x = 20;
  let y = 24;
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame background={visual.background} label="Treemap chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {data.map((d, i) => {
        const areaW = Math.max(44, (Math.max(0, d.value) / total) * (width - 40));
        const h = 58;
        if (x + areaW > width - 20) {
          x = 20;
          y += h + 10;
        }
        const rect = { x, y, w: Math.min(areaW, width - x - 20), h };
        x += rect.w + 8;
        return (
          <g key={d.name}>
            <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h} rx="10" fill={colors[i]} opacity="0.88" />
            <text x={rect.x + 10} y={rect.y + 24} fontSize="12" fill="#fff" fontWeight="800">
              {String(d.name).slice(0, 14)}
            </text>
            <text x={rect.x + 10} y={rect.y + 43} fontSize="11" fill="#fff">
              {formatValue(d.value)}
            </text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderSlope({ visual }) {
  // data=A:10,30|B:20,15 where start,end
  const points = splitPipeList(visual.map.data || visual.map.points || "").map((part, i) => {
    const [label, numsRaw] = part.split(":");
    const nums = parseNumberList(numsRaw);
    return { name: label || `Item ${i + 1}`, start: nums[0] ?? 0, end: nums[1] ?? 0 };
  });

  if (!points.length) return <EmptyChart message="Slope chart requires data=A:10,30|B:20,15." />;

  const width = 520;
  const height = 300;
  const leftX = 140;
  const rightX = 380;
  const top = 34;
  const bottom = 50;
  const vals = points.flatMap((p) => [p.start, p.end]);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 1);
  const scaleY = (v) => top + (height - top - bottom) - ((v - min) / (max - min || 1)) * (height - top - bottom);
  const colors = getSeriesColors(points.length, visual);

  return (
    <SvgFrame background={visual.background} label="Slope chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <text x={leftX} y={24} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="800">Start</text>
      <text x={rightX} y={24} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="800">End</text>
      {points.map((p, i) => {
        const y1 = scaleY(p.start);
        const y2 = scaleY(p.end);
        return (
          <g key={p.name}>
            <line x1={leftX} y1={y1} x2={rightX} y2={y2} stroke={colors[i]} strokeWidth="3" />
            <circle cx={leftX} cy={y1} r="5" fill={colors[i]} />
            <circle cx={rightX} cy={y2} r="5" fill={colors[i]} />
            <text x={leftX - 8} y={y1 + 4} textAnchor="end" fontSize="11" fill={TEXT_DARK}>{p.name} {formatValue(p.start)}</text>
            <text x={rightX + 8} y={y2 + 4} fontSize="11" fill={TEXT_DARK}>{formatValue(p.end)}</text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderLollipop({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const left = 90;
  const right = 28;
  const top = 24;
  const rowH = Math.min(34, (height - 60) / data.length);
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const color = visual.color || DEFAULT_PALETTE[0];

  return (
    <SvgFrame background={visual.background} label="Lollipop chart">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {data.map((d, i) => {
        const y = top + i * rowH + rowH / 2;
        const x2 = left + (Math.abs(d.value) / max) * (width - left - right);
        return (
          <g key={d.name}>
            <text x={left - 8} y={y + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 12)}
            </text>
            <line x1={left} y1={y} x2={x2} y2={y} stroke={GRID} strokeWidth="3" />
            <circle cx={x2} cy={y} r="8" fill={color} />
            <text x={x2 + 12} y={y + 4} fontSize="10" fill={TEXT_DARK} fontWeight="700">{formatValue(d.value)}</text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

function RenderDotPlot({ visual }) {
  const data = normalizeCategoryData(visual.map);
  if (!data.length) return <EmptyChart />;

  const width = 520;
  const height = 300;
  const left = 90;
  const right = 28;
  const top = 24;
  const rowH = Math.min(34, (height - 60) / data.length);
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);
  const colors = getSeriesColors(data.length, visual);

  return (
    <SvgFrame background={visual.background} label="Dot plot">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {data.map((d, i) => {
        const y = top + i * rowH + rowH / 2;
        const x = left + (Math.abs(d.value) / max) * (width - left - right);
        return (
          <g key={d.name}>
            <text x={left - 8} y={y + 4} textAnchor="end" fontSize="11" fill={TEXT_MUTED} fontWeight="700">
              {String(d.name).slice(0, 12)}
            </text>
            <circle cx={x} cy={y} r="8" fill={colors[i]} />
            <text x={x + 12} y={y + 4} fontSize="10" fill={TEXT_DARK} fontWeight="700">{formatValue(d.value)}</text>
          </g>
        );
      })}
    </SvgFrame>
  );
}

/* =========================
   Geometry / science diagrams
   ========================= */

function parseDimensionPair(value) {
  const match = String(value || "").trim().match(/(-?\d+(?:\.\d+)?)\s*[x×]\s*(-?\d+(?:\.\d+)?)/i);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
    ? { width, height }
    : null;
}

function parseRectanglePartitions(value) {
  return splitStructuredSegments(value).map((part, index) => {
    const raw = String(part || "").trim();
    const simple = raw.match(/^\s*([^:=]+?)\s*[:=]\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*$/i);
    if (simple) {
      const width = Number(simple[2]);
      const height = Number(simple[3]);
      return {
        name: simple[1].trim() || `Section ${index + 1}`,
        width,
        height,
        area: width * height,
        explicitPosition: false,
      };
    }

    const named = raw.match(/^\s*([^:=]+?)\s*[:=]\s*(.+)$/);
    if (!named) return null;
    const payload = named[2];
    const read = (key) => {
      const match = payload.match(new RegExp(`(?:^|[,\\s])${key}\\s*(?:=|:)?\\s*(-?\\d+(?:\\.\\d+)?)`, "i"));
      return match ? Number(match[1]) : null;
    };
    const x = read("x");
    const y = read("y");
    const width = read("width") ?? read("w");
    const height = read("height") ?? read("h");
    const area = read("area");
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
    return {
      name: named[1].trim() || `Section ${index + 1}`,
      x: Number.isFinite(x) ? x : 0,
      y: Number.isFinite(y) ? y : 0,
      width,
      height,
      area: Number.isFinite(area) ? area : width * height,
      explicitPosition: Number.isFinite(x) && Number.isFinite(y),
    };
  }).filter(Boolean);
}

function buildRectanglePartitionLayout(totalWidth, totalHeight, partitions) {
  if (partitions.length && partitions.every((item) => item.explicitPosition)) {
    return partitions.map((item) => ({ ...item }));
  }

  const eps = 0.001;
  const nearly = (a, b) => Math.abs(Number(a) - Number(b)) <= eps;
  if (!partitions.length) return [];

  // Common floor-plan pattern: one full-height section plus a stacked column.
  for (let index = 0; index < partitions.length; index += 1) {
    const main = partitions[index];
    const rest = partitions.filter((_, i) => i !== index);
    const remainingWidth = totalWidth - main.width;
    if (
      nearly(main.height, totalHeight) &&
      remainingWidth > 0 &&
      rest.length &&
      rest.every((item) => nearly(item.width, remainingWidth)) &&
      nearly(rest.reduce((sum, item) => sum + item.height, 0), totalHeight)
    ) {
      let y = 0;
      return [
        { ...main, x: 0, y: 0 },
        ...rest.map((item) => {
          const placed = { ...item, x: main.width, y };
          y += item.height;
          return placed;
        }),
      ];
    }
  }

  // Inverse pattern: one full-width section plus side-by-side sections below/above.
  for (let index = 0; index < partitions.length; index += 1) {
    const main = partitions[index];
    const rest = partitions.filter((_, i) => i !== index);
    const remainingHeight = totalHeight - main.height;
    if (
      nearly(main.width, totalWidth) &&
      remainingHeight > 0 &&
      rest.length &&
      rest.every((item) => nearly(item.height, remainingHeight)) &&
      nearly(rest.reduce((sum, item) => sum + item.width, 0), totalWidth)
    ) {
      let x = 0;
      return [
        { ...main, x: 0, y: 0 },
        ...rest.map((item) => {
          const placed = { ...item, x, y: main.height };
          x += item.width;
          return placed;
        }),
      ];
    }
  }

  // Generic proportional fallback: horizontal strips by stated area. It never leaks
  // the raw partition instruction into the document and always stays in bounds.
  const totalArea = partitions.reduce((sum, item) => sum + Math.max(0, item.area), 0) || 1;
  let x = 0;
  return partitions.map((item, index) => {
    const width = index === partitions.length - 1
      ? Math.max(0, totalWidth - x)
      : totalWidth * (Math.max(0, item.area) / totalArea);
    const placed = { ...item, x, y: 0, width, height: totalHeight };
    x += width;
    return placed;
  });
}

function RenderRectangleGeometry({ visual }) {
  const totalWidth = Math.max(1, safeNumber(visual.map.width || visual.map.total_width || 60, 60));
  const totalHeight = Math.max(1, safeNumber(visual.map.height || visual.map.total_height || 30, 30));
  const partitions = parseRectanglePartitions(visual.map.partitions || visual.map.sections || visual.map.areas || "");
  const showAreas = visual.map.showareas == null ? true : isTruthyFlag(visual.map.showareas);
  const showDimensions = visual.map.showdimensions == null ? true : isTruthyFlag(visual.map.showdimensions);
  const color = visual.color || DEFAULT_PALETTE[0];
  const width = 600;
  const height = 340;
  const left = 54;
  const right = 34;
  const top = 48;
  const bottom = 54;
  const drawWidth = width - left - right;
  const drawHeight = height - top - bottom;
  const scale = Math.min(drawWidth / totalWidth, drawHeight / totalHeight);
  const planWidth = totalWidth * scale;
  const planHeight = totalHeight * scale;
  const originX = left + (drawWidth - planWidth) / 2;
  const originY = top + (drawHeight - planHeight) / 2;
  const layout = buildRectanglePartitionLayout(totalWidth, totalHeight, partitions);
  const colors = getSeriesColors(Math.max(layout.length, 1), visual);

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Rectangle / floor-space diagram">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <rect
        x={originX}
        y={originY}
        width={planWidth}
        height={planHeight}
        fill="rgba(37,99,235,0.04)"
        stroke={color}
        strokeWidth="3"
      />

      {layout.map((item, index) => {
        const x = originX + item.x * scale;
        const y = originY + item.y * scale;
        const w = Math.max(1, item.width * scale);
        const h = Math.max(1, item.height * scale);
        const fontSize = Math.max(9, Math.min(13, Math.min(w / 9, h / 4)));
        return (
          <g key={`${item.name}-${index}`}>
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill={colors[index % colors.length]}
              fillOpacity="0.16"
              stroke={colors[index % colors.length]}
              strokeWidth="2"
            />
            <text x={x + w / 2} y={y + h / 2 - 8} textAnchor="middle" fontSize={fontSize} fill={TEXT_DARK} fontWeight="800">
              {String(item.name).slice(0, 28)}
            </text>
            {showDimensions ? (
              <text x={x + w / 2} y={y + h / 2 + 10} textAnchor="middle" fontSize={Math.max(9, fontSize - 1)} fill={TEXT_MUTED}>
                {formatValue(item.width)} ft × {formatValue(item.height)} ft
              </text>
            ) : null}
            {showAreas ? (
              <text x={x + w / 2} y={y + h / 2 + 27} textAnchor="middle" fontSize={Math.max(9, fontSize - 1)} fill={TEXT_MUTED}>
                {formatValue(item.area)} sq ft
              </text>
            ) : null}
          </g>
        );
      })}

      {showDimensions ? (
        <>
          <text x={originX + planWidth / 2} y={originY + planHeight + 26} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="700">
            {formatValue(totalWidth)} ft
          </text>
          <text x={originX - 26} y={originY + planHeight / 2} textAnchor="middle" fontSize="12" fill={TEXT_MUTED} fontWeight="700" transform={`rotate(-90 ${originX - 26} ${originY + planHeight / 2})`}>
            {formatValue(totalHeight)} ft
          </text>
        </>
      ) : null}
    </SvgFrame>
  );
}

function RenderGeometry({ visual }) {
  const type = visual.type;
  const color = visual.color || DEFAULT_PALETTE[0];
  const radius = clamp(safeNumber(visual.map.radius || 54, 54), 16, 90);
  const degrees = safeNumber(visual.map.degrees || visual.map.angle || 45, 45);

  if (type === "circle") {
    return (
      <SvgFrame background={visual.background} label="Circle">
        <circle cx="260" cy="150" r={radius} fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
        <line x1="260" y1="150" x2={260 + radius} y2="150" stroke={color} strokeWidth="3" />
        <text x={260 + radius / 2} y="142" fontSize="12" fill={TEXT_MUTED}>r</text>
      </SvgFrame>
    );
  }

  if (type === "ellipse") {
    return (
      <SvgFrame background={visual.background} label="Ellipse">
        <ellipse cx="260" cy="150" rx="120" ry="70" fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
      </SvgFrame>
    );
  }

  if (type === "square") {
    return (
      <SvgFrame background={visual.background} label="Square">
        <rect x="180" y="70" width="160" height="160" rx="12" fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" />
      </SvgFrame>
    );
  }

  if (type === "rectangle") {
    return <RenderRectangleGeometry visual={visual} />;
  }

  if (type === "triangle" || type === "right_triangle") {
    const points = type === "right_triangle" ? "150,230 370,230 150,70" : "260,55 390,230 130,230";
    return (
      <SvgFrame background={visual.background} label="Triangle">
        <polygon points={points} fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" strokeLinejoin="round" />
        {type === "right_triangle" ? <path d="M150 205 L175 205 L175 230" fill="none" stroke={color} strokeWidth="3" /> : null}
      </SvgFrame>
    );
  }

  if (type === "angle") {
    const rad = (degrees * Math.PI) / 180;
    const cx = 220;
    const cy = 210;
    const x2 = cx + 150 * Math.cos(-rad);
    const y2 = cy + 150 * Math.sin(-rad);
    return (
      <SvgFrame background={visual.background} label="Angle">
        <line x1="90" y1={cy} x2={cx} y2={cy} stroke={color} strokeWidth="5" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={x2} y2={y2} stroke={color} strokeWidth="5" strokeLinecap="round" />
        <path d={`M ${cx - 48} ${cy} A 48 48 0 0 0 ${cx + 48 * Math.cos(-rad)} ${cy + 48 * Math.sin(-rad)}`} fill="none" stroke="#f59e0b" strokeWidth="4" />
        <text x={cx + 58} y={cy - 52} fontSize="18" fill={TEXT_DARK} fontWeight="900">{degrees}°</text>
      </SvgFrame>
    );
  }

  if (type === "parabola" || type === "coordinate_graph" || type === "sine_wave" || type === "cosine_wave") {
    return <RenderCoordinateGraph visual={visual} />;
  }

  if (type === "number_line") {
    return <RenderNumberLine visual={visual} />;
  }

  if (type === "venn") {
    return <RenderVenn visual={visual} />;
  }

  if (type === "flowchart_basic") {
    return <RenderFlowchart visual={visual} />;
  }

  if (type === "polygon") {
    const sides = clamp(safeNumber(visual.map.sides || 5, 5), 3, 12);
    const points = Array.from({ length: sides }, (_, index) => {
      const angle = -Math.PI / 2 + (Math.PI * 2 * index) / sides;
      return `${260 + 105 * Math.cos(angle)},${150 + 105 * Math.sin(angle)}`;
    }).join(" ");
    return (
      <SvgFrame background={visual.background} label="Polygon">
        <polygon points={points} fill="rgba(37,99,235,0.10)" stroke={color} strokeWidth="5" strokeLinejoin="round" />
      </SvgFrame>
    );
  }

  if (type === "vector") {
    const x = safeNumber(visual.map.x || visual.map.x_value || 3, 3);
    const y = safeNumber(visual.map.y || visual.map.y_value || 2, 2);
    const endX = 260 + x * 42;
    const endY = 170 - y * 42;
    return (
      <SvgFrame background={visual.background} label="Vector diagram">
        <defs>
          <marker id="doc-writer-vector-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
            <path d="M0,0 L0,6 L9,3 z" fill={color} />
          </marker>
        </defs>
        <line x1="70" y1="170" x2="450" y2="170" stroke={GRID} strokeWidth="2" />
        <line x1="260" y1="35" x2="260" y2="270" stroke={GRID} strokeWidth="2" />
        <line x1="260" y1="170" x2={endX} y2={endY} stroke={color} strokeWidth="5" markerEnd="url(#doc-writer-vector-arrow)" />
        <text x={Math.min(455, endX + 10)} y={Math.max(24, endY - 8)} fontSize="12" fill={TEXT_DARK} fontWeight="800">({formatValue(x)}, {formatValue(y)})</text>
      </SvgFrame>
    );
  }

  return <RenderCoordinateGraph visual={visual} />;
}

function RenderCoordinateGraph({ visual }) {
  const type = visual.type;
  const color = visual.color || DEFAULT_PALETTE[0];
  const width = 520;
  const height = 300;
  const left = 45;
  const right = 25;
  const top = 24;
  const bottom = 36;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  const x0 = left + plotW / 2;
  const y0 = top + plotH / 2;

  let path = "";

  if (type === "parabola") {
    const pts = [];
    for (let i = 0; i <= 100; i += 1) {
      const t = -2.5 + (5 * i) / 100;
      const x = x0 + t * 60;
      const y = y0 + 80 - t * t * 28;
      pts.push(`${i === 0 ? "M" : "L"} ${x} ${y}`);
    }
    path = pts.join(" ");
  } else if (type === "sine_wave" || type === "cosine_wave") {
    const pts = [];
    for (let i = 0; i <= 160; i += 1) {
      const t = (Math.PI * 4 * i) / 160;
      const x = left + (plotW * i) / 160;
      const y = y0 - (type === "sine_wave" ? Math.sin(t) : Math.cos(t)) * 70;
      pts.push(`${i === 0 ? "M" : "L"} ${x} ${y}`);
    }
    path = pts.join(" ");
  } else {
    path = `M ${left + 20} ${y0 + 60} L ${width - right - 20} ${y0 - 70}`;
  }

  return (
    <SvgFrame background={visual.background} label="Coordinate graph">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      {Array.from({ length: 7 }, (_, i) => {
        const x = left + (plotW * i) / 6;
        return <line key={`x-${i}`} x1={x} y1={top} x2={x} y2={height - bottom} stroke={GRID} />;
      })}
      {Array.from({ length: 5 }, (_, i) => {
        const y = top + (plotH * i) / 4;
        return <line key={`y-${i}`} x1={left} y1={y} x2={width - right} y2={y} stroke={GRID} />;
      })}
      <line x1={left} y1={y0} x2={width - right} y2={y0} stroke={AXIS} strokeWidth="2" />
      <line x1={x0} y1={top} x2={x0} y2={height - bottom} stroke={AXIS} strokeWidth="2" />
      <path d={path} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {visual.equation ? <text x={left + 12} y={top + 18} fontSize="13" fill={TEXT_DARK} fontWeight="800">{visual.equation}</text> : null}
    </SvgFrame>
  );
}

function RenderNumberLine({ visual }) {
  const min = safeNumber(visual.map.min || -10, -10);
  const max = safeNumber(visual.map.max || 10, 10);
  const points = parseNumberList(visual.map.points || visual.map.values || "");
  const width = 520;
  const height = 180;
  const left = 46;
  const right = 46;
  const y = 90;
  const scale = (v) => left + ((v - min) / (max - min || 1)) * (width - left - right);

  return (
    <SvgFrame background={visual.background} viewBox={`0 0 ${width} ${height}`} label="Number line">
      <rect x="0" y="0" width={width} height={height} fill={visual.background || CARD_BG} />
      <line x1={left} y1={y} x2={width - right} y2={y} stroke={AXIS} strokeWidth="3" />
      <polygon points={`${width - right},${y} ${width - right - 10},${y - 6} ${width - right - 10},${y + 6}`} fill={AXIS} />
      {Array.from({ length: 9 }, (_, i) => {
        const v = min + ((max - min) * i) / 8;
        const x = scale(v);
        return (
          <g key={i}>
            <line x1={x} y1={y - 8} x2={x} y2={y + 8} stroke={AXIS} />
            <text x={x} y={y + 28} textAnchor="middle" fontSize="11" fill={TEXT_MUTED}>{formatValue(v)}</text>
          </g>
        );
      })}
      {points.map((p, i) => (
        <circle key={i} cx={scale(p)} cy={y} r="8" fill={DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]} stroke="#fff" strokeWidth="2" />
      ))}
    </SvgFrame>
  );
}

function RenderVenn({ visual }) {
  return (
    <SvgFrame background={visual.background} label="Venn diagram">
      <circle cx="220" cy="150" r="90" fill="rgba(37,99,235,0.28)" stroke="#2563eb" strokeWidth="4" />
      <circle cx="300" cy="150" r="90" fill="rgba(22,163,74,0.28)" stroke="#16a34a" strokeWidth="4" />
      <text x="185" y="150" fontSize="18" fill={TEXT_DARK} fontWeight="900">A</text>
      <text x="335" y="150" fontSize="18" fill={TEXT_DARK} fontWeight="900">B</text>
      <text x="260" y="154" textAnchor="middle" fontSize="16" fill={TEXT_DARK} fontWeight="900">A∩B</text>
    </SvgFrame>
  );
}

function RenderFlowchart({ visual }) {
  return (
    <SvgFrame background={visual.background} label="Flowchart">
      <rect x="60" y="45" width="150" height="48" rx="14" fill="#dbeafe" stroke="#2563eb" strokeWidth="3" />
      <rect x="310" y="45" width="150" height="48" rx="14" fill="#dcfce7" stroke="#16a34a" strokeWidth="3" />
      <rect x="185" y="180" width="150" height="48" rx="14" fill="#fee2e2" stroke="#dc2626" strokeWidth="3" />
      <text x="135" y="75" textAnchor="middle" fontSize="13" fill={TEXT_DARK} fontWeight="800">Input</text>
      <text x="385" y="75" textAnchor="middle" fontSize="13" fill={TEXT_DARK} fontWeight="800">Process</text>
      <text x="260" y="210" textAnchor="middle" fontSize="13" fill={TEXT_DARK} fontWeight="800">Output</text>
      <line x1="210" y1="69" x2="310" y2="69" stroke={AXIS} strokeWidth="3" markerEnd="url(#arrow)" />
      <line x1="385" y1="93" x2="300" y2="180" stroke={AXIS} strokeWidth="3" />
      <defs>
        <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill={AXIS} />
        </marker>
      </defs>
    </SvgFrame>
  );
}

class DocWriterChartRenderBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch() {}

  render() {
    if (this.state.hasError) {
      return (
        <EmptyChart message="This chart could not be rendered from the current saved data. Open Edit to review the chart type and data." />
      );
    }
    return this.props.children;
  }
}

/* =========================
   Router
   ========================= */

function renderVisualPreview(visual) {
  switch (visual.type) {
    case "bar":
      return <RenderBarLike visual={visual} mode="bar" />;
    case "column":
      return <RenderBarLike visual={visual} mode="column" />;
    case "grouped_bar":
      return <RenderBarLike visual={visual} mode="column" grouped />;
    case "stacked_bar":
      return <RenderBarLike visual={visual} mode="column" stacked />;
    case "line":
      return <RenderLine visual={visual} />;
    case "area":
      return <RenderArea visual={visual} />;
    case "stacked_area":
      return <RenderArea visual={visual} stacked />;
    case "pie":
      return <RenderPieDonut visual={visual} />;
    case "donut":
      return <RenderPieDonut visual={visual} donut />;
    case "scatter":
      return <RenderScatterBubble visual={visual} />;
    case "bubble":
      return <RenderScatterBubble visual={visual} bubble />;
    case "histogram":
      return <RenderHistogram visual={visual} />;
    case "bell_curve":
      return <RenderBellCurve visual={visual} />;
    case "confidence_interval":
      return <RenderConfidenceInterval visual={visual} />;
    case "confidence_band":
      return <RenderConfidenceBand visual={visual} />;
    case "kde_density":
      return <RenderKdeDensity visual={visual} />;
    case "roc_curve":
      return <RenderRocCurve visual={visual} />;
    case "precision_recall":
      return <RenderPrecisionRecall visual={visual} />;
    case "violin_plot":
      return <RenderViolinPlot visual={visual} />;
    case "radar":
      return <RenderRadar visual={visual} />;
    case "composed":
      return <RenderComposed visual={visual} />;
    case "pareto":
      return <RenderPareto visual={visual} />;
    case "waterfall":
      return <RenderWaterfall visual={visual} />;
    case "boxplot":
      return <RenderBoxPlot visual={visual} />;
    case "heatmap":
      return <RenderHeatmap visual={visual} />;
    case "funnel":
      return <RenderFunnel visual={visual} />;
    case "gauge":
      return <RenderGauge visual={visual} />;
    case "progress":
      return <RenderProgress visual={visual} />;
    case "timeline":
      return <RenderTimeline visual={visual} />;
    case "gantt_basic":
      return <RenderGantt visual={visual} />;
    case "candlestick_basic":
      return <RenderCandlestick visual={visual} />;
    case "treemap_basic":
      return <RenderTreemapBasic visual={visual} />;
    case "slope":
      return <RenderSlope visual={visual} />;
    case "lollipop":
      return <RenderLollipop visual={visual} />;
    case "dotplot":
      return <RenderDotPlot visual={visual} />;
    case "circle":
    case "ellipse":
    case "square":
    case "rectangle":
    case "triangle":
    case "right_triangle":
    case "angle":
    case "parabola":
    case "coordinate_graph":
    case "sine_wave":
    case "cosine_wave":
    case "number_line":
    case "venn":
    case "flowchart_basic":
    case "polygon":
    case "vector":
      return <RenderGeometry visual={visual} />;
    default:
      // Smart fallback: if it has points, render scatter; if series, line; otherwise column.
      if (visual.map.points) return <RenderScatterBubble visual={{ ...visual, type: "scatter" }} />;
      if (visual.map.series) return <RenderLine visual={{ ...visual, type: "line" }} />;
      return <RenderBarLike visual={{ ...visual, type: "column" }} mode="column" />;
  }
}

function fieldsForChartType(type, map = {}) {
  const t = normalizeType(type);
  let keys = [];
  if (["bar", "column", "grouped_bar", "stacked_bar", "line", "area", "stacked_area", "pie", "donut", "radar", "composed"].includes(t)) {
    keys = ["labels", "values", "series", "colors"];
  } else if (["scatter", "bubble", "roc_curve", "precision_recall"].includes(t)) {
    keys = ["points", "x_values", "y_values", "colors"];
  } else if (["histogram", "bell_curve", "kde_density", "violin_plot", "boxplot"].includes(t)) {
    keys = ["values", "data", "colors"];
  } else if (["confidence_interval", "confidence_band"].includes(t)) {
    keys = ["labels", "low", "mean", "expected", "high", "colors"];
  } else if (["waterfall", "pareto", "funnel", "slope", "lollipop", "dotplot", "treemap_basic"].includes(t)) {
    keys = ["data", "labels", "values", "colors"];
  } else if (t === "heatmap") {
    keys = ["rows", "cols", "values", "colors"];
  } else if (t === "timeline") {
    keys = ["events", "data", "colors"];
  } else if (t === "gantt_basic") {
    keys = ["tasks", "data", "colors"];
  } else if (t === "candlestick_basic") {
    keys = ["candles", "data", "colors"];
  } else if (t === "rectangle") {
    keys = ["width", "height", "partitions", "colors"];
  } else if (["parabola", "coordinate_graph", "sine_wave", "cosine_wave"].includes(t)) {
    keys = ["equation", "points", "x_values", "y_values", "colors"];
  } else {
    keys = ["data", "values", "points", "equation", "colors"];
  }

  Object.keys(map).forEach((key) => {
    if (DOC_WRITER_EDITABLE_FIELDS.some(([candidate]) => candidate === key) && !keys.includes(key)) {
      keys.push(key);
    }
  });
  const keepRawColorField = ["heatmap", "rectangle", "polygon", "venn", "flowchart_basic"].includes(t);
  return keepRawColorField ? keys : keys.filter((key) => key !== "colors");
}

function editorFieldLabel(key) {
  return DOC_WRITER_EDITABLE_FIELDS.find(([field]) => field === key)?.[1] || titleCase(key);
}

function DocWriterChartEditDialog({
  open,
  ownerDocument,
  draft,
  setDraft,
  size,
  setSize,
  alignment,
  setAlignment,
  maxWidth,
  onCancel,
  onSave,
  onDelete,
}) {
  const [widthInput, setWidthInput] = useState(() => String(Math.round(size?.width || DOC_WRITER_DEFAULT_WIDTH)));
  const [heightInput, setHeightInput] = useState(() => String(Math.round(size?.height || 390)));
  const [legendResetNonce, setLegendResetNonce] = useState(0);
  const [draftUndoStack, setDraftUndoStack] = useState([]);
  const [draftRedoStack, setDraftRedoStack] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [activeSupplemental, setActiveSupplemental] = useState({ kind: "", id: "" });
  const [previewResizeHover, setPreviewResizeHover] = useState("");
  const draftStateRef = useRef({ draft: { ...(draft || {}) }, size: { ...(size || {}) }, alignment });
  const previewResizeRef = useRef(null);
  const DRAFT_HISTORY_LIMIT = 120;

  useEffect(() => {
    if (!open) return;
    draftStateRef.current = { draft: { ...(draft || {}) }, size: { ...(size || {}) }, alignment };
    setDraftUndoStack([]);
    setDraftRedoStack([]);
    setDeleteConfirmOpen(false);
    setActiveSupplemental({ kind: "", id: "" });
    setWidthInput(String(Math.round(size?.width || DOC_WRITER_DEFAULT_WIDTH)));
    setHeightInput(String(Math.round(size?.height || 390)));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setWidthInput(String(Math.round(size?.width || DOC_WRITER_DEFAULT_WIDTH)));
    draftStateRef.current = { ...draftStateRef.current, size: { ...(size || {}) } };
  }, [open, size?.width]);

  useEffect(() => {
    if (!open) return;
    setHeightInput(String(Math.round(size?.height || 390)));
    draftStateRef.current = { ...draftStateRef.current, size: { ...(size || {}) } };
  }, [open, size?.height]);

  useEffect(() => {
    if (!open) return;
    draftStateRef.current = { ...draftStateRef.current, draft: { ...(draft || {}) } };
  }, [open, draft]);

  useEffect(() => {
    if (!open) return;
    draftStateRef.current = { ...draftStateRef.current, alignment };
  }, [open, alignment]);

  if (!open || !ownerDocument?.body) return null;

  const previewVisual = parseVisualInstruction(serializeInstructionMap(draft));
  const dataFields = fieldsForChartType(draft.type, draft);
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: TEXT_DARK,
    background: "#ffffff",
    outline: "none",
  };
  const labelStyle = {
    display: "grid",
    gap: 5,
    fontSize: 12,
    fontWeight: 750,
    color: TEXT_MUTED,
  };
  const buttonStyle = {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "7px 10px",
    background: "#ffffff",
    color: TEXT_DARK,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  };
  const sectionStyle = {
    border: "1px solid rgba(148,163,184,0.30)",
    borderRadius: 12,
    padding: 13,
    background: "#ffffff",
  };
  const sectionTitleStyle = { fontSize: 12, fontWeight: 900, color: TEXT_DARK, marginBottom: 10 };
  const rowGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 };
  const checkStyle = { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: TEXT_DARK, fontWeight: 750 };

  const snapshotDraftState = () => ({
    draft: { ...(draftStateRef.current?.draft || {}) },
    size: { ...(draftStateRef.current?.size || {}) },
    alignment: normalizeDocWriterAlignment(draftStateRef.current?.alignment || "center"),
  });
  const snapshotSignature = (snapshot) => {
    try { return JSON.stringify(snapshot); } catch (_) { return String(Date.now()); }
  };
  const captureDraftCheckpoint = () => {
    const snapshot = snapshotDraftState();
    const signature = snapshotSignature(snapshot);
    setDraftUndoStack((current) => {
      const last = current[current.length - 1];
      if (last && snapshotSignature(last) === signature) return current;
      return [...current, snapshot].slice(-DRAFT_HISTORY_LIMIT);
    });
    setDraftRedoStack([]);
  };
  const restoreDraftSnapshot = (snapshot) => {
    if (!snapshot) return;
    const next = {
      draft: { ...(snapshot.draft || {}) },
      size: { ...(snapshot.size || {}) },
      alignment: normalizeDocWriterAlignment(snapshot.alignment),
    };
    draftStateRef.current = next;
    setDraft(next.draft);
    setSize(next.size);
    setAlignment(next.alignment);
    setWidthInput(String(Math.round(next.size.width || DOC_WRITER_DEFAULT_WIDTH)));
    setHeightInput(String(Math.round(next.size.height || 390)));
  };
  const undoDraftChange = () => {
    if (!draftUndoStack.length) return;
    const previous = draftUndoStack[draftUndoStack.length - 1];
    const current = snapshotDraftState();
    setDraftUndoStack(draftUndoStack.slice(0, -1));
    setDraftRedoStack((stack) => [...stack, current].slice(-DRAFT_HISTORY_LIMIT));
    restoreDraftSnapshot(previous);
  };
  const redoDraftChange = () => {
    if (!draftRedoStack.length) return;
    const next = draftRedoStack[draftRedoStack.length - 1];
    const current = snapshotDraftState();
    setDraftRedoStack(draftRedoStack.slice(0, -1));
    setDraftUndoStack((stack) => [...stack, current].slice(-DRAFT_HISTORY_LIMIT));
    restoreDraftSnapshot(next);
  };
  const replaceDraft = (nextDraft, { history = true } = {}) => {
    const next = { ...(nextDraft || {}) };
    let changed = true;
    try { changed = JSON.stringify(next) !== JSON.stringify(draftStateRef.current?.draft || {}); } catch (_) {}
    if (!changed) return;
    if (history) captureDraftCheckpoint();
    draftStateRef.current = { ...draftStateRef.current, draft: next };
    setDraft(next);
  };
  const patch = (key, value, options = {}) => replaceDraft({ ...(draftStateRef.current?.draft || {}), [key]: value }, options);
  const applyAlignment = (nextAlignment, { history = true } = {}) => {
    const normalized = normalizeDocWriterAlignment(nextAlignment);
    if (normalized === normalizeDocWriterAlignment(draftStateRef.current?.alignment)) return;
    if (history) captureDraftCheckpoint();
    draftStateRef.current = { ...draftStateRef.current, alignment: normalized };
    setAlignment(normalized);
  };
  const applySize = (nextSize, { history = true } = {}) => {
    const current = draftStateRef.current?.size || size;
    const next = {
      width: sanitizeDocWriterDimension(nextSize?.width, current.width, DOC_WRITER_MIN_WIDTH, maxWidth),
      height: sanitizeDocWriterDimension(nextSize?.height, current.height, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT),
    };
    if (next.width === current.width && next.height === current.height) return;
    if (history) captureDraftCheckpoint();
    draftStateRef.current = { ...draftStateRef.current, size: next };
    setSize(next);
    setWidthInput(String(Math.round(next.width)));
    setHeightInput(String(Math.round(next.height)));
  };

  const aspectLocked = boolSetting(draft.lock_aspect_ratio, false);
  const sizeFromDimension = (kind, value) => {
    if (!aspectLocked) return { ...size, [kind]: value };
    if (kind === "width") {
      const scale = clampChartScale(size.width, size.height, value / Math.max(1, size.width), maxWidth);
      return { width: Math.round(size.width * scale), height: Math.round(size.height * scale) };
    }
    const scale = clampChartScale(size.width, size.height, value / Math.max(1, size.height), maxWidth);
    return { width: Math.round(size.width * scale), height: Math.round(size.height * scale) };
  };

  const setPreset = (name) => {
    const presets = {
      small: { width: Math.min(maxWidth, 420), height: 300 },
      medium: { width: Math.min(maxWidth, 520), height: 360 },
      large: { width: Math.min(maxWidth, 620), height: 420 },
      full: { width: maxWidth, height: Math.max(420, size.height) },
    };
    if (!presets[name]) return;
    if (!aspectLocked) {
      applySize(presets[name]);
      return;
    }
    const desiredWidth = presets[name].width;
    const scale = clampChartScale(size.width, size.height, desiredWidth / Math.max(1, size.width), maxWidth);
    applySize({ width: Math.round(size.width * scale), height: Math.round(size.height * scale) });
  };

  const commitDimensionInput = (kind) => {
    const isWidth = kind === "width";
    const raw = isWidth ? widthInput : heightInput;
    const currentValue = isWidth ? size.width : size.height;
    const min = isWidth ? DOC_WRITER_MIN_WIDTH : DOC_WRITER_MIN_HEIGHT;
    const max = isWidth ? maxWidth : DOC_WRITER_MAX_HEIGHT;
    const resolved = sanitizeDocWriterDimension(raw, currentValue, min, max);
    applySize(sizeFromDimension(kind, resolved));
  };

  const handleDimensionTyping = (kind, raw) => {
    if (kind === "width") setWidthInput(raw);
    else setHeightInput(raw);
    if (String(raw).trim() === "") return;
    const parsed = Number.parseFloat(raw);
    const min = kind === "width" ? DOC_WRITER_MIN_WIDTH : DOC_WRITER_MIN_HEIGHT;
    const max = kind === "width" ? maxWidth : DOC_WRITER_MAX_HEIGHT;
    if (Number.isFinite(parsed) && parsed >= min && parsed <= max) {
      applySize(sizeFromDimension(kind, Math.round(parsed)));
    }
  };

  const resetLegendPosition = () => {
    replaceDraft({ ...(draftStateRef.current?.draft || {}), legend_align: "center", legend_offset: "0,0" });
    setLegendResetNonce((current) => current + 1);
  };

  const getColorTargets = () => {
    const t = normalizeType(draft.type || previewVisual.type);
    const base = normalizeCategoryData(draft);
    const parsedSeries = parseSeries(draft.series || draft.datasets || "");
    if (["pie", "donut", "funnel", "treemap_basic", "waterfall", "lollipop", "dotplot"].includes(t)) {
      return base.map((row, index) => row.name || `Item ${index + 1}`);
    }
    if (["bar", "column"].includes(t) && !parsedSeries.length && base.length > 1) {
      return base.map((row, index) => row.name || `Bar ${index + 1}`);
    }
    if (parsedSeries.length) return parsedSeries.map((series, index) => series.name || `Series ${index + 1}`);
    return [draft.value_label || previewVisual.yLabel || "Series 1"];
  };

  const colorTargets = getColorTargets();
  const currentColors = parseColorList(draft.colors || draft.color || "");
  const previewCategoryLabels = (() => {
    const multi = normalizeMultiSeriesData(draft);
    if (multi.labels?.length) return multi.labels;
    return normalizeCategoryData(draft).map((row, index) => row?.name || `Item ${index + 1}`);
  })();
  const hiddenCategorySet = parseHiddenCategoryIndices(draft.hidden_category_indices || "");

  const commitDraftLabelOffset = (labelKey, offset) => {
    const offsets = parseLabelOffsets(draft.label_offsets || "");
    offsets[labelKey] = { dx: Math.round(safeNumber(offset?.dx, 0)), dy: Math.round(safeNumber(offset?.dy, 0)) };
    patch("label_offsets", serializeLabelOffsets(offsets));
  };
  const commitDraftCategoryOffset = (index, offset) => {
    const offsets = parseCategoryOffsets(draft.category_label_offsets || "");
    offsets[index] = { dx: Math.round(safeNumber(offset?.dx, 0)), dy: Math.round(safeNumber(offset?.dy, 0)) };
    patch("category_label_offsets", serializeCategoryOffsets(offsets));
  };
  const commitDraftLegendOffset = (offset) => patch("legend_offset", serializeLegendOffset(offset));
  const setCategoryHidden = (index, hidden) => {
    const next = new Set(parseHiddenCategoryIndices(draft.hidden_category_indices || ""));
    if (hidden) next.add(index); else next.delete(index);
    patch("hidden_category_indices", serializeHiddenCategoryIndices(next));
  };

  const topTitles = parseChartSupplementalLines(draft.top_titles, "title");
  const bottomNotes = parseChartSupplementalLines(draft.bottom_notes, "note");
  const activeLines = activeSupplemental.kind === "note" ? bottomNotes : topTitles;
  const activeSupplementalLine = activeLines.find((line) => line.id === activeSupplemental.id) || null;
  const updateSupplementalLine = (kind, lineId, linePatch) => {
    const key = kind === "note" ? "bottom_notes" : "top_titles";
    const lines = kind === "note" ? bottomNotes : topTitles;
    let changed = false;
    const next = lines.map((line) => {
      if (line.id !== lineId) return line;
      changed = true;
      return normalizeChartSupplementalLine({ ...line, ...(linePatch || {}) }, kind, 0);
    });
    if (changed) patch(key, serializeChartSupplementalLines(next, kind));
  };
  const focusSupplementalPreviewLine = (kind, lineId) => {
    const selector = `[data-docwriter-chart-supplemental-kind="${kind}"][data-docwriter-chart-supplemental-id="${lineId}"]`;
    const focusLine = () => {
      const node = ownerDocument?.querySelector?.(selector);
      if (!node) return false;
      try { node.scrollIntoView?.({ block: "nearest", inline: "nearest" }); } catch (_) {}
      try { node.focus?.({ preventScroll: true }); } catch (_) {
        try { node.focus?.(); } catch (_) {}
      }
      try {
        const selection = ownerDocument?.getSelection?.();
        if (selection) {
          const range = ownerDocument.createRange();
          range.selectNodeContents(node);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (_) {}
      return true;
    };

    const view = ownerDocument?.defaultView;
    const firstFrame = () => {
      if (focusLine()) return;
      try {
        view?.requestAnimationFrame?.(() => {
          if (focusLine()) return;
          view?.setTimeout?.(focusLine, 0);
        });
      } catch (_) {
        try { view?.setTimeout?.(focusLine, 0); } catch (_) {}
      }
    };

    try { view?.requestAnimationFrame?.(firstFrame); } catch (_) {
      try { view?.setTimeout?.(firstFrame, 0); } catch (_) {}
    }
  };

  const addSupplementalLine = (kind) => {
    const key = kind === "note" ? "bottom_notes" : "top_titles";
    const lines = kind === "note" ? bottomNotes : topTitles;
    const nextLine = normalizeChartSupplementalLine({
      id: createChartSupplementalId(kind === "note" ? "noteline" : "titleline"),
      text: "",
      alignment: "center",
    }, kind, lines.length);
    patch(key, serializeChartSupplementalLines([...lines, nextLine], kind));
    setActiveSupplemental({ kind, id: nextLine.id });
    focusSupplementalPreviewLine(kind, nextLine.id);
  };
  const removeSupplementalLine = (kind) => {
    const key = kind === "note" ? "bottom_notes" : "top_titles";
    const lines = kind === "note" ? bottomNotes : topTitles;
    if (!lines.length) return;
    const activeMatches = activeSupplemental.kind === kind && lines.some((line) => line.id === activeSupplemental.id);
    const targetId = activeMatches ? activeSupplemental.id : lines[lines.length - 1].id;
    patch(key, serializeChartSupplementalLines(lines.filter((line) => line.id !== targetId), kind));
    setActiveSupplemental({ kind: "", id: "" });
  };

  const dialogPreviewVisual = {
    ...previewVisual,
    chartId: "dialog-preview",
    width: size.width,
    height: size.height,
    alignment,
    background: normalizeColor(draft.background, CARD_BG) || CARD_BG,
    legendPosition: normalizeLegendPosition(draft.legend_position),
    legendAlign: normalizeTextAlignment(draft.legend_align, "center"),
    legendColor: cssColor(draft.legend_color, TEXT_MUTED),
    canEditLabels: true,
    onCommitLabelOffset: commitDraftLabelOffset,
    canEditCategoryLabels: true,
    onCommitCategoryOffset: commitDraftCategoryOffset,
    canEditLegend: true,
    onCommitLegendOffset: commitDraftLegendOffset,
    legendResetNonce,
  };

  const setColorAt = (index, colorValue) => {
    const next = Array.from({ length: Math.max(colorTargets.length, currentColors.length, index + 1) }, (_, i) =>
      currentColors[i] || DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]
    );
    next[index] = colorValue;
    patch("colors", next.join(","));
  };

  const colorEditor = (label, key, fallback) => {
    const raw = String(draft[key] || fallback || "");
    return (
      <label style={labelStyle}>
        {label}
        <div style={{ display: "grid", gridTemplateColumns: "42px minmax(0,1fr)", gap: 7 }}>
          <input type="color" value={safeColorPickerValue(raw, safeColorPickerValue(fallback || "#2563eb"))} onChange={(event) => patch(key, event.target.value)} style={{ width: 42, height: 36, padding: 2, border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", cursor: "pointer" }} />
          <input value={raw} onChange={(event) => patch(key, event.target.value)} placeholder="#2563eb, rgb(...), hsl(...), transparent" style={inputStyle} />
        </div>
      </label>
    );
  };

  const stopPortalEvent = (event) => event.stopPropagation();
  const headingControl = ({ label, textKey, showKey, alignKey, colorKey, fontSizeKey, orderKey, defaultOrder, fallbackColor, defaultFontSize, placeholder }) => (
    <div style={{ ...sectionStyle, padding: 11 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <strong style={{ fontSize: 12, color: TEXT_DARK }}>{label}</strong>
        <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft[showKey], label !== "Chart type")} onChange={(event) => patch(showKey, event.target.checked ? "true" : "false")} />Show</label>
      </div>
      {textKey ? (
        <input value={draft[textKey] || ""} onChange={(event) => patch(textKey, event.target.value)} placeholder={placeholder || label} style={inputStyle} />
      ) : (
        <div style={{ ...inputStyle, background: "#f8fafc", color: TEXT_MUTED }}>{DOC_WRITER_CHART_LABELS[normalizeType(draft.type)] || titleCase(draft.type)}</div>
      )}
      <div style={{ ...rowGrid, marginTop: 8 }}>
        <label style={labelStyle}>Alignment<select value={normalizeTextAlignment(draft[alignKey], "center")} onChange={(event) => patch(alignKey, event.target.value)} style={inputStyle}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
        <label style={labelStyle}>Row<select value={settingOrder(draft[orderKey], defaultOrder)} onChange={(event) => patch(orderKey, event.target.value)} style={inputStyle}><option value="1">Row 1</option><option value="2">Row 2</option><option value="3">Row 3</option></select></label>
        <label style={labelStyle}>Font size<input type="number" min="9" max="24" value={draft[fontSizeKey] || String(defaultFontSize)} onChange={(event) => patch(fontSizeKey, event.target.value)} style={inputStyle} /></label>
        {colorEditor("Text color", colorKey, fallbackColor)}
      </div>
    </div>
  );

  const previewResizeHandles = [
    { handle: "nw", glyph: "↖", cursor: "nwse-resize", vertical: "top", horizontal: "left" },
    { handle: "ne", glyph: "↗", cursor: "nesw-resize", vertical: "top", horizontal: "right" },
    { handle: "sw", glyph: "↙", cursor: "nesw-resize", vertical: "bottom", horizontal: "left" },
    { handle: "se", glyph: "↘", cursor: "nwse-resize", vertical: "bottom", horizontal: "right" },
  ];
  const previewEdgeHandles = [
    { handle: "n", edge: "top", cursor: "ns-resize" },
    { handle: "s", edge: "bottom", cursor: "ns-resize" },
  ];

  const beginPreviewResize = (event, handle = "se") => {
    if (!event || event.button !== 0 || previewResizeRef.current) return;
    const view = ownerDocument?.defaultView || (typeof window !== "undefined" ? window : null);
    if (!view) return;
    event.preventDefault();
    event.stopPropagation();

    const current = snapshotDraftState();
    const startWidth = current.size.width;
    const startHeight = current.size.height;
    const startInset = clamp(safeNumber(current.draft.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT);
    const locked = boolSetting(current.draft.lock_aspect_ratio, false);
    const resizeHorizontal = handle.includes("e") || handle.includes("w");
    const resizeFromWest = handle.includes("w");
    const resizeFromNorth = handle.includes("n");
    const resizeFromTop = handle === "n" || resizeFromNorth;
    const state = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      startHeight,
      startInset,
      locked,
      historyCaptured: false,
      latestSize: { ...current.size },
      latestInset: startInset,
    };
    previewResizeRef.current = state;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}

    const move = (moveEvent) => {
      const active = previewResizeRef.current;
      if (!active) return;
      const dx = moveEvent.clientX - state.startX;
      const dy = moveEvent.clientY - state.startY;
      if (!dx && !dy) return;
      if (!state.historyCaptured) {
        captureDraftCheckpoint();
        state.historyCaptured = true;
      }

      let nextWidth = startWidth;
      let nextHeight = startHeight;
      if (locked) {
        let desiredScale = 1;
        if (resizeHorizontal) {
          const widthCandidate = startWidth + (resizeFromWest ? -dx : dx);
          const heightCandidate = startHeight + (resizeFromNorth ? -dy : dy);
          const sx = widthCandidate / Math.max(1, startWidth);
          const sy = heightCandidate / Math.max(1, startHeight);
          desiredScale = Math.abs(sx - 1) >= Math.abs(sy - 1) ? sx : sy;
        } else {
          const heightCandidate = startHeight + (resizeFromNorth ? -dy : dy);
          desiredScale = heightCandidate / Math.max(1, startHeight);
        }
        const scale = clampChartScale(startWidth, startHeight, desiredScale, maxWidth);
        nextWidth = Math.round(startWidth * scale);
        nextHeight = Math.round(startHeight * scale);
      } else {
        if (resizeHorizontal) {
          nextWidth = sanitizeDocWriterDimension(startWidth + (resizeFromWest ? -dx : dx), startWidth, DOC_WRITER_MIN_WIDTH, maxWidth);
        }
        nextHeight = sanitizeDocWriterDimension(startHeight + (resizeFromNorth ? -dy : dy), startHeight, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT);
      }

      const actualHeightDelta = startHeight - nextHeight;
      const nextInset = resizeFromTop
        ? clamp(startInset + actualHeightDelta, 0, DOC_WRITER_MAX_HEIGHT)
        : startInset;
      const nextSize = { width: nextWidth, height: nextHeight };
      state.latestSize = nextSize;
      state.latestInset = nextInset;
      draftStateRef.current = {
        ...draftStateRef.current,
        size: nextSize,
        draft: { ...(draftStateRef.current?.draft || {}), doc_top_edge_inset: String(Math.round(nextInset)) },
      };
      setSize(nextSize);
      setDraft(draftStateRef.current.draft);
      setWidthInput(String(nextWidth));
      setHeightInput(String(nextHeight));
    };

    const finish = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", finish, true);
      previewResizeRef.current = null;
    };
    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", finish, true);
  };

  const previewTopInset = clamp(safeNumber(draft.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT);
  const previewInnerWidth = Math.max(DOC_WRITER_MIN_WIDTH, Math.round(size.width));

  return createPortal(
    <div
      className="doc-writer-chart-edit-overlay"
      {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
      onPointerDown={stopPortalEvent}
      onMouseDown={stopPortalEvent}
      onClick={stopPortalEvent}
      onKeyDown={(event) => {
        event.stopPropagation();
        const accelerator = event.ctrlKey || event.metaKey;
        const key = String(event.key || "").toLowerCase();
        if (accelerator && key === "z") {
          event.preventDefault();
          if (event.shiftKey) redoDraftChange(); else undoDraftChange();
          return;
        }
        if (accelerator && key === "y") {
          event.preventDefault();
          redoDraftChange();
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          if (deleteConfirmOpen) setDeleteConfirmOpen(false); else onCancel?.();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483000,
        background: "rgba(15,23,42,0.30)",
        display: "grid",
        placeItems: "center",
        padding: 20,
        boxSizing: "border-box",
      }}
    >
      <style>{`
        @media (max-width: 920px) {
          .doc-writer-chart-edit-two-pane { grid-template-columns: minmax(0, 1fr) !important; }
          .doc-writer-chart-edit-preview-pane { position: relative !important; top: auto !important; }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit chart"
        onPointerDown={stopPortalEvent}
        onMouseDown={stopPortalEvent}
        onClick={stopPortalEvent}
        style={{
          width: "min(1180px, 96vw)",
          maxHeight: "90vh",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#ffffff",
          borderRadius: 18,
          boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
          border: "1px solid rgba(148,163,184,0.35)",
        }}
      >
        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "17px 18px 15px", borderBottom: "1px solid rgba(148,163,184,0.22)", background: "#ffffff" }}>
          <div>
            <div style={{ fontWeight: 900, color: TEXT_DARK, fontSize: 16 }}>Edit chart</div>
            <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>Edit chart data and presentation. The document chart changes only when you choose Save changes.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
            <button type="button" style={{ ...buttonStyle, minWidth: 66, opacity: draftUndoStack.length ? 1 : 0.48, cursor: draftUndoStack.length ? "pointer" : "default" }} disabled={!draftUndoStack.length} onClick={undoDraftChange} title="Undo the last unsaved chart-preview change">Undo</button>
            <button type="button" style={{ ...buttonStyle, minWidth: 66, opacity: draftRedoStack.length ? 1 : 0.48, cursor: draftRedoStack.length ? "pointer" : "default" }} disabled={!draftRedoStack.length} onClick={redoDraftChange} title="Redo the last unsaved chart-preview change">Redo</button>
            <button type="button" onClick={onCancel} style={buttonStyle}>Close</button>
          </div>
        </div>

        <div className="doc-writer-chart-edit-scroll-body" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarGutter: "stable", padding: "16px 18px 18px", background: "#ffffff" }}>
          <div className="doc-writer-chart-edit-two-pane" style={{ display: "grid", gridTemplateColumns: "minmax(330px, 410px) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
            <div style={{ minWidth: 0, display: "grid", gap: 13 }}>
<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Chart type and headings</div>
  <label style={labelStyle}>
    Chart type
    <select value={draft.type || "column"} onChange={(event) => patch("type", event.target.value)} style={inputStyle}>
      {DOC_WRITER_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
  </label>
  <div style={{ display: "grid", gap: 9, marginTop: 10 }}>
    {headingControl({ label: "Title", textKey: "title", showKey: "show_title", alignKey: "title_align", colorKey: "title_color", fontSizeKey: "title_font_size", orderKey: "title_order", defaultOrder: 1, fallbackColor: TEXT_DARK, defaultFontSize: 14, placeholder: "Chart title" })}
    {headingControl({ label: "Subtitle", textKey: "subtitle", showKey: "show_subtitle", alignKey: "subtitle_align", colorKey: "subtitle_color", fontSizeKey: "subtitle_font_size", orderKey: "subtitle_order", defaultOrder: 2, fallbackColor: TEXT_MUTED, defaultFontSize: 12, placeholder: "Optional subtitle" })}
    {headingControl({ label: "Chart type", textKey: null, showKey: "show_chart_type", alignKey: "chart_type_align", colorKey: "chart_type_color", fontSizeKey: "chart_type_font_size", orderKey: "chart_type_order", defaultOrder: 3, fallbackColor: TEXT_MUTED, defaultFontSize: 11 })}
  </div>
</section>


<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Top titles and bottom notes</div>
  <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45, marginBottom: 10 }}>
    Add as many independent lines as needed. Click a title/note line in the preview to make it active, then use the formatting controls below. These remain draft-only until Save changes.
  </div>
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
    <button type="button" style={buttonStyle} onClick={() => addSupplementalLine("title")}>Add top title line</button>
    <button type="button" style={buttonStyle} onClick={() => removeSupplementalLine("title")}>Remove active top line</button>
    <button type="button" style={buttonStyle} onClick={() => addSupplementalLine("note")}>Add bottom note line</button>
    <button type="button" style={buttonStyle} onClick={() => removeSupplementalLine("note")}>Remove active bottom line</button>
  </div>
  <div style={{ marginTop: 9, fontSize: 11, color: TEXT_MUTED }}>
    Click a top title or bottom note line in the preview to format it.
  </div>
  {activeSupplementalLine ? (
    <div style={{ ...rowGrid, marginTop: 10 }}>
      <label style={labelStyle}>
        Active line alignment
        <select
          value={activeSupplementalLine.alignment}
          onChange={(event) => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { alignment: event.target.value })}
          style={inputStyle}
        >
          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
        </select>
      </label>
      <label style={labelStyle}>
        Active line font size (px)
        <input
          type="number"
          min="3"
          max="96"
          value={activeSupplementalLine.fontSizePx}
          onChange={(event) => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { fontSizePx: clamp(safeNumber(event.target.value, activeSupplementalLine.fontSizePx), 3, 96) })}
          style={inputStyle}
        />
      </label>
      <div style={{ display: "flex", alignItems: "end", gap: 8, flexWrap: "wrap" }}>
        <button type="button" style={{ ...buttonStyle, background: activeSupplementalLine.bold ? "#eef2ff" : "#ffffff" }} onClick={() => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { bold: !activeSupplementalLine.bold })}>Bold</button>
        <button type="button" style={{ ...buttonStyle, background: activeSupplementalLine.italic ? "#eef2ff" : "#ffffff" }} onClick={() => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { italic: !activeSupplementalLine.italic })}>Italic</button>
        <button type="button" style={{ ...buttonStyle, background: activeSupplementalLine.underline ? "#eef2ff" : "#ffffff" }} onClick={() => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { underline: !activeSupplementalLine.underline })}>Underline</button>
      </div>
    </div>
  ) : null}
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Legend</div>
  <div style={rowGrid}>
    <label style={labelStyle}>
      Position
      <select value={normalizeLegendPosition(draft.legend_position)} onChange={(event) => patch("legend_position", event.target.value)} style={inputStyle}>
        <option value="bottom">Bottom</option><option value="top">Top</option><option value="none">Hidden</option>
      </select>
    </label>
    <label style={labelStyle}>
      Alignment
      <select value={normalizeTextAlignment(draft.legend_align, "center")} onChange={(event) => patch("legend_align", event.target.value)} style={inputStyle}>
        <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
      </select>
    </label>
    <label style={labelStyle}>
      Font size
      <input type="number" min="9" max="18" value={draft.legend_font_size || "12"} onChange={(event) => patch("legend_font_size", event.target.value)} style={inputStyle} />
    </label>
    <label style={labelStyle}>
      Legend content
      <select value={normalizeLegendContent(draft.legend_content)} onChange={(event) => patch("legend_content", event.target.value)} style={inputStyle}>
        <option value="name">Name / color only</option>
        <option value="name_value">Name / color + value</option>
      </select>
    </label>
    {colorEditor("Legend text color", "legend_color", TEXT_MUTED)}
  </div>
  <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
    <button type="button" onClick={resetLegendPosition} style={buttonStyle}>Reset legend position</button>
    <span style={{ fontSize: 11, color: TEXT_MUTED }}>Default: bottom and centered to the plot area. In the live preview, drag the legend anywhere inside the chart and Save preserves the position.</span>
  </div>
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Labels / categories</div>
  <div style={rowGrid}>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.show_category_labels, true)} onChange={(event) => patch("show_category_labels", event.target.checked ? "true" : "false")} />Show category labels</label>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.category_label_pointer, false)} onChange={(event) => patch("category_label_pointer", event.target.checked ? "true" : "false")} />Show pointer when a category label is moved</label>
    <label style={labelStyle}>Font size<input type="number" min="7" max="28" value={draft.category_label_font_size || draft.x_tick_font_size || "12"} onChange={(event) => patch("category_label_font_size", event.target.value)} style={inputStyle} /></label>
    <label style={labelStyle}>Rotation (-70° to 70°)<input type="number" min="-70" max="70" value={draft.category_label_angle || "0"} onChange={(event) => patch("category_label_angle", event.target.value)} style={inputStyle} /></label>
    {colorEditor("Category label color", "category_label_color", draft.x_tick_color || TEXT_MUTED)}
  </div>
  <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
    <button type="button" onClick={() => patch("category_label_offsets", "")} style={buttonStyle}>Reset moved categories</button>
    <button type="button" onClick={() => patch("hidden_category_indices", "")} style={buttonStyle}>Show all categories</button>
    <span style={{ fontSize: 11, color: TEXT_MUTED }}>Drag a visible category label directly in the live preview. Long labels wrap instead of being silently truncated.</span>
  </div>
  {previewCategoryLabels.length ? (
    <div style={{ marginTop: 11, display: "grid", gap: 6, maxHeight: 170, overflowY: "auto", paddingRight: 4 }}>
      {previewCategoryLabels.map((label, index) => (
        <label key={`category-control-${index}-${label}`} style={{ ...checkStyle, justifyContent: "space-between", border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 9px", background: "#f8fafc" }}>
          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{String(label || `Item ${index + 1}`)}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={!hiddenCategorySet.has(index)} onChange={(event) => setCategoryHidden(index, !event.target.checked)} /> Show
          </span>
        </label>
      ))}
    </div>
  ) : null}
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Axes and grid</div>
  <div style={{ display: "grid", gap: 11 }}>
    <div style={{ ...rowGrid, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
      <label style={labelStyle}>X-axis label<input value={draft.x_label || ""} onChange={(event) => patch("x_label", event.target.value)} style={inputStyle} /></label>
      <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.show_x_axis_label, true)} onChange={(event) => patch("show_x_axis_label", event.target.checked ? "true" : "false")} />Show X-axis label</label>
      <label style={labelStyle}>Position<select value={normalizeXAxisLabelPosition(draft.x_label_position)} onChange={(event) => patch("x_label_position", event.target.value)} style={inputStyle}><option value="bottom">Bottom</option><option value="top">Top</option></select></label>
      <label style={labelStyle}>Label font size<input type="number" min="9" max="20" value={draft.x_label_font_size || "12"} onChange={(event) => patch("x_label_font_size", event.target.value)} style={inputStyle} /></label>
      <label style={labelStyle}>Tick font size<input type="number" min="9" max="18" value={draft.x_tick_font_size || "12"} onChange={(event) => patch("x_tick_font_size", event.target.value)} style={inputStyle} /></label>
      {colorEditor("X-axis label color", "x_label_color", TEXT_MUTED)}
      {colorEditor("X-axis tick color", "x_tick_color", TEXT_MUTED)}
    </div>
    <div style={{ ...rowGrid, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
      <label style={labelStyle}>Y-axis label<input value={draft.y_label || ""} onChange={(event) => patch("y_label", event.target.value)} style={inputStyle} /></label>
      <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.show_y_axis_label, true)} onChange={(event) => patch("show_y_axis_label", event.target.checked ? "true" : "false")} />Show Y-axis label</label>
      <label style={labelStyle}>Position<select value={normalizeYAxisLabelPosition(draft.y_label_position)} onChange={(event) => patch("y_label_position", event.target.value)} style={inputStyle}><option value="left">Left</option><option value="right">Right</option></select></label>
      <label style={labelStyle}>Label font size<input type="number" min="9" max="20" value={draft.y_label_font_size || "12"} onChange={(event) => patch("y_label_font_size", event.target.value)} style={inputStyle} /></label>
      <label style={labelStyle}>Tick font size<input type="number" min="9" max="18" value={draft.y_tick_font_size || "12"} onChange={(event) => patch("y_tick_font_size", event.target.value)} style={inputStyle} /></label>
      {colorEditor("Y-axis label color", "y_label_color", TEXT_MUTED)}
      {colorEditor("Y-axis tick color", "y_tick_color", TEXT_MUTED)}
    </div>
    <div style={rowGrid}>
      <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.show_grid, true)} onChange={(event) => patch("show_grid", event.target.checked ? "true" : "false")} />Show gridlines</label>
      {colorEditor("Grid color", "grid_color", GRID)}
      {colorEditor("Axis line color", "axis_color", AXIS)}
    </div>
  </div>
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Plot appearance</div>
  <div style={rowGrid}>
    <label style={labelStyle}>Line width<input type="number" min="1" max="10" value={draft.line_width || "3"} onChange={(event) => patch("line_width", event.target.value)} style={inputStyle} /></label>
    <label style={labelStyle}>Point / marker size<input type="number" min="1" max="20" value={draft.marker_size || "5"} onChange={(event) => patch("marker_size", event.target.value)} style={inputStyle} /></label>
    <label style={labelStyle}>Bar spacing (%)<input type="number" min="0" max="70" value={draft.bar_gap || "24"} onChange={(event) => patch("bar_gap", event.target.value)} style={inputStyle} /></label>
  </div>
  <div style={{ marginTop: 7, fontSize: 11, color: TEXT_MUTED }}>Only controls relevant to the selected chart type affect the rendered chart.</div>
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Values / data labels</div>
  <div style={rowGrid}>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.show_data_labels, true)} onChange={(event) => patch("show_data_labels", event.target.checked ? "true" : "false")} />Show values on chart</label>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.label_pointer, false)} onChange={(event) => patch("label_pointer", event.target.checked ? "true" : "false")} />Show pointer when a value label is moved</label>
    <label style={labelStyle}>
      Label content
      <select value={normalizeDataLabelContent(draft.data_label_content)} onChange={(event) => patch("data_label_content", event.target.value)} style={inputStyle}>
        <option value="value">Value only</option>
        <option value="category">Category only</option>
        <option value="series">Series only</option>
        <option value="category_value">Category + Value</option>
        <option value="series_value">Series + Value</option>
        <option value="category_series">Category + Series</option>
        <option value="category_series_value">Category + Series + Value</option>
      </select>
    </label>
    <label style={labelStyle}>Label font size<input type="number" min="7" max="24" value={draft.data_label_font_size || "12"} onChange={(event) => patch("data_label_font_size", event.target.value)} style={inputStyle} /></label>
    {colorEditor("Value label color", "data_label_color", TEXT_DARK)}
  </div>
  <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
    <button type="button" onClick={() => patch("label_offsets", "")} style={buttonStyle}>Reset moved labels</button>
    <span style={{ fontSize: 11, color: TEXT_MUTED }}>Drag visible values directly in the live preview or on the saved chart. Bar, line, area, scatter/bubble, pie, and donut labels preserve their moved positions; pointer lines are optional.</span>
  </div>
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Data below chart</div>
  <div style={rowGrid}>
    <label style={labelStyle}>
      Display
      <select value={normalizeDataTableMode(draft.data_table_mode)} onChange={(event) => patch("data_table_mode", event.target.value)} style={inputStyle}>
        <option value="none">None</option>
        <option value="values">Values only</option>
        <option value="category_values">Category + values</option>
        <option value="series_values">Series + values</option>
        <option value="full_table">Full data table</option>
      </select>
    </label>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.data_table_show_colors, true)} onChange={(event) => patch("data_table_show_colors", event.target.checked ? "true" : "false")} />Show series colors</label>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.data_table_show_headers, true)} onChange={(event) => patch("data_table_show_headers", event.target.checked ? "true" : "false")} />Show table headers</label>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.data_table_show_categories, true)} onChange={(event) => patch("data_table_show_categories", event.target.checked ? "true" : "false")} />Show category names</label>
    <label style={checkStyle}><input type="checkbox" checked={boolSetting(draft.data_table_show_series, true)} onChange={(event) => patch("data_table_show_series", event.target.checked ? "true" : "false")} />Show series names</label>
    <label style={labelStyle}>Table font size<input type="number" min="7" max="18" value={draft.data_table_font_size || "11"} onChange={(event) => patch("data_table_font_size", event.target.value)} style={inputStyle} /></label>
    {colorEditor("Table text color", "data_table_text_color", TEXT_DARK)}
    {colorEditor("Table border color", "data_table_border_color", "#cbd5e1")}
  </div>
  <div style={{ marginTop: 8, fontSize: 11, color: TEXT_MUTED }}>This section is optional. It uses the chart's real category × series × value relationships and is included in the saved chart/PDF. If it makes the live preview taller, only the preview pane gets its own vertical scrollbar.</div>
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Colors and background</div>
  <div style={rowGrid}>{colorEditor("Chart background", "background", "#ffffff")}</div>
  <div style={{ display: "grid", gap: 8, marginTop: 11 }}>
    <div style={{ fontSize: 11, fontWeight: 850, color: TEXT_MUTED }}>Series / category colors</div>
    {colorTargets.map((target, index) => {
      const raw = currentColors[index] || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
      return (
        <div key={`${target}-${index}`} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) 42px minmax(150px, 1.2fr)", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: TEXT_DARK, fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(target)}>{target}</span>
          <input type="color" value={safeColorPickerValue(raw, DEFAULT_PALETTE[index % DEFAULT_PALETTE.length])} onChange={(event) => setColorAt(index, event.target.value)} style={{ width: 42, height: 34, padding: 2, border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", cursor: "pointer" }} />
          <input value={raw} onChange={(event) => setColorAt(index, event.target.value)} style={inputStyle} placeholder="#2563eb or rgb()/hsl()" />
        </div>
      );
    })}
  </div>
</section>

<section style={sectionStyle}>
  <div style={sectionTitleStyle}>Size and placement</div>
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
    {[["small", "Small"], ["medium", "Medium"], ["large", "Large"], ["full", "Full width"]].map(([key, label]) => (
      <button type="button" key={key} onClick={() => setPreset(key)} style={buttonStyle}>{label}</button>
    ))}
  </div>
  <div style={rowGrid}>
    <label style={checkStyle}><input type="checkbox" checked={aspectLocked} onChange={(event) => patch("lock_aspect_ratio", event.target.checked ? "true" : "false")} />Lock aspect ratio</label>
    <label style={labelStyle}>Width<input type="number" min={DOC_WRITER_MIN_WIDTH} max={maxWidth} value={widthInput} onChange={(event) => handleDimensionTyping("width", event.target.value)} onBlur={() => commitDimensionInput("width")} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); commitDimensionInput("width"); event.currentTarget.blur(); } }} style={inputStyle} /></label>
    <label style={labelStyle}>Height<input type="number" min={DOC_WRITER_MIN_HEIGHT} max={DOC_WRITER_MAX_HEIGHT} value={heightInput} onChange={(event) => handleDimensionTyping("height", event.target.value)} onBlur={() => commitDimensionInput("height")} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); commitDimensionInput("height"); event.currentTarget.blur(); } }} style={inputStyle} /></label>
    <label style={labelStyle}>Chart alignment<select value={alignment} onChange={(event) => applyAlignment(normalizeDocWriterAlignment(event.target.value))} style={inputStyle}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
  </div>
  <div style={{ marginTop: 7, fontSize: 11, color: TEXT_MUTED }}>These are draft dimensions until Save. All six chart resize handles remain available afterward; Lock aspect ratio keeps width and height proportional.</div>
</section>

{dataFields.length ? (
  <section style={sectionStyle}>
    <div style={sectionTitleStyle}>Chart data</div>
    <div style={{ display: "grid", gap: 11 }}>
      {dataFields.map((key) => (
        <label key={key} style={labelStyle}>
          {editorFieldLabel(key)}
          <textarea rows={key === "data" || key === "series" || key === "points" || key === "partitions" ? 4 : 2} value={draft[key] || ""} onChange={(event) => patch(key, event.target.value)} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.4 }} />
        </label>
      ))}
    </div>
  </section>
) : null}

<details style={sectionStyle}>
  <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 850, color: TEXT_MUTED }}>Advanced chart source</summary>
  <textarea key={`advanced-${draft.type}-${draft.title || "chart"}`} rows={7} defaultValue={serializeInstructionMap(draft)} onBlur={(event) => { const next = docWriterChartMapFromValue(event.target.value); if (Object.keys(next).length) replaceDraft({ ...draft, ...next }); }} style={{ ...inputStyle, marginTop: 8, resize: "vertical", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 11 }} />
</details>
            </div>

            <div className="doc-writer-chart-edit-preview-pane" style={{ minWidth: 0, position: "sticky", top: 0, alignSelf: "start" }}>
              <div style={{ ...sectionTitleStyle, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 6 }}>
                <span>Live preview</span>
                <span style={{ fontSize: 10, color: TEXT_MUTED, fontWeight: 750 }}>{Math.round(size.width)} × {Math.round(size.height)}{aspectLocked ? " · ratio locked" : ""}</span>
              </div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45, marginBottom: 9 }}>
                Interactive preview: drag values, category labels, and the legend. Click a supplemental title/note to format it. Use any of the six resize handles; the preview gets right and bottom scrollbars whenever the chart grows beyond its pane.
              </div>
              <div
                className="doc-writer-chart-live-preview-scroll"
                style={{
                  position: "relative",
                  maxHeight: "min(64vh, 650px)",
                  minHeight: 330,
                  overflowY: "auto",
                  overflowX: "auto",
                  scrollbarGutter: "stable",
                  padding: "14px 20px 26px 14px",
                  border: "1px solid #dbe3ee",
                  borderRadius: 12,
                  background: "#f8fafc",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ minWidth: "100%", width: "max-content", padding: 8, boxSizing: "border-box" }}>
                  <div style={{ display: "flex", justifyContent: alignment === "left" ? "flex-start" : alignment === "right" ? "flex-end" : "center", minWidth: "100%", width: "max-content" }}>
                    <div
                      key={`preview-${legendResetNonce}`}
                      style={{
                        position: "relative",
                        width: `${previewInnerWidth}px`,
                        minWidth: `${previewInnerWidth}px`,
                        marginTop: previewTopInset,
                        background: dialogPreviewVisual.background,
                        borderRadius: 10,
                        boxShadow: "0 0 0 1px rgba(148,163,184,0.22)",
                        boxSizing: "border-box",
                      }}
                    >
                      {previewResizeHandles.map((handle) => {
                        const hovered = previewResizeHover === handle.handle;
                        return (
                          <div
                            key={`chart-preview-resize-${handle.handle}`}
                            role="button"
                            tabIndex={-1}
                            {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
                            data-docwriter-chart-preview-resize-corner={handle.handle}
                            aria-label={`Resize preview chart from ${handle.vertical} ${handle.horizontal} corner`}
                            title={aspectLocked ? "Resize chart proportionally" : "Resize chart"}
                            onPointerEnter={() => setPreviewResizeHover(handle.handle)}
                            onPointerLeave={() => setPreviewResizeHover("")}
                            onPointerDown={(event) => beginPreviewResize(event, handle.handle)}
                            style={{
                              position: "absolute",
                              ...(handle.vertical === "top" ? { top: 4 } : { bottom: 4 }),
                              ...(handle.horizontal === "left" ? { left: 4 } : { right: 4 }),
                              width: DOC_WRITER_CHART_RESIZE_HANDLE_PX,
                              height: DOC_WRITER_CHART_RESIZE_HANDLE_PX,
                              zIndex: 24,
                              cursor: handle.cursor,
                              touchAction: "none",
                              display: "grid",
                              placeItems: "center",
                              borderRadius: 7,
                              background: hovered ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.88)",
                              border: hovered ? "1px solid rgba(30,42,120,0.34)" : "1px solid rgba(30,42,120,0.12)",
                              color: "rgba(30,42,120,0.88)",
                              fontSize: 15,
                              lineHeight: 1,
                              userSelect: "none",
                              boxSizing: "border-box",
                            }}
                          ><span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>{handle.glyph}</span></div>
                        );
                      })}
                      {previewEdgeHandles.map((handle) => {
                        const hovered = previewResizeHover === handle.handle;
                        return (
                          <div
                            key={`chart-preview-resize-edge-${handle.handle}`}
                            role="button"
                            tabIndex={-1}
                            {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
                            data-docwriter-chart-preview-resize-edge={handle.edge}
                            aria-label={`Resize preview chart height from ${handle.edge} edge`}
                            title={aspectLocked ? "Resize chart proportionally" : "Resize chart height"}
                            onPointerEnter={() => setPreviewResizeHover(handle.handle)}
                            onPointerLeave={() => setPreviewResizeHover("")}
                            onPointerDown={(event) => beginPreviewResize(event, handle.handle)}
                            style={{
                              position: "absolute",
                              left: DOC_WRITER_CHART_RESIZE_HANDLE_PX + 8,
                              right: DOC_WRITER_CHART_RESIZE_HANDLE_PX + 8,
                              ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }),
                              height: 14,
                              zIndex: 30,
                              cursor: handle.cursor,
                              touchAction: "none",
                              userSelect: "none",
                              background: "transparent",
                              pointerEvents: "auto",
                            }}
                          >
                            <span aria-hidden="true" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }), width: hovered ? 54 : 34, height: 2, borderRadius: 999, background: hovered ? "rgba(30,42,120,0.62)" : "rgba(30,42,120,0.20)", pointerEvents: "none", transition: "width 100ms ease, background 100ms ease" }} />
                          </div>
                        );
                      })}
                      <ChartShell
                        visual={dialogPreviewVisual}
                        height={size.height}
                        background={dialogPreviewVisual.background}
                        supplementalEditable
                        onSupplementalFocus={(kind, id) => setActiveSupplemental({ kind, id })}
                        onSupplementalCommit={(kind, id, nextText) => updateSupplementalLine(kind, id, { text: nextText })}
                      >
                        {renderVisualPreview(dialogPreviewVisual)}
                      </ChartShell>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "13px 18px 15px", borderTop: "1px solid rgba(148,163,184,0.22)", background: "#ffffff" }}>
          <button type="button" onClick={() => setDeleteConfirmOpen(true)} style={{ ...buttonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" }}>Delete chart</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={onCancel} style={buttonStyle}>Cancel</button>
            <button type="button" onClick={onSave} style={{ ...buttonStyle, background: "#1E2A78", borderColor: "#1E2A78", color: "#ffffff" }}>Save changes</button>
          </div>
        </div>
      </div>

      {deleteConfirmOpen ? (
        <div
          {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
          role="presentation"
          onPointerDown={(event) => {
            event.stopPropagation();
            if (event.target === event.currentTarget) setDeleteConfirmOpen(false);
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          style={{ position: "fixed", inset: 0, zIndex: 2147483100, display: "grid", placeItems: "center", padding: 20, boxSizing: "border-box", background: "rgba(15,23,42,0.38)" }}
        >
          <div role="alertdialog" aria-modal="true" aria-label="Confirm delete chart" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()} style={{ width: "min(420px, calc(100vw - 40px))", padding: 20, borderRadius: 14, border: "1px solid rgba(148,163,184,0.35)", background: "#ffffff", boxShadow: "0 24px 70px rgba(15,23,42,0.30)", color: TEXT_DARK, boxSizing: "border-box" }}>
            <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.25 }}>Delete chart?</div>
            <div style={{ marginTop: 7, fontSize: 12, lineHeight: 1.5, color: TEXT_MUTED }}>This will remove the chart from the document. This action can be undone from the Canvas after the delete is committed.</div>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={buttonStyle} onClick={() => setDeleteConfirmOpen(false)}>Cancel</button>
              <button type="button" style={{ ...buttonStyle, background: "#b91c1c", borderColor: "#b91c1c", color: "#ffffff" }} onClick={onDelete}>Delete chart</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    ownerDocument.body
  );
}

const DocWriterChart = forwardRef(function DocWriterChart(
  {
    value,
    chartId = "",
    editable = true,
    selected = false,
    maxWidth = DOC_WRITER_DEFAULT_MAX_WIDTH,
    width,
    height,
    alignment,
    onChange,
    onDelete,
    onResize,
    onAlignmentChange,
    onSnapshot,
    onRequestDrag,
    onEditingChange,
    className = "",
  },
  ref
) {
  const normalizedIncoming = useMemo(() => normalizeDocWriterChartSource(value), [value]);
  const incomingVisual = useMemo(() => parseVisualInstruction(normalizedIncoming), [normalizedIncoming]);
  const safeMaxWidth = Math.max(DOC_WRITER_MIN_WIDTH, Number(maxWidth) || DOC_WRITER_DEFAULT_MAX_WIDTH);
  const rootRef = useRef(null);
  const printSurfaceRef = useRef(null);
  const resizeRef = useRef(null);
  const moveRef = useRef(null);
  const [source, setSource] = useState(normalizedIncoming);
  const [editOpen, setEditOpen] = useState(false);
  const [resizeHover, setResizeHover] = useState("");
  const [topEdgeInset, setTopEdgeInset] = useState(() => clamp(safeNumber(incomingVisual.map.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT));
  const [draft, setDraft] = useState(() => ({ ...incomingVisual.map, type: incomingVisual.type }));
  const [objectAlignment, setObjectAlignment] = useState(
    normalizeDocWriterAlignment(alignment || incomingVisual.alignment)
  );
  const [size, setSize] = useState(() => ({
    width: sanitizeDocWriterDimension(width, Math.min(safeMaxWidth, incomingVisual.width || DOC_WRITER_DEFAULT_WIDTH), DOC_WRITER_MIN_WIDTH, safeMaxWidth),
    height: sanitizeDocWriterDimension(height, incomingVisual.height || 390, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT),
  }));
  const [editSize, setEditSize] = useState(() => ({
    width: sanitizeDocWriterDimension(width, Math.min(safeMaxWidth, incomingVisual.width || DOC_WRITER_DEFAULT_WIDTH), DOC_WRITER_MIN_WIDTH, safeMaxWidth),
    height: sanitizeDocWriterDimension(height, incomingVisual.height || 390, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT),
  }));
  const [editAlignment, setEditAlignment] = useState(
    normalizeDocWriterAlignment(alignment || incomingVisual.alignment)
  );

  useEffect(() => {
    if (editOpen || resizeRef.current || moveRef.current) return;
    setSource(normalizedIncoming);
    const nextVisual = parseVisualInstruction(normalizedIncoming);
    if (!editOpen) setDraft({ ...nextVisual.map, type: nextVisual.type });
    setTopEdgeInset(clamp(safeNumber(nextVisual.map.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT));
    if (width == null) {
      setSize((current) => ({
        ...current,
        width: sanitizeDocWriterDimension(nextVisual.width, current.width, DOC_WRITER_MIN_WIDTH, safeMaxWidth),
      }));
    }
    if (height == null) {
      setSize((current) => ({
        ...current,
        height: sanitizeDocWriterDimension(nextVisual.height, current.height, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT),
      }));
    }
    if (alignment == null) setObjectAlignment(normalizeDocWriterAlignment(nextVisual.alignment));
  }, [normalizedIncoming]); // External document changes only.

  useEffect(() => {
    if (editOpen || resizeRef.current || moveRef.current || width == null) return;
    setSize((current) => ({ ...current, width: sanitizeDocWriterDimension(width, current.width, DOC_WRITER_MIN_WIDTH, safeMaxWidth) }));
  }, [width, safeMaxWidth]);

  useEffect(() => {
    if (editOpen || resizeRef.current || moveRef.current || height == null) return;
    setSize((current) => ({ ...current, height: sanitizeDocWriterDimension(height, current.height, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT) }));
  }, [height]);

  useEffect(() => {
    if (editOpen || resizeRef.current || moveRef.current || alignment == null) return;
    setObjectAlignment(normalizeDocWriterAlignment(alignment));
  }, [alignment]);

  const visual = useMemo(() => {
    const parsed = parseVisualInstruction(source);
    return { ...parsed, width: size.width, height: size.height, alignment: objectAlignment };
  }, [source, size.width, size.height, objectAlignment]);

  const title = visual.title || "";
  const subtitle = visual.subtitle || (visual.equation ? `Equation: ${visual.equation}` : "");

  function commitLabelOffset(labelKey, offset) {
    const currentOffsets = parseLabelOffsets(visual.map.label_offsets || "");
    currentOffsets[labelKey] = {
      dx: Math.round(safeNumber(offset?.dx, 0)),
      dy: Math.round(safeNumber(offset?.dy, 0)),
    };
    const nextMap = {
      ...visual.map,
      type: visual.type,
      label_offsets: serializeLabelOffsets(currentOffsets),
    };
    commitObjectState(nextMap, size, objectAlignment);
  }

  function commitCategoryOffset(index, offset) {
    const currentOffsets = parseCategoryOffsets(visual.map.category_label_offsets || "");
    currentOffsets[index] = {
      dx: Math.round(safeNumber(offset?.dx, 0)),
      dy: Math.round(safeNumber(offset?.dy, 0)),
    };
    const nextMap = {
      ...visual.map,
      type: visual.type,
      category_label_offsets: serializeCategoryOffsets(currentOffsets),
    };
    commitObjectState(nextMap, size, objectAlignment);
  }

  function commitLegendOffset(offset) {
    const nextMap = {
      ...visual.map,
      type: visual.type,
      legend_offset: serializeLegendOffset(offset),
    };
    commitObjectState(nextMap, size, objectAlignment);
  }

  function commitLiveSupplementalText(kind, lineId, nextText) {
    const key = kind === "note" ? "bottom_notes" : "top_titles";
    const lines = parseChartSupplementalLines(visual.map[key], kind);
    let changed = false;
    const nextLines = lines.map((line) => {
      if (line.id !== lineId) return line;
      const text = String(nextText || "");
      if (text === line.text) return line;
      changed = true;
      return { ...line, text };
    });
    if (!changed) return;
    commitObjectState(
      { ...visual.map, type: visual.type, [key]: serializeChartSupplementalLines(nextLines, kind) },
      size,
      objectAlignment
    );
  }

  const emitSnapshot = () => {
    const html = printableChartSnapshotHtml(printSurfaceRef.current).trim();
    if (!html) return "";
    try {
      onSnapshot?.({ chartId, html, source, width: size.width, height: size.height, alignment: objectAlignment });
    } catch (_) {}
    return html;
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let frame1 = 0;
    let frame2 = 0;
    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => emitSnapshot());
    });
    return () => {
      if (frame1) window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [source, size.width, size.height, objectAlignment]);

  const commitObjectState = (nextMap, nextSize = size, nextAlignment = objectAlignment) => {
    const cleanMap = { ...nextMap };
    cleanMap.type = normalizeType(cleanMap.type || visual.type || "column");
    cleanMap.doc_width = String(Math.round(nextSize.width));
    cleanMap.doc_height = String(Math.round(nextSize.height));
    cleanMap.doc_alignment = normalizeDocWriterAlignment(nextAlignment);
    cleanMap.doc_top_edge_inset = String(Math.round(clamp(safeNumber(cleanMap.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT)));
    cleanMap.lock_aspect_ratio = boolSetting(cleanMap.lock_aspect_ratio, false) ? "true" : "false";
    cleanMap.legend_position = normalizeLegendPosition(cleanMap.legend_position);
    const nextSource = serializeInstructionMap(cleanMap);
    setSource(nextSource);
    setDraft(cleanMap);
    setSize(nextSize);
    setTopEdgeInset(clamp(safeNumber(cleanMap.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT));
    setObjectAlignment(cleanMap.doc_alignment);
    const detail = {
      chartId,
      value: nextSource,
      source: nextSource,
      spec: { ...cleanMap },
      width: nextSize.width,
      height: nextSize.height,
      alignment: cleanMap.doc_alignment,
    };
    try { onChange?.(detail); } catch (_) {}
    try { onResize?.({ chartId, width: nextSize.width, height: nextSize.height }); } catch (_) {}
    try { onAlignmentChange?.({ chartId, alignment: cleanMap.doc_alignment }); } catch (_) {}
    return detail;
  };

  const setEditorOpen = (next) => {
    setEditOpen(Boolean(next));
    try { onEditingChange?.(Boolean(next), { chartId }); } catch (_) {}
  };

  const openEditor = () => {
    setDraft({
      ...visual.map,
      type: visual.type,
      doc_width: String(size.width),
      doc_height: String(size.height),
      doc_alignment: objectAlignment,
    });
    setEditSize({ ...size });
    setEditAlignment(objectAlignment);
    // Signal the parent before focus can leave the contentEditable canvas. This
    // lets DocCanvasEditor suppress pagination/remount work for this chart.
    setEditorOpen(true);
  };

  const deleteChart = () => {
    setEditorOpen(false);
    try {
      onDelete?.({ chartId, source, width: size.width, height: size.height, alignment: objectAlignment });
    } catch (_) {}
    try {
      rootRef.current?.dispatchEvent?.(
        new CustomEvent("doc-writer-chart-delete", {
          bubbles: true,
          detail: { chartId, source },
        })
      );
    } catch (_) {}
  };

  const beginResize = (event, handle = "se") => {
    if (!editable || !event || event.button !== 0 || resizeRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const locked = boolSetting(visual.map.lock_aspect_ratio, false);
    const startInset = clamp(safeNumber(topEdgeInset, 0), 0, DOC_WRITER_MAX_HEIGHT);
    const resizeHorizontal = handle.includes("e") || handle.includes("w");
    const resizeFromWest = handle.includes("w");
    const resizeFromNorth = handle.includes("n");
    const resizeFromTop = handle === "n" || resizeFromNorth;
    resizeRef.current = {
      pointerId: event.pointerId,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: size.width,
      startHeight: size.height,
      startInset,
      locked,
      latest: { ...size },
      latestInset: startInset,
    };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}
    try { onEditingChange?.(true, { chartId, operation: "resize", handle }); } catch (_) {}

    const move = (moveEvent) => {
      const state = resizeRef.current;
      if (!state) return;
      const dx = moveEvent.clientX - state.startX;
      const dy = moveEvent.clientY - state.startY;
      let nextWidth = state.startWidth;
      let nextHeight = state.startHeight;

      if (state.locked) {
        let desiredScale = 1;
        if (resizeHorizontal) {
          const widthCandidate = state.startWidth + (resizeFromWest ? -dx : dx);
          const heightCandidate = state.startHeight + (resizeFromNorth ? -dy : dy);
          const sx = widthCandidate / Math.max(1, state.startWidth);
          const sy = heightCandidate / Math.max(1, state.startHeight);
          desiredScale = Math.abs(sx - 1) >= Math.abs(sy - 1) ? sx : sy;
        } else {
          const heightCandidate = state.startHeight + (resizeFromNorth ? -dy : dy);
          desiredScale = heightCandidate / Math.max(1, state.startHeight);
        }
        const scale = clampChartScale(state.startWidth, state.startHeight, desiredScale, safeMaxWidth);
        nextWidth = Math.round(state.startWidth * scale);
        nextHeight = Math.round(state.startHeight * scale);
      } else {
        if (resizeHorizontal) {
          nextWidth = sanitizeDocWriterDimension(
            state.startWidth + (resizeFromWest ? -dx : dx),
            state.startWidth,
            DOC_WRITER_MIN_WIDTH,
            safeMaxWidth
          );
        }
        nextHeight = sanitizeDocWriterDimension(
          state.startHeight + (resizeFromNorth ? -dy : dy),
          state.startHeight,
          DOC_WRITER_MIN_HEIGHT,
          DOC_WRITER_MAX_HEIGHT
        );
      }

      const actualHeightDelta = state.startHeight - nextHeight;
      const nextInset = resizeFromTop
        ? clamp(state.startInset + actualHeightDelta, 0, DOC_WRITER_MAX_HEIGHT)
        : state.startInset;
      const next = { width: nextWidth, height: nextHeight };
      state.latest = next;
      state.latestInset = nextInset;
      setSize(next);
      setTopEdgeInset(nextInset);
    };

    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = resizeRef.current;
      resizeRef.current = null;
      if (!state) return;
      const finalSize = state.latest || { width: state.startWidth, height: state.startHeight };
      commitObjectState(
        { ...visual.map, type: visual.type, doc_top_edge_inset: String(Math.round(state.latestInset ?? state.startInset)) },
        finalSize,
        objectAlignment
      );
      try { onEditingChange?.(false, { chartId, operation: "resize", handle: state.handle }); } catch (_) {}
    };

    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  useImperativeHandle(ref, () => ({
    openEditor,
    closeEditor: () => setEditorOpen(false),
    getSource: () => source,
    getSpec: () => ({ ...visual.map, type: visual.type }),
    getSize: () => ({ ...size }),
    getAlignment: () => objectAlignment,
    getPrintableHtml: () => printableChartSnapshotHtml(printSurfaceRef.current),
    captureSnapshot: emitSnapshot,
    setSize: (next = {}) => {
      const resolved = {
        width: sanitizeDocWriterDimension(next.width, size.width, DOC_WRITER_MIN_WIDTH, safeMaxWidth),
        height: sanitizeDocWriterDimension(next.height, size.height, DOC_WRITER_MIN_HEIGHT, DOC_WRITER_MAX_HEIGHT),
      };
      commitObjectState({ ...visual.map, type: visual.type }, resolved, objectAlignment);
      return resolved;
    },
  }));

  if (!visual.raw) return null;

  const alignmentMargins = objectAlignment === "left"
    ? { marginLeft: 0, marginRight: "auto" }
    : objectAlignment === "right"
      ? { marginLeft: "auto", marginRight: 0 }
      : { marginLeft: "auto", marginRight: "auto" };

  const ownerDocument = rootRef.current?.ownerDocument || (typeof document !== "undefined" ? document : null);

  return (
    <div
      ref={rootRef}
      className={`doc-writer-chart-object ${className}`.trim()}
      data-doc-writer-chart="true"
      data-doc-chart-version={DOC_WRITER_CHART_COMPONENT_VERSION}
      data-doc-chart-id={chartId || undefined}
      data-doc-chart-width={String(size.width)}
      data-doc-chart-height={String(size.height)}
      data-doc-chart-alignment={objectAlignment}
      style={{
        position: "relative",
        boxSizing: "border-box",
        width: `${Math.min(size.width, safeMaxWidth)}px`,
        maxWidth: "100%",
        minWidth: 0,
        paddingTop: editable ? 48 : 0,
        ...alignmentMargins,
        outline: selected && editable ? "1px solid rgba(30,42,120,0.28)" : "none",
        outlineOffset: 4,
        borderRadius: 12,
        background: "#ffffff",
        pointerEvents: "none",
        breakInside: "avoid",
        pageBreakInside: "avoid",
      }}
    >
      <style {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}>{`
        @media print {
          .doc-writer-chart-object [${DOC_WRITER_EDITOR_ONLY_ATTR}="true"],
          .doc-writer-chart-edit-overlay { display: none !important; }
          .doc-writer-chart-object { outline: none !important; }
        }
      `}</style>

      {editable ? (
        <div
          {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
          style={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: "45%",
            maxWidth: 380,
            minWidth: 0,
            zIndex: 20,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "none",
          }}
        >
          <button
            type="button"
            {...{ [DOC_WRITER_DRAG_HANDLE_ATTR]: "true", [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
            aria-label="Move chart"
            title="Move chart — drag this handle"
            style={{
              width: 38,
              minWidth: 38,
              height: 34,
              minHeight: 34,
              padding: 0,
              borderRadius: 9,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              display: "grid",
              placeItems: "center",
              cursor: "grab",
              touchAction: "none",
              boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
              fontSize: 17,
              userSelect: "none",
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
            onPointerDown={(event) => {
              if (event.button !== 0 || moveRef.current) return;
              event.preventDefault();
              event.stopPropagation();
              setResizeHover("");
              moveRef.current = { pointerId: event.pointerId };
              try { onEditingChange?.(true, { chartId, operation: "move" }); } catch (_) {}
              const finishMove = () => {
                window.removeEventListener("pointerup", finishMove, true);
                window.removeEventListener("pointercancel", finishMove, true);
                if (!moveRef.current) return;
                moveRef.current = null;
                try { onEditingChange?.(false, { chartId, operation: "move" }); } catch (_) {}
              };
              window.addEventListener("pointerup", finishMove, true);
              window.addEventListener("pointercancel", finishMove, true);
              try {
                onRequestDrag?.(event, {
                  chartId,
                  source,
                  width: size.width,
                  height: size.height,
                  alignment: objectAlignment,
                  operation: "move",
                });
              } catch (_) {}
            }}
          ><span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>⠿</span></button>

          <button
            type="button"
            {...{ [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
            aria-label="Edit chart"
            style={{
              minHeight: 34,
              height: 34,
              padding: "0 14px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#1E2A78",
              fontSize: 12,
              fontWeight: 850,
              cursor: "pointer",
              boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!editOpen) openEditor();
            }}
            onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}
          >Edit</button>
        </div>
      ) : null}

      <div
        className="doc-writer-chart-canvas-presentation"
        data-doc-writer-chart-canvas-presentation="true"
        style={{
          position: "relative",
          display: "flow-root",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          marginTop: `${topEdgeInset}px`,
          pointerEvents: "auto",
        }}
      >
        {editable ? [
          { handle: "nw", glyph: "↖", cursor: "nwse-resize", vertical: "top", horizontal: "left" },
          { handle: "ne", glyph: "↗", cursor: "nesw-resize", vertical: "top", horizontal: "right" },
          { handle: "sw", glyph: "↙", cursor: "nesw-resize", vertical: "bottom", horizontal: "left" },
          { handle: "se", glyph: "↘", cursor: "nwse-resize", vertical: "bottom", horizontal: "right" },
        ].map((handle) => {
          const hovered = resizeHover === handle.handle;
          return (
            <div
              key={`chart-resize-${handle.handle}`}
              {...{ [DOC_WRITER_RESIZE_HANDLE_ATTR]: "true", [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
              role="button"
              tabIndex={-1}
              data-docwriter-chart-resize-corner={handle.handle}
              aria-label={`Resize chart from ${handle.vertical} ${handle.horizontal} corner`}
              title={boolSetting(visual.map.lock_aspect_ratio, false) ? "Resize chart proportionally" : "Resize chart"}
              onPointerEnter={() => setResizeHover(handle.handle)}
              onPointerLeave={() => setResizeHover("")}
              onPointerDown={(event) => beginResize(event, handle.handle)}
              style={{
                position: "absolute",
                ...(handle.vertical === "top" ? { top: 4 } : { bottom: 4 }),
                ...(handle.horizontal === "left" ? { left: 4 } : { right: 4 }),
                width: DOC_WRITER_CHART_RESIZE_HANDLE_PX,
                height: DOC_WRITER_CHART_RESIZE_HANDLE_PX,
                zIndex: 24,
                cursor: handle.cursor,
                touchAction: "none",
                display: "grid",
                placeItems: "center",
                borderRadius: 7,
                background: hovered ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.74)",
                border: hovered ? "1px solid rgba(30,42,120,0.34)" : "1px solid rgba(30,42,120,0.10)",
                color: "rgba(30,42,120,0.88)",
                fontSize: 15,
                lineHeight: 1,
                userSelect: "none",
                boxSizing: "border-box",
              }}
            ><span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>{handle.glyph}</span></div>
          );
        }) : null}

        {editable ? [
          { handle: "n", edge: "top", cursor: "ns-resize" },
          { handle: "s", edge: "bottom", cursor: "ns-resize" },
        ].map((handle) => {
          const hovered = resizeHover === handle.handle;
          return (
            <div
              key={`chart-resize-edge-${handle.handle}`}
              {...{ [DOC_WRITER_RESIZE_HANDLE_ATTR]: "true", [DOC_WRITER_EDITOR_ONLY_ATTR]: "true" }}
              role="button"
              tabIndex={-1}
              data-docwriter-chart-resize-edge={handle.edge}
              aria-label={`Resize chart height from ${handle.edge} edge`}
              title={boolSetting(visual.map.lock_aspect_ratio, false) ? "Resize chart proportionally" : "Resize chart height"}
              onPointerEnter={() => setResizeHover(handle.handle)}
              onPointerLeave={() => setResizeHover("")}
              onPointerDown={(event) => beginResize(event, handle.handle)}
              style={{
                position: "absolute",
                left: DOC_WRITER_CHART_RESIZE_HANDLE_PX + 8,
                right: DOC_WRITER_CHART_RESIZE_HANDLE_PX + 8,
                ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }),
                height: 14,
                zIndex: 30,
                cursor: handle.cursor,
                touchAction: "none",
                userSelect: "none",
                boxSizing: "border-box",
                background: "transparent",
                pointerEvents: "auto",
              }}
            >
              <span aria-hidden="true" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }), width: hovered ? 54 : 34, height: 2, borderRadius: 999, background: hovered ? "rgba(30,42,120,0.62)" : "rgba(30,42,120,0.16)", pointerEvents: "none", transition: "width 100ms ease, background 100ms ease" }} />
            </div>
          );
        }) : null}

      <div ref={printSurfaceRef} className="doc-writer-chart-print-surface" style={{ width: "100%", maxWidth: "100%", overflow: "hidden", background: visual.background || CARD_BG }}>
        <DocWriterChartRenderBoundary resetKey={`${source}|${size.width}|${size.height}`}>
          <ChartShell
            visual={{ ...visual, title, subtitle }}
            height={size.height}
            background={visual.background || CARD_BG}
            supplementalEditable={editable && !editOpen}
            onSupplementalCommit={commitLiveSupplementalText}
            canvasTextAlignmentHandoff
          >
            {renderVisualPreview({
              ...visual,
              chartId,
              title,
              subtitle,
              height: size.height,
              width: size.width,
              canEditLabels: editable && !editOpen,
              onCommitLabelOffset: commitLabelOffset,
              canEditCategoryLabels: editable && !editOpen,
              onCommitCategoryOffset: commitCategoryOffset,
              canEditLegend: editable && !editOpen,
              onCommitLegendOffset: commitLegendOffset,
            })}
          </ChartShell>
        </DocWriterChartRenderBoundary>
      </div>
      </div>

      <DocWriterChartEditDialog
        open={editOpen}
        ownerDocument={ownerDocument}
        draft={draft}
        setDraft={setDraft}
        size={editSize}
        setSize={setEditSize}
        alignment={editAlignment}
        setAlignment={setEditAlignment}
        maxWidth={safeMaxWidth}
        onCancel={() => {
          setEditorOpen(false);
          setDraft({ ...visual.map, type: visual.type });
          setEditSize({ ...size });
          setEditAlignment(objectAlignment);
        }}
        onDelete={deleteChart}
        onSave={() => {
          const nextMap = {
            ...draft,
            type: normalizeType(draft.type || visual.type),
            doc_width: String(editSize.width),
            doc_height: String(editSize.height),
            doc_alignment: editAlignment,
            legend_position: normalizeLegendPosition(draft.legend_position),
            legend_align: normalizeTextAlignment(draft.legend_align, "center"),
            show_chart_type: draft.show_chart_type ?? "false",
            show_data_labels: draft.show_data_labels ?? "true",
            data_label_content: normalizeDataLabelContent(draft.data_label_content),
            data_table_mode: normalizeDataTableMode(draft.data_table_mode),
            legend_content: normalizeLegendContent(draft.legend_content),
            show_category_labels: draft.show_category_labels ?? "true",
            show_grid: draft.show_grid ?? "true",
            top_titles: serializeChartSupplementalLines(parseChartSupplementalLines(draft.top_titles, "title"), "title"),
            bottom_notes: serializeChartSupplementalLines(parseChartSupplementalLines(draft.bottom_notes, "note"), "note"),
            lock_aspect_ratio: boolSetting(draft.lock_aspect_ratio, false) ? "true" : "false",
            doc_top_edge_inset: String(Math.round(clamp(safeNumber(draft.doc_top_edge_inset, 0), 0, DOC_WRITER_MAX_HEIGHT))),
          };
          commitObjectState(nextMap, editSize, editAlignment);
          setEditorOpen(false);
        }}
      />
    </div>
  );
});

export default DocWriterChart;
